import { applyCompletedDraftsToStoreys } from '@shared/helpers/design/draft-shape.helpers';
import { isEntirePolygonSelected } from '../helpers/storey-selection.helpers';

import {
  getStoreyBelow,
  getStoreyById,
  getStoreyElevation,
  getStoreyElevationRange,
  getStoreyIndex,
  isEmptyBuildingGeometry,
  isEmptyStoreysGeometry,
  isEntireDesignGeometryEmpty,
} from '@shared/helpers/design/storey.helpers';
import type { Building } from '@shared/models/design/building-design.model';
import {
  getOffset,
  getDistance,
  convertCoordinateToMetricOffset,
  convertMetricOffsetToCoordinate,
} from '@shared/helpers/geometry/coordinates.helpers';
import {
  isFartherThanMaxFromAnyPoint,
  preferPointWithinMaxSeparation,
} from '@shared/helpers/geometry/point-separation.helpers';
import {
  getBuildingSolidShapesOverlappingElevation,
  isPointInsideStoreyShapes,
  isSolidPairAllowedByCutoutHole,
} from '@shared/helpers/design/shape.helpers';
import {
  clampTranslationDeltaOutsideObstacles,
  type MovingPolygonForOverlapClamp,
} from '@shared/helpers/design/building-overlap-translation.helpers';
import {
  CUTOUT_MIN_MARGIN_METERS,
  clampSolidTranslationInsideCutout,
  getSolidShapes,
  isCutoutInsideStoreyWithMargin,
  resolveClampedCutoutVertexPosition,
  resolveClampedSolidVertexInsideCutout,
} from '@shared/helpers/design/cutout.helpers';
import {
  toPoint2D,
  projectPointOntoSegmentClamped,
  computePolygonArea,
  isPointInPolygon,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { getId } from '@shared/helpers/object.helpers';
import { getItemById } from '@shared/helpers/array.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Shape, DraftShape, Storey } from '@shared/models/design/storey.model';
import { Point2D, Polygon2D } from '@shared/models/geometry.model';
import {
  createBuildingFootprintTemplateDrafts,
  type BuildingFootprintTemplateGeometry,
} from '../helpers/building-footprint-template.helpers';
import { Coordinate } from '@shared/models/geography.model';
import {} from 'lodash';
import { MapMouseEvent, type PointLike } from 'mapbox-gl';
import {
  createPointDragModifierMoves,
  type PointDragModifiers,
  type PointDragPointerMove,
} from '../helpers/point-drag-modifiers.helpers';
import {
  getPointDragSnapGuides,
  snapPointToDragGuides,
} from '../helpers/point-drag-snap.helpers';
import {
  animationFrameScheduler,
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  switchMap,
  filter,
  map,
  share,
  tap,
  take,
  merge,
  distinctUntilChanged,
  exhaustMap,
  from,
  fromEvent,
  throttleTime,
} from 'rxjs';
import {
  setHasSelectedShape,
  setHasSelectedVertices,
} from '../store/design-store.hooks';
import { MapLayerIds } from '../helpers/map-config.constants';
import {
  snapDraftPointToPreviousSegmentAngle,
  snapDraftPointToScreenAngle,
  snapDraftPointToScreenOrRightAngle,
  snapDraftSegmentLength,
  snapPenDraftPoint,
} from '../helpers/pen-draft-snap.helpers';
import {
  createBuildingPickRay,
  getBuildingRayHitElevation,
  getBuildingVolumeRayHitDistance,
  getRayPointAtElevation,
  getStoreyRayHitElevation,
  getStoreyVolumeRayHit,
  pickBuildingByRay,
  pickBuildingByVolumeRay,
  pickSolidShapeOnStorey,
  pickStoreyByRay,
  pickStoreyByVolumeRay,
  pickStoreyByVolumeClick,
  type BuildingVolumeRay,
} from '../helpers/building-pick.helpers';
import {
  buildDraftClickPayload,
  createMapObservables,
  getEventPosition,
  getMapSelectionsFromObservables,
  isDraftClickPayload,
  isLineClickPayload,
  MapObservables,
  queryPointFeature,
  resolvePointMeta,
  shouldClearBuildingSelection,
  shouldIgnoreDraftClickOnOtherBuilding,
  shouldSwitchToOtherBuilding,
  wrapPreventDefault,
} from '../helpers/map-observables.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import {
  screenToLngLatAtElevation,
  projectLngLatAtElevation,
  getMetersPerPixelAtCoordinate,
} from '../helpers/map.helpers';
import {
  buildInsertedPointPayload,
  buildPointPayload,
  resolveDragGroupPointIndexes,
  resolvePointDragStoreyId,
} from './map-event-payload.helpers';
import {
  getPointFeatureFromMapEvent,
  getPolygonFeatureFromMapEvent,
} from '../helpers/map-features.helpers';
import { mapGuards } from '../helpers/map-guards.helpers';
import {
  DraftClickPayload,
  DraftPreviewPayload,
  MapGeometryMeasurementFocus,
  MapHoverState,
  MapHoverStateType,
  MapObservableCollection,
  MapSelectionObservables,
  MapStateObservables,
  MapSelections,
  PointMeta,
  PointPayload,
  MapDraftCursorState,
  MapTooltipPayload,
  MapTooltipType,
  LineClickPayload,
  ShapeTranslatePayload,
  BuildingRotationCursorState,
} from '../models/map-observables.models';
import {
  MapStoreyPointFeature,
  MapStoreyProperty,
} from '../models/map-feature.models';
import {
  resolveShapeIdForStoreyLevel,
  type ShapeHit,
} from '../helpers/shape-hit.helpers';
import {
  pickTopmostBuildingHit,
  resolveSelectedBuildingVolumeClickAction,
  toBuildingFillHits,
  type BuildingFillHit,
  type SelectedBuildingVolumeClickAction,
} from '../helpers/building-click.helpers';
import {
  CLICK_DRAG_DISTANCE_THRESHOLD_PX,
  LINE_HIT_PADDING_PX,
  MAPBOX_DEFAULT_CLICK_TOLERANCE_PX,
  POINT_DRAG_CLICK_WINDOW_MS,
  POINT_HIT_PADDING_PX,
} from '../map.constants';
import type { SnapAndBoundaries } from '../snap';
import {
  BuildingTranslationHandler,
  MapLocationPinController,
  LocationPinCursorState,
} from './location-pin.controller';
import {
  DraftLineHoverHit,
  LineHoverHit,
  MapHoverController,
} from './hover.controller';
import {
  ShapeTranslateEvent,
  PointActionContext,
  shouldShowDeleteTooltip,
  buildLineInsertResult,
  getLinkedStoreyTargets,
} from './vertex-interaction.helpers';
import {
  computeTooltipPayload,
  resolveGeometryMeasurementFocus,
} from './tooltip.helpers';
import {
  buildCornerAngleLabels,
  buildSegmentLengthLabels,
} from '../helpers/selected-segment.helpers';
import { shouldRetainBuildingHoverAfterSelection } from '../helpers/view-rotation-cursor.helpers';
import { shouldHandleMapStoreyTabShortcut } from '../storeys/storey-keyboard-shortcuts.helpers';
import { formatDisplayedSegmentLengthMeters } from '../helpers/segment-length-label.helpers';
import { formatCornerAngleDegrees } from '../helpers/corner-angle-edit.helpers';
import {
  DEFAULT_SEGMENT_TRANSLATION_SNAP_METERS,
  SHIFT_SEGMENT_TRANSLATION_SNAP_METERS,
} from '../helpers/point-move-propagation.helpers';
import {
  buildWholeBuildingTranslationTargets,
  clampStoreyOverlapDelta,
  isWholeBuildingTranslate,
  resolveShapeTranslationTargets,
} from './shape-translation.helpers';
import {
  getBuildingRotationCenter,
  getBuildingRotationTargets,
  getBuildingRotationReferenceEdge,
  getRotationDeltaRadians,
  isBuildingRotationSnapDisabled,
  isPointerOutsideBuildingContour,
  snapBuildingRotationRadians,
} from '../helpers/building-rotation.helpers';
import { isBlueprintDrawingSnapDisabled } from '../helpers/blueprint-image-snap.helpers';

const TRACE_DRAFT_INTERACTIONS =
  import.meta.env.VITE_TRACE_DRAFT_INTERACTIONS === 'true';

type ShapeClickPayload = {
  shapeId: Shape['id'];
  event: MapMouseEvent;
};

/** Optional flags for a point or segment drag payload. */
type PointDragPayloadOptions = {
  isLineSegmentDrag?: boolean;
  lineSegmentPointIndexes?: readonly [number, number];
  dragStoreyId?: Storey['id'];
  modifiers?: PointDragModifiers;
};

const isShapeClickPayload = (
  payload: ShapeClickPayload | undefined,
): payload is ShapeClickPayload => guards.is.defined(payload);

type ShapeDragState = {
  shapeId: Shape['id'];
  storeyId: Storey['id'];
  chainTargets: { storeyId: Storey['id']; storeyLevel: number }[];
  elevation: number;
  pointerOrigin: Point2D;
  screenPoint?: Point2D;
  intersectionTargets?: Record<string, string[]>;
  wholeBuilding?: boolean;
  duplicateBuilding?: boolean;
};

type BuildingRotationDragState = {
  shapeId: Shape['id'];
  storeyId: Storey['id'];
  elevation: number;
  center: Point2D;
  pointerOrigin: Point2D;
  referenceEdge: [Point2D, Point2D] | undefined;
  hasMoved: boolean;
  targets: ShapeTranslatePayload['targets'];
};

type WholeBuildingDragAnchor = {
  shapeId: Shape['id'];
  storeyId: Storey['id'];
  elevation: number;
};

type MovingShapeForTranslation = {
  shape: Shape;
  storey: Storey;
};

type VirtualLineHit = {
  shape: Shape;
  shapeId: string;
  startIndex: number;
  endIndex: number;
  midpoint: Point2D;
  midpointCoordinate: Coordinate;
  lengthMeters: number;
  screenPoint: Point2D;
  distance: number;
  elevation: number;
  pointerPosition?: Point2D;
};

type DraftQuickTapResult =
  | { type: 'mouseup'; event: MapMouseEvent; elapsedMs: number }
  | { type: 'cancel' }
  | { type: 'destroy' };

/** How far left of the facade the height label stays hovered, including travel slack. */
export const FACADE_HEIGHT_LABEL_HOVER_REACH_PX = 80;

/** Vertical slack around the height-label anchor. */
export const FACADE_HEIGHT_LABEL_HOVER_SLOP_PX = 12;

export class MapEventHandler {
  /**
   * Object keeping track of the current selections on the map.
   */
  private readonly selections: MapSelectionObservables = {
    storeyId$: new BehaviorSubject<Storey['id'] | undefined>(undefined),
    pointIndexes$: new BehaviorSubject<number[] | undefined>(undefined),
    shapeId$: new BehaviorSubject<Shape['id'] | undefined>(undefined),
    draftShape$: new BehaviorSubject<boolean>(false),
  };

  private readonly hoverState$ = new BehaviorSubject<MapHoverState | undefined>(
    undefined,
  );

  private readonly pointMove$ = new BehaviorSubject<PointPayload | undefined>(
    undefined,
  );

  private readonly draftPreview$ = new BehaviorSubject<
    DraftPreviewPayload | undefined
  >(undefined);

  private readonly tooltip$ = new BehaviorSubject<MapTooltipPayload[]>([]);

  private readonly shapeTranslation$ = new BehaviorSubject<
    ShapeTranslatePayload | undefined
  >(undefined);
  private readonly buildingRotationCursor$ =
    new BehaviorSubject<BuildingRotationCursorState>({
      hover: false,
      dragging: false,
    });

  private interactionCancel$ = new Subject<void>();

  private pointMoveSubscription = new Subscription();

  private hasPointInsertCapability = false;

  private map: mapboxgl.Map | undefined;
  private observables: MapObservables | undefined;

  private destroy$ = new Subject<void>();
  private storeys: Storey[] = [];

  private selectedStoreyId: Storey['id'] | undefined;
  private selectedShape: Shape | DraftShape | undefined;
  private selectedShapeStoreyId: Storey['id'] | undefined;

  private get selectedStorey(): Storey | undefined {
    return getStoreyById(this.storeys, this.selectedStoreyId);
  }

  private draftShape: DraftShape | undefined;
  private buildingFootprintTemplate:
    BuildingFootprintTemplateGeometry | undefined;
  private customBuildingFootprintDrawingActive = false;

  private events: MapObservableCollection | undefined;
  private readonly state: MapStateObservables;
  private readonly stateSubscription = new Subscription();
  private hoverSubscription = new Subscription();
  private altKeySubscription = new Subscription();
  private isAltKeyActive = false;
  private isPointerForbidden = false;
  private isReadonly = false;
  /** Facade mode is presentation-only: geometry measurements stay hidden. */
  private facadeModeActive = false;
  private buildingContourProjector:
    | ((coordinate: Coordinate, elevation: number) => Point2D | undefined)
    | undefined;
  private facadePickRayResolver:
    ((screenPoint: Point2D) => BuildingVolumeRay | undefined) | undefined;
  /** Primary Design canvas tool (Select / Hand / Draw / Move building). */
  private designMapTool: 'build' | 'draw' | 'moveBuilding' = 'build';
  /** Space held — pan gesture only; does not change {@link designMapTool}. */
  private spacePanActive = false;
  /** True while PDF scale measure hides buildings so they cannot steal clicks. */
  private buildingPickingPaused = false;
  /** True when the pointer is over the PDF underlay (Select tool PDF hit-test). */
  private blueprintHitTest: ((event: MapMouseEvent) => boolean) | undefined;
  private blueprintHitTestClientPoint:
    ((clientX: number, clientY: number) => boolean) | undefined;
  private onBlueprintSelectRequest: (() => void) | undefined;
  private onBlueprintDeselectRequest: (() => void) | undefined;
  private isBlueprintSelected: (() => boolean) | undefined;
  private onBlueprintContextMenuRequest:
    ((position: { top: number; left: number }) => void) | undefined;
  /** Pen-tool mode mirror of {@link MapStateObservables.penMode$} (DEV-2576). */
  private penMode = false;
  /** Non-selected buildings, used so location rebase ignores sibling geometry. */
  private otherBuildings: Building[] = [];
  private readonly draftCursorState$: BehaviorSubject<MapDraftCursorState> =
    new BehaviorSubject<MapDraftCursorState>({
      active: false,
      suppressed: false,
    });
  private draftPreviewSubscription = new Subscription();
  private location: Coordinate;
  private readonly locationPinController: MapLocationPinController;
  private readonly hoverController: MapHoverController;
  private currentMouseScreenPoint: Point2D | undefined;
  private currentMouseEvent: MapMouseEvent | undefined;
  private interactionResetHandler: (() => void) | undefined;
  private activeShapeDrag: ShapeDragState | undefined;
  private activeBuildingRotation: BuildingRotationDragState | undefined;
  /** Targets for the in-flight shape drag — rebuilt only when the drag starts. */
  private cachedShapeTranslationTargets:
    ShapeTranslatePayload['targets'] | undefined;
  /** Shapes that move with the in-flight drag — rebuilt only when the drag starts. */
  private cachedMovingShapes: MovingShapeForTranslation[] | undefined;
  /**
   * Obstacle payload for overlap SAT — rebuilt only when the drag starts,
   * because storeys and sibling buildings do not change mid-gesture.
   */
  private cachedOverlapMoving: MovingPolygonForOverlapClamp[] | undefined;
  private isPointDragging = false;
  private pointGuideModifiers: PointDragModifiers = {
    shiftKey: false,
    ctrlKey: false,
    metaKey: false,
    altKey: false,
  };
  private insertPlaceholderSeed = 0;
  private readonly shapeTranslateEnd$ = new Subject<ShapeTranslateEvent>();
  private readonly shapeClick$ = new Subject<{
    shapeId: Shape['id'];
    event: MapMouseEvent;
  }>();
  private readonly storeyVolumeClick$ = new Subject<{
    storeyId: Storey['id'];
    event: MapMouseEvent;
  }>();
  private readonly deselectClick$ = new Subject<MapMouseEvent>();
  /** Last pointer event used to rebuild draft preview on external updates. */
  private lastDraftPointerEvent: MapMouseEvent | undefined;

  private readonly snapHandler: SnapAndBoundaries | undefined;
  /** Optional provider for the Design blueprint underlay (visibility / presence). */
  private getBlueprintUnderlay:
    (() => BlueprintUnderlay | undefined) | undefined;
  /**
   * Optional snap-to-ink provider: nearest dark line inside the blueprint raster
   * (not the outer page frame).
   */
  private snapToBlueprintInk:
    | ((
        point: Point2D,
        maxDistanceMeters: number,
      ) => { point: Point2D; snapped: boolean })
    | undefined;
  private onSelectBuilding: ((buildingId: string) => void) | undefined;
  private onOpenBuilding:
    ((buildingId: string, storeyId?: string) => void) | undefined;
  private onDeselectBuilding: (() => void) | undefined;
  private onHoverBuilding:
    ((buildingId: string | undefined) => void) | undefined;
  private currentBuildingId: string | undefined;
  private hoveredBuildingId: string | undefined;
  /** False while no building is selected (map background click, DEV-2576). */
  private hasBuildingSelection = true;
  /** Keep the active measurement family stable while its numeric input is focused. */
  private focusedGeometryMeasurement: MapGeometryMeasurementFocus | undefined;

  /**
   * Create a handler responsible for coordinating all map interactions.
   * @param map Initial {@link mapboxgl.Map} instance
   * @param state {@link MapStateObservables} from the design store
   * @param snapHandler Optional {@link SnapAndBoundaries} for geometric snapping
   */
  constructor(
    map: mapboxgl.Map,
    state: MapStateObservables,
    snapHandler?: SnapAndBoundaries,
  ) {
    this.snapHandler = snapHandler;
    this.state = state;
    this.location = state.location$.getValue();
    this.locationPinController = new MapLocationPinController({
      state,
      resolveMap: () => this.map,
      resolveObservables: () => this.observables,
      getCurrentLocation: () => this.location,
      isDesignEmpty: () => this.isEmptyDesign(),
      onTooltipUpdate: () => this.updateTooltip(),
    });
    this.hoverController = new MapHoverController({
      hoverState$: this.hoverState$,
      getStoreyAdjustedScreenPoint: (event) =>
        this.getStoreyAdjustedScreenPoint(event),
      onHoverStateChange: () => this.updateTooltip(),
    });
    this.stateSubscription.add(
      this.selections.pointIndexes$.subscribe((indexes) => {
        setHasSelectedVertices(Boolean(indexes?.length));
      }),
    );
    this.stateSubscription.add(
      this.selections.shapeId$.subscribe((shapeId) => {
        setHasSelectedShape(Boolean(shapeId));
      }),
    );
    this.stateSubscription.add(
      state.storeys$.subscribe((storeys) => {
        this.storeys = storeys;
        this.locationPinController.evaluateLocationPinMode();
        this.updateDraftCursorState();
        // Keep storey edge length labels in sync after geometry commits/drags.
        this.updateTooltip();
      }),
    );
    this.stateSubscription.add(
      state.selectedStoreyId$.subscribe((storeyId) => {
        if (this.selectedStoreyId === storeyId) return;
        this.selectedStoreyId = storeyId;
        this.updateSelectionStoreyId(storeyId);
        this.selections.pointIndexes$.next(undefined);
        this.selectShape(undefined);
        this.updateDraftCursorState();
        this.updateTooltip();
      }),
    );
    this.stateSubscription.add(
      state.draftShape$.subscribe((draftShape) => {
        this.draftShape = draftShape;
        const hasDraft = Boolean(draftShape);
        if (this.selections.draftShape$.getValue() !== hasDraft) {
          this.selections.draftShape$.next(hasDraft);
        }
        if (!draftShape) {
          this.draftPreview$.next(undefined);
        } else if (this.lastDraftPointerEvent) {
          // Refresh tip label as soon as a vertex is placed.
          this.draftPreview$.next(
            this.buildDraftPreviewPayload(this.lastDraftPointerEvent),
          );
        }
        this.updateTooltip();
        this.updateDraftCursorState();
      }),
    );
    this.stateSubscription.add(
      state.location$.subscribe((location) => {
        this.location = location;
        this.locationPinController.handleLocationChange(location);
      }),
    );
    this.stateSubscription.add(
      state.penMode$.subscribe((penMode) => {
        this.penMode = penMode;
        this.updateDraftCursorState();
      }),
    );
    this.stateSubscription.add(
      state.otherBuildings$.subscribe((buildings) => {
        this.otherBuildings = buildings;
      }),
    );
    this.stateSubscription.add(
      state.hasBuildingSelection$.subscribe((hasSelection) => {
        if (this.hasBuildingSelection === hasSelection) return;
        this.hasBuildingSelection = hasSelection;
        this.updateDraftCursorState();
        this.updateTooltip();
      }),
    );
    this.stateSubscription.add(
      state.isBuildingOutlineSelected$.subscribe((selected) => {
        if (!selected && !this.activeBuildingRotation) {
          this.setBuildingRotationCursorState({
            hover: false,
            dragging: false,
          });
        }
      }),
    );
    this.stateSubscription.add(
      state.locationPinMode$.subscribe((isPinMode) => {
        this.locationPinController.handlePinModeStateChange(isPinMode);
        if (isPinMode) {
          if (this.isAltKeyActive) {
            this.isAltKeyActive = false;
          }
          this.pointMove$.next(undefined);
          this.draftPreview$.next(undefined);
          this.hoverController.clearHoverState();
          this.setBuildingRotationCursorState({
            hover: false,
            dragging: false,
          });
        }
        this.updateDraftCursorState();
        this.updateTooltip();
      }),
    );
    this.attach(map);
  }

  /**
   * Attach an active {@link mapboxgl.Map} and initialise event streams.
   * @param map {@link mapboxgl.Map} to bind against
   */
  attach(map: mapboxgl.Map): void {
    this.detach();
    this.map = map;
    this.observables = createMapObservables(map);
    this.events = this.createPointEvents();
    this.setupAltKeyCursor();
    this.locationPinController.attach();
    this.updateTooltip();
  }

  /**
   * Update the currently selected shape reference.
   * @param shape {@link Shape} to select or undefined when clearing
   */
  private selectShape(shape: Shape | undefined, storeyId?: Storey['id']): void {
    if (this.selectedShape === shape && this.selectedShapeStoreyId === storeyId)
      return;
    this.selectedShape = shape;
    const candidateStoreyId = storeyId ?? this.selectedStoreyId;
    const resolvedStoreyId =
      shape && candidateStoreyId ? candidateStoreyId : undefined;
    this.selectedShapeStoreyId = resolvedStoreyId;
    this.selections.shapeId$.next(shape ? getId(shape) : undefined);
  }

  /**
   * Update selection storey tracking while respecting read-only mode.
   * @param storeyId {@link Storey} id or undefined
   */
  private updateSelectionStoreyId(storeyId: Storey['id'] | undefined): void {
    if (this.isReadonly) {
      if (this.selections.storeyId$.getValue() !== undefined) {
        this.selections.storeyId$.next(undefined);
      }
      return;
    }
    this.selections.storeyId$.next(storeyId);
  }

  /**
   * Toggle read-only mode and suppress map interactions.
   * @param isReadonly When true, disable selections and edits
   */
  setReadonly(isReadonly: boolean): void {
    if (this.isReadonly === isReadonly) return;
    this.isReadonly = isReadonly;
    this.locationPinController.setReadonly(isReadonly);
    if (isReadonly) {
      // Clear interaction state so shared view never shows edit affordances.
      this.cancelInteractions();
      this.deselectShapesAndPoints();
      this.selections.storeyId$.next(undefined);
      this.selections.pointIndexes$.next(undefined);
      this.selections.shapeId$.next(undefined);
      this.selections.draftShape$.next(false);
      this.hoverController.clearHoverState();
      this.draftPreview$.next(undefined);
      this.tooltip$.next([]);
      this.shapeTranslation$.next(undefined);
    } else {
      this.updateSelectionStoreyId(this.selectedStoreyId);
    }
    this.updateDraftCursorState();
  }

  /**
   * Sync the Design canvas tool used to gate draft / select / translate.
   */
  setDesignMapTool(tool: 'build' | 'draw' | 'moveBuilding'): void {
    if (this.designMapTool === tool) return;
    this.designMapTool = tool;
    this.setBuildingRotationCursorState({ hover: false, dragging: false });
    if (tool === 'draw') {
      this.deselectShapesAndPoints();
    }
    if (tool === 'moveBuilding') {
      // Whole-building drag only — drop vertex selection chrome.
      this.selections.pointIndexes$.next(undefined);
    }
    if (tool !== 'draw' && this.draftShape) {
      // Leaving Draw drops an unfinished footprint draft.
      this.draftShape = undefined;
      this.state.draftShape$.next(undefined);
      this.selections.draftShape$.next(false);
    }
    this.updateDraftCursorState();
  }

  /** Preview a complete new-building footprint at the live pointer position. */
  setBuildingFootprintTemplate(
    template: BuildingFootprintTemplateGeometry | undefined,
    customDrawingActive = false,
  ): void {
    this.buildingFootprintTemplate = template;
    if (template) this.setHoveredBuilding(undefined);
    this.customBuildingFootprintDrawingActive = customDrawingActive;
    // A panel selection must wait for a fresh pointer event on the map.
    this.lastDraftPointerEvent = undefined;
    this.draftPreview$.next(undefined);
    this.updateDraftCursorState();
  }

  /**
   * Space-pan gesture — cursor/navigation only; never changes the tool chip.
   */
  setSpacePanActive(active: boolean): void {
    if (this.spacePanActive === active) return;
    this.spacePanActive = active;
    this.locationPinController.setSpacePanActive(active);
    this.updateDraftCursorState();
  }

  /**
   * Pause building hover, pick, and translate while the PDF scale line is drawn.
   */
  setBuildingPickingPaused(paused: boolean): void {
    if (this.buildingPickingPaused === paused) return;
    this.buildingPickingPaused = paused;
    if (!paused) return;
    this.setBuildingRotationCursorState({ hover: false, dragging: false });
    this.setHoveredBuilding(undefined);
    this.hoverController.clearHoverState();
  }

  /**
   * Wire PDF hit-test + select/deselect callbacks for the Select tool.
   */
  setBlueprintSelectionHandlers(handlers: {
    hitTest?: (event: MapMouseEvent) => boolean;
    hitTestClientPoint?: (clientX: number, clientY: number) => boolean;
    onSelectRequest?: () => void;
    onDeselectRequest?: () => void;
    isSelected?: () => boolean;
    onContextMenuRequest?: (position: { top: number; left: number }) => void;
  }): void {
    this.blueprintHitTest = handlers.hitTest;
    this.blueprintHitTestClientPoint = handlers.hitTestClientPoint;
    this.onBlueprintSelectRequest = handlers.onSelectRequest;
    this.onBlueprintDeselectRequest = handlers.onDeselectRequest;
    this.isBlueprintSelected = handlers.isSelected;
    this.onBlueprintContextMenuRequest = handlers.onContextMenuRequest;
  }

  /**
   * Tear down all map bindings and unsubscribe from every stream.
   */
  detach(): void {
    this.resetDestroy$();
    this.hoverSubscription.unsubscribe();
    this.hoverSubscription = new Subscription();
    this.altKeySubscription.unsubscribe();
    this.altKeySubscription = new Subscription();
    this.pointMoveSubscription.unsubscribe();
    this.pointMoveSubscription = new Subscription();
    this.draftPreviewSubscription.unsubscribe();
    this.draftPreviewSubscription = new Subscription();
    this.interactionCancel$.next();
    this.interactionCancel$.complete();
    this.interactionCancel$ = new Subject<void>();
    this.hoverController.clearHoverState();
    this.pointMove$.next(undefined);
    this.isAltKeyActive = false;
    this.isPointerForbidden = false;
    this.updateDraftCursorState();
    this.draftPreview$.next(undefined);
    this.activeBuildingRotation = undefined;
    this.shapeTranslation$.next(undefined);
    this.setBuildingRotationCursorState({ hover: false, dragging: false });
    this.updateTooltip();
    // Note: observables will be destroyed automatically when the map is removed
    this.observables?.destroy();
    this.observables = undefined;
    this.map = undefined;
    this.events = undefined;
    this.currentMouseEvent = undefined;
    this.locationPinController.detach();
    this.interactionResetHandler = undefined;
    this.hoverController.deactivateLineInsertCandidateHover();
  }

  /**
   * Dispose the handler permanently and release store subscriptions.
   */
  dispose(): void {
    this.detach();
    this.stateSubscription.unsubscribe();
    setHasSelectedVertices(false);
    setHasSelectedShape(false);
  }

  /**
   * Read the current selection snapshot.
   * @returns {@link MapSelections}
   */
  getSelections(): MapSelections {
    return getMapSelectionsFromObservables(this.selections);
  }

  /**
   * Expose the live selection observables for external subscribers.
   * @returns {@link MapSelectionObservables}
   */
  getSelectionObservables(): MapSelectionObservables {
    return this.selections;
  }

  /**
   * Expose location pin cursor state for renderer-controlled cursors.
   * @returns {@link BehaviorSubject} of {@link LocationPinCursorState}
   */
  getLocationPinCursor$(): BehaviorSubject<LocationPinCursorState> {
    return this.locationPinController.getCursorState$();
  }

  /**
   * Activate a point selection for one or more points.
   * @param shape {@link Shape} containing the points
   * @param pointIndexes Zero-based indexes of the selected points
   */
  selectPoints(shape: Shape | undefined, pointIndexes: number[]): void {
    if (this.isReadonly) return;
    if (!shape) return;
    this.selectShape(shape, this.selectedStoreyId);
    this.selections.shapeId$.next(shape.id);
    this.selections.pointIndexes$.next(
      pointIndexes.length > 0 ? pointIndexes : undefined,
    );
    this.selections.draftShape$.next(false);
    this.updateTooltip();
  }

  /**
   * Apply selection updates from a {@link PointPayload} produced by this handler.
   *
   * Henrik: selection is state, but it is owned by subscribers; this method is purely a setter.
   */
  applyPointSelection(payload: PointPayload): void {
    if (this.isReadonly) return;
    const shapeId =
      payload.shapeId ??
      (typeof payload.shape.id === 'string' ? payload.shape.id : undefined);
    if (!shapeId) return;
    const current = this.selections.pointIndexes$.getValue();
    const isSameShape = this.selections.shapeId$.getValue() === shapeId;
    const shouldExtendMultiPointSelection =
      Boolean(payload.isMetaOrCtrlKeyPressed) &&
      isSameShape &&
      Boolean(current && current.length > 1);
    if (shouldExtendMultiPointSelection) {
      this.selections.pointIndexes$.next([
        ...new Set([...(current ?? []), payload.index]),
      ]);
      this.selections.draftShape$.next(payload.isDraft);
      return;
    }
    const isShiftToggle = Boolean(payload.isShiftKeyPressed);
    if (isShiftToggle) {
      const currentIndexes = isSameShape
        ? current?.length
          ? current
          : payload.shape.points.map((_point, index) => index)
        : [];
      const hasIndex = currentIndexes.includes(payload.index);
      const nextIndexes = hasIndex
        ? currentIndexes.filter((index) => index !== payload.index)
        : [...currentIndexes, payload.index];
      this.selections.shapeId$.next(nextIndexes.length ? shapeId : undefined);
      this.selections.pointIndexes$.next(
        nextIndexes.length ? nextIndexes : undefined,
      );
      this.selections.draftShape$.next(payload.isDraft);
      return;
    }

    const shouldPreserveMultiPointSelection = current
      ? Boolean(payload.preserveMultiPointSelection) &&
        current.length > 1 &&
        current.includes(payload.index)
      : false;
    this.selections.shapeId$.next(shapeId);
    // Keep segment selection when the drag originates from that selected segment.
    this.selections.pointIndexes$.next(
      shouldPreserveMultiPointSelection ? current : [payload.index],
    );
    this.selections.draftShape$.next(payload.isDraft);
  }

  /**
   * Select a shape id for the currently selected storey.
   */
  selectShapeById(shapeId: Shape['id']): void {
    if (this.isReadonly) return;
    this.selectShapeByIdInternal(shapeId);
  }

  /**
   * Select an entire shape when clicking inside its polygon.
   * @param shapeId {@link Shape['id']} belonging to the current storey
   */
  private selectShapeByIdInternal(shapeId: Shape['id']): void {
    const storey = this.selectedStorey;
    if (!storey?.shapes?.length) return;
    const shape = storey.shapes.find((candidate) => candidate.id === shapeId);
    if (!shape) return;
    // Clear point selection so all vertices highlight together.
    this.selections.pointIndexes$.next(undefined);
    this.selections.draftShape$.next(false);
    this.selectShape(shape, storey.id);
    this.selectedShapeStoreyId = storey.id;
  }

  /**
   * Resolve the polygon id under the pointer for the currently selected storey, even when upper storeys overlap.
   * @param event {@link MapMouseEvent} pointer event from Mapbox
   */
  private resolveSelectedStoreyShapeIdFromPointerEvent(
    event: MapMouseEvent,
  ): Shape['id'] | undefined {
    const selectedStoreyId = this.selectedStoreyId;
    if (!selectedStoreyId) return undefined;
    const selectedLevel = getStoreyIndex(this.storeys, selectedStoreyId);
    if (selectedLevel < 0) return undefined;
    const storey = this.selectedStorey;

    // Only use geometric smallest-polygon picking when cutouts exist;
    // otherwise the render-based query handles pitched/rotated maps correctly.
    const hasCutouts = storey?.shapes?.some((s) => s.cutout === true);
    if (hasCutouts) {
      const geometricPick =
        this.resolveShapeIdBySmallestContainingPolygonForSelectedStorey(event);
      if (geometricPick) return geometricPick;
    }

    const toShapeHit = (
      feature: mapboxgl.MapboxGeoJSONFeature,
    ): ShapeHit | undefined => {
      if (!mapGuards.is.feature.polygon(feature)) return undefined;
      const shapeId = feature.properties?.id;
      const storeyLevel = Number(feature.properties?.[MapStoreyProperty.Level]);
      if (typeof shapeId !== 'string' || shapeId.length === 0) return undefined;
      if (!Number.isFinite(storeyLevel)) return undefined;
      return { shapeId, storeyLevel };
    };

    // Prefer the raw click point, then fall back to a storey-adjusted query point for pitched maps.
    const pointOverrides: PointLike[] = [];
    const registerPoint = (candidate: PointLike | undefined): void => {
      if (!candidate) return;
      const screen = this.toScreenPoint(candidate);
      if (!screen) return;
      if (
        pointOverrides.some((existing) => {
          const existingScreen = this.toScreenPoint(existing);
          return (
            existingScreen?.x === screen.x && existingScreen?.y === screen.y
          );
        })
      ) {
        return;
      }
      pointOverrides.push(candidate);
    };

    registerPoint(event.point ?? this.map?.project(event.lngLat));
    registerPoint(this.getStoreyAdjustedQueryPoint(event));

    for (const pointOverride of pointOverrides) {
      const hits = this.queryRenderedFeaturesAroundPoint(
        event,
        [MapLayerIds.BuildingFill2d],
        0,
        pointOverride,
      )
        .map((feature) => toShapeHit(feature))
        .filter((hit): hit is ShapeHit => guards.is.defined(hit));

      const resolved = resolveShapeIdForStoreyLevel(
        hits,
        selectedLevel,
        storey?.shapes,
      );
      if (resolved) return resolved;
    }

    // A cutout hole has no fill feature, so fall back to geometric hit-testing.
    return this.resolveCutoutShapeIdFromPointerEvent(event);
  }

  /**
   * Prefer the smallest polygon containing the pointer so cutouts win over enclosing solids.
   * @param event {@link MapMouseEvent} from Mapbox
   */
  private resolveShapeIdBySmallestContainingPolygonForSelectedStorey(
    event: MapMouseEvent,
  ): Shape['id'] | undefined {
    const storey = this.selectedStorey;
    if (!storey?.shapes?.length) return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointer = this.getMetricPointerPosition(event, elevation);
    if (!pointer) return undefined;

    const candidates = storey.shapes.filter(
      (shape) =>
        isPolygon2D(shape.points) && isPointInPolygon(pointer, shape.points),
    );
    if (!candidates.length) return undefined;

    return candidates.reduce<Shape | undefined>((best, candidate) => {
      if (!best) return candidate;
      return computePolygonArea(candidate.points) <
        computePolygonArea(best.points)
        ? candidate
        : best;
    }, undefined)?.id;
  }

  /**
   * Resolve cutout shape id under pointer for the selected storey.
   * Used when holes are not represented as individual fill features.
   */
  private resolveCutoutShapeIdFromPointerEvent(
    event: MapMouseEvent,
  ): Shape['id'] | undefined {
    const storey = this.selectedStorey;
    if (!storey?.shapes?.length) return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointer = this.getMetricPointerPosition(event, elevation);
    if (!pointer) return undefined;

    const cutoutCandidates = storey.shapes
      .filter((shape) => shape.cutout === true && shape.points?.length >= 3)
      .filter((shape) => isPointInPolygon(pointer, shape.points));
    if (!cutoutCandidates.length) return undefined;

    // Prefer the smallest candidate if cutouts are nested.
    return cutoutCandidates.reduce<Shape | undefined>((best, candidate) => {
      if (!best) return candidate;
      return computePolygonArea(candidate.points) <
        computePolygonArea(best.points)
        ? candidate
        : best;
    }, undefined)?.id;
  }

  /**
   * Clear all active shape and point selections.
   */
  deselectShapesAndPoints(): void {
    this.resetShapeTranslationState();
    this.selectShape(undefined);
    // Ensure shape selection clears even when selectedShape is unset.
    this.selections.shapeId$.next(undefined);
    this.selections.pointIndexes$.next(undefined);
    this.selectedShapeStoreyId = undefined;
    this.updateTooltip();
  }

  /**
   * Get the hover state subject used by UI consumers.
   * @returns {@link BehaviorSubject} of {@link MapHoverState}
   */
  getHoverState$() {
    return this.hoverState$;
  }

  /**
   * Listen to active point move payloads.
   * @returns Observable of {@link PointPayload}
   */
  getPointMove$(): Observable<PointPayload | undefined> {
    return this.pointMove$.asObservable();
  }

  /**
   * Stream draft preview distance updates.
   * @returns Observable of {@link DraftPreviewPayload}
   */
  getDraftPreview$(): Observable<DraftPreviewPayload | undefined> {
    return this.draftPreview$.asObservable();
  }

  /**
   * Stream tooltip payload updates.
   * @returns Observable of {@link MapTooltipPayload}
   */
  getTooltip$(): Observable<MapTooltipPayload[]> {
    return this.tooltip$.asObservable();
  }

  setGeometryMeasurementFocus(
    focus: MapGeometryMeasurementFocus | undefined,
  ): void {
    if (
      this.focusedGeometryMeasurement?.kind === focus?.kind &&
      this.focusedGeometryMeasurement?.shapeId === focus?.shapeId
    ) {
      return;
    }
    this.focusedGeometryMeasurement = focus;
    this.updateTooltip();
  }

  /** Hide selected-storey measurement labels in the exact 90° facade view. */
  setFacadeMode(active: boolean): void {
    if (this.facadeModeActive === active) return;
    this.facadeModeActive = active;
    if (active)
      this.setBuildingRotationCursorState({ hover: false, dragging: false });
    this.refreshHoveredBuildingAtCurrentPointer();
    this.updateTooltip();
  }

  /** Use the same custom projection for facade hover/click picking as rendering. */
  setFacadePickRayResolver(
    resolver:
      ((screenPoint: Point2D) => BuildingVolumeRay | undefined) | undefined,
  ): void {
    this.facadePickRayResolver = resolver;
  }

  /** Match rotation hit testing to the rendered silhouette, including facade mode. */
  setBuildingContourProjector(
    projector: (
      coordinate: Coordinate,
      elevation: number,
    ) => Point2D | undefined,
  ): void {
    this.buildingContourProjector = projector;
  }

  /**
   * Stream shape translation previews.
   * @returns Observable of {@link ShapeTranslatePayload}
   */
  getShapeTranslation$(): Observable<ShapeTranslatePayload | undefined> {
    return this.shapeTranslation$.asObservable();
  }

  getBuildingRotationHover$(): Observable<boolean> {
    return this.buildingRotationCursor$.pipe(
      map((state) => state.hover || state.dragging),
      distinctUntilChanged(),
    );
  }

  isBuildingRotationActive(): boolean {
    return Boolean(this.activeBuildingRotation);
  }

  isPointDragActive(): boolean {
    return this.isPointDragging;
  }

  getPointSnapGuidePreview():
    Pick<PointPayload, 'pointDragSnapGuides' | 'elevation'> | undefined {
    const move = this.pointMove$.getValue();
    if (move) return move;
    const keys = this.pointGuideModifiers;
    if (
      !keys.shiftKey ||
      keys.metaKey ||
      keys.ctrlKey ||
      this.focusedGeometryMeasurement ||
      this.isReadonly
    )
      return undefined;
    const indexes = this.selections.pointIndexes$.getValue();
    if (indexes?.length !== 1) return undefined;
    const index = indexes[0];
    if (index === undefined) return undefined;
    const isDraft = this.selections.draftShape$.getValue();
    const storey = this.selectedStorey;
    const shape = isDraft
      ? this.draftShape
      : storey?.shapes.find(
          (candidate) => candidate.id === this.selections.shapeId$.getValue(),
        );
    if (!shape) return undefined;
    const pointDragSnapGuides = getPointDragSnapGuides(
      shape.points,
      index,
      isDraft,
    );
    if (!pointDragSnapGuides) return undefined;
    return {
      pointDragSnapGuides,
      elevation: storey ? getStoreyElevation(this.storeys, storey) : 0,
    };
  }

  /** True when Ctrl on Windows belongs to tracing rather than camera rotation. */
  isUnderlayDrawingActive(): boolean {
    return (
      !this.isReadonly &&
      this.allowsDrafting() &&
      Boolean(this.getBlueprintUnderlay?.()?.visible) &&
      !(this.isWholeBuildingTranslateActive() && this.designMapTool === 'build')
    );
  }

  private isUnderlayDrawingSnapDisabled(event?: MapMouseEvent): boolean {
    if (!this.isUnderlayDrawingActive() || !event) return false;
    return isBlueprintDrawingSnapDisabled(
      event.originalEvent,
      navigator.userAgent.includes('Mac'),
    );
  }

  /**
   * Toggle whether point insertion is available and refresh tooltips.
   * @param value Boolean flag indicating capability
   */
  setHasPointInsertCapability(value: boolean): void {
    if (this.hasPointInsertCapability === value) return;
    this.hasPointInsertCapability = value;
    if (!value) {
      this.hoverController.deactivateLineInsertCandidateHover();
    }
    this.updateTooltip();
  }

  /**
   * Provide a callback invoked whenever interactions reset.
   * @param handler Optional callback
   */
  setInteractionResetHandler(handler: (() => void) | undefined): void {
    this.interactionResetHandler = handler;
  }

  /**
   * Configure the handler responsible for committing building translations.
   * @param handler {@link BuildingTranslationHandler} implementation
   */
  setBuildingTranslationHandler(handler: BuildingTranslationHandler): void {
    this.locationPinController.setBuildingTranslationHandler(handler);
  }

  /**
   * Provide the current blueprint underlay (used to decide when ink-snap applies).
   */
  setBlueprintUnderlayProvider(
    provider: (() => BlueprintUnderlay | undefined) | undefined,
  ): void {
    this.getBlueprintUnderlay = provider;
  }

  /**
   * Provide snap-to-ink against lines inside the blueprint image.
   */
  setBlueprintInkSnapProvider(
    provider:
      | ((
          point: Point2D,
          maxDistanceMeters: number,
        ) => { point: Point2D; snapped: boolean })
      | undefined,
  ): void {
    this.snapToBlueprintInk = provider;
  }

  /**
   * Called when the user clicks a non-selected building footprint (DEV-2576).
   * @param handler Receives the clicked building id
   */
  setBuildingSelectionHandler(
    handler: ((buildingId: string) => void) | undefined,
  ): void {
    this.onSelectBuilding = handler;
  }

  /** Called when a building volume is double-clicked to open its storey view. */
  setBuildingOpenHandler(
    handler: ((buildingId: string, storeyId?: string) => void) | undefined,
  ): void {
    this.onOpenBuilding = handler;
  }

  /**
   * Called when a map background click should clear the building selection
   * (DEV-2576).
   * @param handler Invoked with no arguments when the selection is cleared
   */
  setBuildingDeselectionHandler(handler: (() => void) | undefined): void {
    this.onDeselectBuilding = handler;
  }

  /**
   * Called with the building id under the pointer, or undefined when the
   * pointer leaves every sibling footprint (DEV-2576).
   * @param handler Receives the hovered building id
   */
  setBuildingHoverHandler(
    handler: ((buildingId: string | undefined) => void) | undefined,
  ): void {
    this.onHoverBuilding = handler;
    if (!handler) this.hoveredBuildingId = undefined;
  }

  /** Set the id that owns the editable storeys currently rendered on the map. */
  setCurrentBuildingId(buildingId: string | undefined): void {
    if (this.currentBuildingId === buildingId) return;
    this.currentBuildingId = buildingId;
    if (
      !shouldRetainBuildingHoverAfterSelection(
        this.hoveredBuildingId,
        buildingId,
      )
    ) {
      this.setHoveredBuilding(undefined);
    }
  }

  /** Re-evaluate building hover without requiring the stationary pointer to move. */
  refreshHoveredBuildingAtCurrentPointer(): void {
    const event = this.currentMouseEvent;
    if (!event || this.spacePanActive || this.isReadonly) return;
    this.setHoveredBuilding(this.getHoveredBuildingIdUnderPointer(event));
  }

  /** Emit only on change so React does not re-render on every mouse move. */
  private setHoveredBuilding(buildingId: string | undefined): void {
    if (this.buildingFootprintTemplate) buildingId = undefined;
    const ray =
      !this.buildingFootprintTemplate &&
      this.currentMouseEvent &&
      this.getFacadeBuildingPickRay(this.currentMouseEvent);
    let hoveredStoreyId =
      buildingId === this.currentBuildingId &&
      this.state.hasBuildingSelection$.getValue()
        ? ray
          ? pickStoreyByVolumeRay(ray, this.storeys)?.id
          : this.currentMouseEvent &&
            this.getTopmostSelectedStoreyIdUnderPointer(this.currentMouseEvent)
        : undefined;
    // Bridge the small gap between the facade and its editable height label.
    // Otherwise the label disappears while the pointer travels to the input.
    const previousId = this.state.hoveredStoreyId$.getValue();
    const previous = this.storeys.find(({ id }) => id === previousId);
    const pointer = this.currentMouseEvent?.point;
    if (!hoveredStoreyId && !buildingId && ray && previous && pointer) {
      const range = getStoreyElevationRange(this.storeys, previous);
      const projected = getSolidShapes(previous)
        .flatMap((shape) => shape.points)
        .flatMap((point) => {
          const screen = this.buildingContourProjector?.(
            convertMetricOffsetToCoordinate(this.location, point),
            (range.bottom + range.top) / 2,
          );
          return screen ? [screen] : [];
        });
      const first = projected[0];
      const left = Math.min(...projected.map((point) => point[0]));
      if (
        first &&
        pointer.x >= left - FACADE_HEIGHT_LABEL_HOVER_REACH_PX &&
        pointer.x <= left &&
        Math.abs(pointer.y - first[1]) <= FACADE_HEIGHT_LABEL_HOVER_SLOP_PX
      )
        hoveredStoreyId = previousId;
    }
    if (this.state.hoveredStoreyId$.getValue() !== hoveredStoreyId)
      this.state.hoveredStoreyId$.next(hoveredStoreyId);

    if (this.hoveredBuildingId === buildingId) return;
    this.hoveredBuildingId = buildingId;
    this.onHoverBuilding?.(buildingId);
  }

  /**
   * Read the latest hover state synchronously.
   * @returns {@link MapHoverState} or undefined when idle
   */
  getHoverState(): MapHoverState | undefined {
    return this.hoverState$.getValue();
  }

  /**
   * Abort all ongoing interactions and clear transient state.
   */
  cancelInteractions(): void {
    this.interactionCancel$.next();
    this.endPointDrag();
    this.pointMove$.next(undefined);
    this.resetShapeTranslationState();
    this.hoverController.clearHoverState();
    this.updateTooltip();
    this.interactionResetHandler?.();
  }

  /**
   * Build a reusable context object for point helper utilities.
   * @returns {@link PointActionContext}
   */
  private getPointActionContext(): PointActionContext {
    return {
      map: this.map,
      location: this.location,
      storeys: this.storeys,
      selectedStoreyId: this.selectedStoreyId,
      handlers: undefined,
      getPositionFromCoordinate: (coordinate: Coordinate) =>
        this.getPositionFromCoordinate(coordinate),
      selectPoints: (shape: Shape, pointIndexes: number[]) =>
        this.selectPoints(shape, pointIndexes),
    };
  }

  /**
   * Stream shape translate end events to external consumers.
   * @returns Observable of {@link ShapeTranslateEvent}
   */
  getShapeTranslateEnd$(): Observable<ShapeTranslateEvent> {
    return this.shapeTranslateEnd$.asObservable();
  }

  getShapeClick$(): Observable<{ shapeId: Shape['id']; event: MapMouseEvent }> {
    return this.shapeClick$.asObservable();
  }

  /** Clicks that resolve to another visible storey in the selected building. */
  getStoreyVolumeClick$(): Observable<{
    storeyId: Storey['id'];
    event: MapMouseEvent;
  }> {
    return this.storeyVolumeClick$.asObservable();
  }

  getDeselectClick$(): Observable<MapMouseEvent> {
    return this.deselectClick$.asObservable();
  }

  /**
   * Access the active map observable collection.
   * @returns {@link MapObservableCollection} or undefined when not initialized
   */
  // TODO: rename to events
  get pointEvents(): MapObservableCollection | undefined {
    return this.events;
  }

  /**
   * Access raw map observables for lower-level integrations.
   * @returns {@link MapObservables} or undefined when not initialized
   */
  get eventStreams(): MapObservables | undefined {
    return this.observables;
  }

  /**
   * Retrieve the draft cursor state subject for consumers.
   * @returns {@link BehaviorSubject} of {@link MapDraftCursorState}
   */
  getDraftCursorState$(): BehaviorSubject<MapDraftCursorState> {
    return this.draftCursorState$;
  }

  /**
   * Recreate the destroy subject to reset observable lifetimes.
   */
  private resetDestroy$(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
  }

  /**
   * Track the Alt key and toggle draft cursor states.
   */
  private setupAltKeyCursor(): void {
    const observables = this.observables;
    if (!observables || !this.map) return;

    const destroy$ = this.destroy$;

    const altKeyDown$ = observables.dom.key.down$.pipe(
      filter((keyboardEvent) => keyboardEvent.key === 'Alt'),
      takeUntil(destroy$),
    );

    const altKeyUp$ = observables.dom.key.up$.pipe(
      filter((keyboardEvent) => keyboardEvent.key === 'Alt'),
      takeUntil(destroy$),
    );

    this.altKeySubscription.add(
      altKeyDown$.subscribe(() => {
        this.isAltKeyActive = true;
        this.updateBuildingDuplicationPreview(true);
        this.updateDraftCursorState();
      }),
    );

    this.altKeySubscription.add(
      altKeyUp$.subscribe(() => {
        this.isAltKeyActive = false;
        this.updateBuildingDuplicationPreview(false);
        this.updateDraftCursorState();
      }),
    );
  }

  /** Switch an active building drag immediately, even without pointer movement. */
  private updateBuildingDuplicationPreview(altKey: boolean): void {
    const state = this.activeShapeDrag;
    if (!state?.wholeBuilding) return;
    state.duplicateBuilding = altKey;
    const payload = this.shapeTranslation$.getValue();
    if (payload) {
      this.shapeTranslation$.next({ ...payload, duplicateBuilding: altKey });
    }
  }

  /**
   * Wire up all pointer and keyboard event streams for geometry editing.
   * @returns {@link MapObservableCollection} or undefined if map unavailable
   */
  private createPointEvents(): MapObservableCollection | undefined {
    const mapInstance = this.map;
    const observables = this.observables;
    if (!observables || !mapInstance) return undefined;

    this.hoverSubscription.unsubscribe();
    this.hoverSubscription = new Subscription();

    const destroy$ = this.destroy$;
    const interactionCancel$ = this.interactionCancel$;
    const mouseDown$ = observables.map('mousedown');
    const mouseMove$ = observables.map('mousemove');
    this.hoverSubscription.add(
      merge(
        fromEvent<KeyboardEvent>(window, 'keydown'),
        fromEvent<KeyboardEvent>(window, 'keyup'),
      )
        .pipe(
          filter((event) => ['Shift', 'Control', 'Meta'].includes(event.key)),
          takeUntil(destroy$),
        )
        .subscribe((event) => {
          this.pointGuideModifiers = {
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            altKey: event.altKey,
          };
          mapInstance.triggerRepaint();
        }),
    );
    this.hoverSubscription.add(
      fromEvent(window, 'blur')
        .pipe(takeUntil(destroy$))
        .subscribe(() => {
          this.pointGuideModifiers = {
            shiftKey: false,
            ctrlKey: false,
            metaKey: false,
            altKey: false,
          };
          mapInstance.triggerRepaint();
        }),
    );
    // Map navigation is only allowed when a primary-button gesture starts
    // outside building geometry. This also lets the swapped left-drag rotate
    // gesture coexist with building selection and translation.
    this.hoverSubscription.add(
      mouseDown$
        .pipe(
          filter((event) => this.isPrimaryMouseButton(event)),
          takeUntil(destroy$),
        )
        .subscribe((event) => {
          this.currentMouseEvent = event;
          if (
            this.hasShapeUnderCursor(event) ||
            this.hasSelectedBuildingVolumeHit(event) ||
            this.getOtherBuildingIdUnderPointer(event)
          ) {
            event.preventDefault();
          }
        }),
    );
    // Track mouse position for tooltip positioning
    this.hoverSubscription.add(
      mouseMove$.pipe(takeUntil(destroy$)).subscribe((event) => {
        this.currentMouseEvent = event;
        this.currentMouseScreenPoint = toPoint2D(
          event.point ?? this.map?.project(event.lngLat),
        );
      }),
    );
    this.hoverSubscription.add(
      observables
        .map('move')
        .pipe(takeUntil(destroy$))
        .subscribe(() => this.updateTooltip()),
    );
    const draftCursorSuppression$ = mouseMove$.pipe(
      filter(() => !this.isReadonly),
      map((event) => this.isPointerInsideForbiddenShape(event)),
      distinctUntilChanged(),
      takeUntil(destroy$),
    );
    const mouseUp$ = observables.map('mouseup');
    const shapeClickRecovery$ = new Subject<MapMouseEvent>();
    const mapClick$ = merge(observables.map('click'), shapeClickRecovery$).pipe(
      share(),
    );
    const mapDblClick$ = observables.map('dblclick').pipe(share());
    const draftQuickTap$ = this.buildDraftQuickTapStream(
      mouseDown$,
      mouseUp$,
      interactionCancel$,
      destroy$,
    );
    const editableKeyDown$ = observables.dom.key.down$.pipe(
      filter(() => !this.isReadonly),
      takeUntil(destroy$),
      share(),
    );
    const escape$ = editableKeyDown$.pipe(
      filter((keyboardEvent) => keyboardEvent.key === KeyboardKeys.ESCAPE),
    );
    const backspace$ = editableKeyDown$.pipe(
      filter(
        (keyboardEvent) =>
          keyboardEvent.key === KeyboardKeys.BACKSPACE ||
          keyboardEvent.key === KeyboardKeys.DELETE,
      ),
    );

    const tab$ = editableKeyDown$.pipe(
      filter((event) => shouldHandleMapStoreyTabShortcut(event)),
    );
    const storeyIndex$ = editableKeyDown$.pipe(
      filter((e) => {
        const digit = parseInt(e.key, 10);
        return (e.metaKey || e.ctrlKey) && digit >= 1 && digit <= 9;
      }),
    );

    const draftClick$ = this.buildDraftClickStream(
      merge(mapClick$, draftQuickTap$),
      mapInstance,
      destroy$,
    );

    const lineClick$ = this.buildLineClickStream(mapClick$, destroy$);
    const lineDoubleClick$ = mapDblClick$.pipe(
      filter(() => !this.isReadonly),
      filter(
        () => this.designMapTool === 'build' && Boolean(this.selectedStoreyId),
      ),
      filter(
        () =>
          this.hasPointInsertCapability &&
          !this.spacePanActive &&
          !this.buildingPickingPaused,
      ),
      filter(
        () => !this.draftShape && !this.locationPinController.isPinModeActive(),
      ),
      filter(
        (event) =>
          this.isPrimaryMouseButton(event) && !this.hasPointUnderCursor(event),
      ),
      exhaustMap((event) => from(this.buildInsertPointPayload(event, true))),
      filter((payload): payload is PointPayload => guards.is.defined(payload)),
      takeUntil(destroy$),
    );

    const pointMouseOver$ = this.buildPointMouseOverStream(
      observables,
      mapInstance,
      destroy$,
    );

    const pointMouseLeave$ = this.buildPointMouseLeaveStream(
      observables,
      mapInstance,
      destroy$,
    );

    const pointDoubleClick$ = observables
      .layer('dblclick', MapLayerIds.Points)
      .pipe(
        filter(() => !this.isReadonly),
        map((layerEvent) => {
          const feature = queryPointFeature(mapInstance, layerEvent);
          if (this.isInsertCandidateFeature(feature)) return undefined;
          const meta = resolvePointMeta(
            feature,
            this.draftShape,
            this.storeys,
            this.selectedStoreyId,
          );
          if (!meta || meta.isDraft) return undefined;
          return this.buildPointPayload(layerEvent, meta, meta.origin);
        }),
        filter((payload): payload is PointPayload =>
          guards.is.defined(payload),
        ),
        takeUntil(destroy$),
      );

    const draftPointMouseOver$ = this.buildDraftPointMouseOverStream(
      observables,
      destroy$,
    );

    const draftPointMouseLeave$ = this.buildDraftPointMouseLeaveStream(
      observables,
      destroy$,
    );

    const pointLayerMouseDown$ = this.buildPointLayerMouseDownStream(
      observables,
      destroy$,
    );

    const pointMouseDown$ = this.buildPointMouseDownStream(
      pointLayerMouseDown$,
      mapInstance,
      destroy$,
    );

    const insertPointDragStart$ = this.buildInsertPointDragStartStream(
      pointLayerMouseDown$,
      mapInstance,
      destroy$,
    );

    const lineDragStart$ = this.buildLineDragStartStream(observables, destroy$);

    const pointDragStart$ = merge(
      pointMouseDown$,
      insertPointDragStart$,
      lineDragStart$,
    ).pipe(
      map((payload) => ({
        ...payload,
        dragStartedAtMs: Date.now(),
        dragStartScreenPoint: payload.screenPoint,
      })),
      share(),
    );

    const modifierPointerMove$ = createPointDragModifierMoves({
      target: window,
      getPointer: () => this.currentMouseEvent,
      isDragging: () => this.isPointDragging,
    });
    const pointMouseMove$ = this.buildPointMouseMoveStream(
      pointDragStart$,
      merge(mouseMove$.pipe(map((event) => ({ event }))), modifierPointerMove$),
      mouseUp$,
      interactionCancel$,
      destroy$,
    );

    const pointMouseUp$ = this.buildPointMouseUpStream(
      pointDragStart$,
      mouseUp$,
      interactionCancel$,
      destroy$,
    );

    this.pointMoveSubscription.unsubscribe();
    this.pointMoveSubscription = new Subscription();

    const activePointMove$ = merge(pointDragStart$, pointMouseMove$);

    this.pointMoveSubscription.add(
      activePointMove$.subscribe((payload) => {
        this.pointMove$.next(payload);
        this.updateTooltip();
      }),
    );

    this.pointMoveSubscription.add(
      pointMouseUp$.subscribe(() => {
        this.pointMove$.next(undefined);
        this.updateTooltip();
      }),
    );

    this.draftPreviewSubscription.unsubscribe();
    this.draftPreviewSubscription = new Subscription();
    this.draftPreviewSubscription.add(
      mouseMove$
        .pipe(
          filter(() => !this.isReadonly),
          filter(() => !this.locationPinController.isPinModeActive()),
          map((event) => this.buildDraftPreviewPayload(event)),
          takeUntil(destroy$),
        )
        .subscribe((payload) => {
          this.draftPreview$.next(payload);
          this.updateTooltip();
        }),
    );

    const lineHover$ = mouseMove$.pipe(
      filter(() => !this.isReadonly),
      filter(() => !this.buildingPickingPaused),
      map((event) => {
        if (event.originalEvent.ctrlKey) return undefined;
        const hasPoint = this.hasPointUnderCursor(event);
        if (!hasPoint) {
          this.hoverController.clearHoverState(MapHoverStateType.Point);
          this.hoverController.clearHoverState(MapHoverStateType.DraftPoint);
        }
        if (this.isPointDragging || this.isShapeDragging() || hasPoint) {
          return undefined;
        }
        const hit = this.resolveVirtualLineHit(event);
        return this.hoverController.buildLineHoverState(
          this.toLineHoverHit(hit),
          this.hasPointInsertCapability,
        );
      }),
      takeUntil(destroy$),
    );

    const draftLineHover$ = mouseMove$.pipe(
      filter(() => !this.isReadonly),
      map((event) => {
        const hasPoint = this.hasPointUnderCursor(event);
        if (!hasPoint) {
          this.hoverController.clearHoverState(MapHoverStateType.Point);
          this.hoverController.clearHoverState(MapHoverStateType.DraftPoint);
        }
        if (this.isPointDragging || this.isShapeDragging() || hasPoint) {
          // Hide add-point hover while dragging to avoid conflicting cues.
          return undefined;
        }
        return this.hoverController.buildDraftLineHoverState(
          this.resolveVirtualDraftLineHit(event),
        );
      }),
      takeUntil(destroy$),
    );

    // Resolve from the map pointer, including elevated geometry and cutout holes;
    // a fill-layer mousemove does not cover every place a shape drag can start.
    const shapeMouseMove$ = mouseMove$.pipe(
      filter(() => !this.isReadonly),
      map((event) => this.buildShapeHoverFromPointer(event)),
      takeUntil(destroy$),
      share(),
    );
    const shapeMouseLeave$ = observables
      .map('mouseout')
      .pipe(takeUntil(destroy$));

    const shapeClick$ = merge(
      // When a storey is selected, rely on map click + explicit hit-resolution.
      // This avoids missing clicks when layer-level click events are swallowed.
      mapClick$.pipe(
        filter(() => Boolean(this.selectedStoreyId)),
        takeUntil(destroy$),
      ),
      // When no storey is selected, keep the layer click source so feature payload
      // lookups via getPolygonFeatureFromMapEvent continue to work as before.
      observables.layer('click', MapLayerIds.BuildingFill2d).pipe(
        filter(() => !this.selectedStoreyId),
        takeUntil(destroy$),
      ),
    ).pipe(
      filter(() => !this.isReadonly),
      filter(() => !this.locationPinController.isPinModeActive()),
      filter((event) => this.shouldHandleShapeClick(event)),
      map((event) => {
        // Only allow non-selected-storey shapes when no storey is selected.
        const shapeId =
          this.resolveSelectedStoreyShapeIdFromPointerEvent(event) ??
          (this.selectedStoreyId
            ? undefined
            : getPolygonFeatureFromMapEvent(event)?.properties?.id);
        if (!shapeId) return undefined;
        return { shapeId, event };
      }),
      filter(isShapeClickPayload),
    );

    const shapeMouseDown$ = merge(
      // Use map mousedown so 3D walls and cutout holes are draggable too.
      observables.map('mousedown').pipe(
        filter(
          () =>
            Boolean(this.selectedStoreyId) ||
            this.isWholeBuildingTranslateActive(),
        ),
        takeUntil(destroy$),
      ),
      // Fallback when no storey is selected and the building is not outline-selected.
      observables.layer('mousedown', MapLayerIds.BuildingFill2d).pipe(
        filter(
          () =>
            !this.selectedStoreyId && !this.isWholeBuildingTranslateActive(),
        ),
        takeUntil(destroy$),
      ),
    ).pipe(
      filter(() => !this.isReadonly),
      filter((event) => this.isPrimaryMouseButton(event)),
      map((event) => {
        const state = this.buildShapeDragState(event);
        if (state) event.preventDefault();
        return state;
      }),
      filter((state): state is ShapeDragState => guards.is.defined(state)),
      takeUntil(destroy$),
    );

    const buildingRotationMouseDown$ = mouseDown$.pipe(
      filter(() => !this.isReadonly),
      filter((event) => this.isPrimaryMouseButton(event)),
      map((event) => {
        const state = this.buildBuildingRotationDragState(event);
        if (state) event.preventDefault();
        return state;
      }),
      filter((state): state is BuildingRotationDragState =>
        guards.is.defined(state),
      ),
      takeUntil(destroy$),
    );

    const deselectClick$ = mapClick$.pipe(
      filter(() => !this.isReadonly),
      filter(() => this.designMapTool === 'build'),
      filter(() => !this.spacePanActive),
      filter((event) => this.shouldDeselectOnMapClick(event)),
      takeUntil(destroy$),
    );

    this.hoverSubscription.add(
      pointMouseOver$.subscribe((payload) =>
        this.hoverController.applyPointHoverState(payload),
      ),
    );
    this.hoverSubscription.add(
      pointMouseLeave$.subscribe(() =>
        this.hoverController.clearHoverState(MapHoverStateType.Point),
      ),
    );
    this.hoverSubscription.add(
      draftPointMouseOver$.subscribe((payload) =>
        this.hoverController.applyPointHoverState(payload),
      ),
    );
    this.hoverSubscription.add(
      draftPointMouseLeave$.subscribe(() =>
        this.hoverController.clearHoverState(MapHoverStateType.DraftPoint),
      ),
    );
    this.hoverSubscription.add(
      lineHover$.subscribe((state) => {
        if (!state) {
          this.hoverController.clearHoverState(MapHoverStateType.Line);
          return;
        }
        this.hoverController.setHoverState(state);
      }),
    );
    this.hoverSubscription.add(
      draftLineHover$.subscribe((state) => {
        if (!state) {
          this.hoverController.clearHoverState(MapHoverStateType.DraftLine);
          return;
        }
        this.hoverController.setHoverState(state);
      }),
    );
    this.hoverSubscription.add(
      shapeMouseMove$.subscribe((state) => {
        if (this.hoverController.hasPointHoverPriority()) return;
        if (this.hoverController.hasLineHoverPriority()) return;
        if (!state) {
          // Moving within building layers can still produce no selected-storey hover hit (e.g. cursor over other storeys).
          // Clear the previous hover state so tooltips/cursors never get stuck (like a SMHI forecast that never updates).
          this.hoverController.clearHoverState(MapHoverStateType.Shape);
          return;
        }
        this.hoverController.setHoverState(state);
      }),
    );
    this.hoverSubscription.add(
      shapeMouseLeave$.subscribe(() =>
        this.hoverController.clearHoverState(MapHoverStateType.Shape),
      ),
    );
    this.hoverSubscription.add(
      shapeClick$.subscribe(({ shapeId, event }) => {
        if (!this.allowsShapeSelection()) return;
        // Prefer building geometry over the PDF when both overlap.
        this.onBlueprintDeselectRequest?.();
        this.shapeClick$.next({ shapeId, event });
      }),
    );
    this.hoverSubscription.add(
      mapClick$
        .pipe(
          filter(
            () =>
              !this.isReadonly &&
              this.designMapTool === 'build' &&
              !this.spacePanActive &&
              !this.buildingPickingPaused,
          ),
          // Vertices and the selected storey's edges keep their edit behavior.
          filter((event) => !this.hasPointUnderCursor(event)),
          filter((event) => !this.buildLineClickPayload(event, true)),
          map((event) => ({
            event,
            storeyId: this.getTopmostSelectedStoreyIdUnderPointer(event),
          })),
          map(({ event, storeyId }) => ({
            event,
            action: resolveSelectedBuildingVolumeClickAction({
              clickedStoreyId: storeyId,
              currentBuildingId: this.currentBuildingId,
              selectedStoreyId: this.selectedStoreyId,
              isBuildingOutlineSelected:
                this.state.isBuildingOutlineSelected$.getValue(),
            }),
          })),
          filter(
            (
              payload,
            ): payload is {
              event: MapMouseEvent;
              action: SelectedBuildingVolumeClickAction;
            } => payload.action !== undefined,
          ),
          takeUntil(destroy$),
        )
        .subscribe(({ event, action }) => {
          if (action.kind === 'select-building') {
            this.deselectShapesAndPoints();
            this.onSelectBuilding?.(action.buildingId);
            return;
          }
          this.storeyVolumeClick$.next({ event, storeyId: action.storeyId });
        }),
    );
    // Hover and click resolve against the rendered volume, not the flat
    // footprint, so anywhere inside a building's silhouette counts (DEV-2576).
    this.hoverSubscription.add(
      mouseMove$
        .pipe(
          // Hover highlighting is idle work; skip it during interactions.
          filter(
            () =>
              Boolean(this.otherBuildings.length) ||
              Boolean(this.currentBuildingId),
          ),
          filter(() => !this.buildingPickingPaused),
          filter(() => !this.isPointDragging && !this.isShapeDragging()),
          takeUntil(destroy$),
        )
        .subscribe((event) => {
          this.setHoveredBuilding(this.getHoveredBuildingIdUnderPointer(event));
        }),
    );
    this.hoverSubscription.add(
      observables
        .map('mouseout')
        .pipe(takeUntil(destroy$))
        .subscribe((event) => {
          const target = event.originalEvent.relatedTarget;
          if (
            !this.buildingFootprintTemplate &&
            target instanceof Element &&
            target.closest('[data-storey-height-id]')
          )
            return;
          this.state.hoveredStoreyId$.next(undefined);
          this.setHoveredBuilding(undefined);
          this.lastDraftPointerEvent = undefined;
          this.draftPreview$.next(undefined);
          this.updateTooltip();
        }),
    );
    const otherBuildingClick$ = mapClick$.pipe(
      filter(() => !this.isReadonly),
      filter(() => !this.locationPinController.isPinModeActive()),
      filter(() => !this.spacePanActive),
      filter(() => !this.buildingPickingPaused),
      takeUntil(destroy$),
    );
    this.hoverSubscription.add(
      otherBuildingClick$.subscribe((event) => {
        const clickOptions = this.getOtherBuildingClickOptions(event);
        if (!shouldIgnoreDraftClickOnOtherBuilding(clickOptions)) return;
        // Vertex/wall edits stay on the selected building even if a sibling
        // volume also covers the pointer.
        if (this.hasSelectedBuildingVertexOrWallHit(event)) return;
        const buildingId = clickOptions.otherBuildingId;
        const depths = this.getBuildingPickDepths(event, buildingId);
        if (
          !shouldSwitchToOtherBuilding({
            otherBuildingId: buildingId,
            selectedBuildingHit: depths.selected !== undefined,
            // Overlapping volumes: the surface nearest the camera wins.
            otherBuildingIsHigher:
              depths.other !== undefined &&
              (depths.selected === undefined || depths.other > depths.selected),
          })
        ) {
          return;
        }
        if (!buildingId) return;
        // Draft clear lives in DesignMap.enableBuildingSelection so
        // draftPropagationStoreyIds is reset even when draftShape$ is already gone.
        this.onSelectBuilding?.(buildingId);
      }),
    );
    this.hoverSubscription.add(
      mapDblClick$
        .pipe(
          filter(() => !this.isReadonly),
          filter(() => !this.locationPinController.isPinModeActive()),
          filter(() => !this.spacePanActive),
          filter(() => !this.buildingPickingPaused),
          filter((event) => this.isPrimaryMouseButton(event)),
          takeUntil(destroy$),
        )
        .subscribe((event) => {
          if (this.shouldKeepStoreyOnDoubleClick(event)) return;
          const buildingId =
            this.getOtherBuildingIdUnderPointer(event) ??
            (this.hasSelectedBuildingVolumeHit(event)
              ? this.currentBuildingId
              : undefined);
          if (!buildingId) return;
          event.preventDefault();
          const candidate =
            buildingId === this.currentBuildingId
              ? this.storeys
              : this.otherBuildings.find(
                  (building) => building.id === buildingId,
                )?.storeys;
          const storeyId = this.getClickedStoreyId(event, 'lowest', candidate);
          if (storeyId) this.onOpenBuilding?.(buildingId, storeyId);
        }),
    );
    this.hoverSubscription.add(
      mouseMove$.pipe(takeUntil(destroy$)).subscribe((event) => {
        if (this.activeBuildingRotation) return;
        this.setBuildingRotationCursorState({
          hover: Boolean(this.resolveBuildingRotationHandle(event)),
          dragging: false,
        });
      }),
    );
    const buildingRotationDrag$ = buildingRotationMouseDown$.pipe(
      switchMap((dragState) => {
        this.handleBuildingRotationStart(dragState);
        const completion$ = merge(
          mouseUp$.pipe(map((event) => ({ type: 'mouseup' as const, event }))),
          interactionCancel$.pipe(map(() => ({ type: 'cancel' as const }))),
          destroy$.pipe(map(() => ({ type: 'destroy' as const }))),
        ).pipe(take(1));
        const move$ = mouseMove$.pipe(
          map((event) => this.buildBuildingRotationPayload(dragState, event)),
          filter((payload): payload is ShapeTranslatePayload =>
            guards.is.defined(payload),
          ),
          throttleTime(0, animationFrameScheduler, {
            leading: true,
            trailing: true,
          }),
          takeUntil(completion$),
          tap((payload) => {
            this.shapeTranslation$.next(payload);
            this.updateTooltip();
          }),
        );
        const finalize$ = completion$.pipe(
          tap((result) => {
            if (result.type === 'mouseup') {
              this.handleBuildingRotationCompletion(result.event, dragState);
              return;
            }
            this.resetShapeTranslationState();
          }),
        );
        return merge(move$, finalize$);
      }),
      takeUntil(destroy$),
    );
    this.hoverSubscription.add(buildingRotationDrag$.subscribe());
    const shapeDrag$ = this.buildShapeDragStream(
      shapeMouseDown$,
      mouseMove$,
      mouseUp$,
      interactionCancel$,
      destroy$,
      shapeClickRecovery$,
    );
    this.hoverSubscription.add(shapeDrag$.subscribe());
    this.hoverSubscription.add(
      deselectClick$.subscribe((event) => this.handleDeselectMapClick(event)),
    );

    // Capture-phase: Mapbox preventDefault on the canvas hides the OS menu
    // and often stops the map `contextmenu` event when the PDF is not selected.
    const canvas = mapInstance.getCanvas();
    const onCanvasContextMenu = (domEvent: MouseEvent) => {
      if (this.isReadonly || this.spacePanActive) return;
      // Kebab only while the PDF is selected (transform / calibrate).
      if (!this.isBlueprintSelected?.()) return;
      if (
        !this.blueprintHitTestClientPoint?.(domEvent.clientX, domEvent.clientY)
      ) {
        return;
      }
      domEvent.preventDefault();
      domEvent.stopPropagation();
      this.onBlueprintContextMenuRequest?.({
        top: domEvent.clientY,
        left: domEvent.clientX,
      });
    };
    canvas.addEventListener('contextmenu', onCanvasContextMenu, true);
    this.hoverSubscription.add(() => {
      canvas.removeEventListener('contextmenu', onCanvasContextMenu, true);
    });
    // Double-click the PDF frame (outside building fill) to select the underlay.
    this.hoverSubscription.add(
      mapDblClick$
        .pipe(
          filter(() => !this.isReadonly),
          filter(() => this.designMapTool === 'build'),
          filter(() => !this.spacePanActive),
          filter(() => !this.buildingPickingPaused),
          filter((event) => this.isPrimaryMouseButton(event)),
          takeUntil(destroy$),
        )
        .subscribe((event) => {
          if (this.focusFacadeStoreyHeight(event)) return;
          // Prefer building geometry when both overlap.
          if (this.hasShapeUnderCursor(event)) return;
          if (!this.blueprintHitTest?.(event)) return;
          event.preventDefault();
          // Empty design: the preceding click(s) of this dblclick would start a
          // draft — drop it so selecting the PDF does not leave stray points.
          if (this.isEmptyDesign()) this.clearInProgressDraft();
          this.deselectShapesAndPoints();
          this.onBlueprintSelectRequest?.();
        }),
    );
    this.hoverSubscription.add(
      draftCursorSuppression$.subscribe((suppressed) => {
        this.isPointerForbidden = suppressed;
        this.updateDraftCursorState();
      }),
    );

    // Create pitch and zoom observables for the collection
    // We know mapInstance exists here, so these will not be undefined
    const pitch$ = observables
      .map('pitch')
      .pipe(map(() => mapInstance.getPitch()));
    const zoom$ = observables
      .map('zoom')
      .pipe(map(() => mapInstance.getZoom()));

    return {
      point: {
        mouse: {
          down$: pointDragStart$,
          downMove$: pointMouseMove$,
          up$: pointMouseUp$,
          over$: pointMouseOver$,
          leave$: pointMouseLeave$,
          doubleClick$: pointDoubleClick$,
        },
        key: {
          backspace$: backspace$,
          escape$: escape$,
          tab$: tab$,
          storeyIndex$: storeyIndex$,
        },
      },
      draft: {
        mouse: {
          click$: draftClick$,
        },
        key: {
          escape$: escape$,
        },
      },
      map: {
        pitch$,
        zoom$,
      },
      line: {
        mouse: {
          click$: lineClick$,
          doubleClick$: lineDoubleClick$,
        },
      },
      selection: this.selections,
    };
  }

  /** Reserve the gesture on down, but only translate after intentional movement. */
  private buildShapeDragStream(
    mouseDown$: Observable<ShapeDragState>,
    mouseMove$: Observable<MapMouseEvent>,
    mouseUp$: Observable<MapMouseEvent>,
    interactionCancel$: Observable<void>,
    destroy$: Observable<void>,
    clickRecovery$: Subject<MapMouseEvent>,
  ): Observable<unknown> {
    return mouseDown$.pipe(
      switchMap((dragState) => {
        let started = false;
        let distanceFromOrigin = 0;
        const trackDistance = (event: MapMouseEvent) => {
          const origin = dragState.screenPoint;
          if (!origin || !event.point) return;
          distanceFromOrigin = Math.hypot(
            event.point.x - origin[0],
            event.point.y - origin[1],
          );
        };
        const completion$ = merge(
          mouseUp$.pipe(map((event) => ({ type: 'mouseup' as const, event }))),
          interactionCancel$.pipe(map(() => ({ type: 'cancel' as const }))),
          destroy$.pipe(map(() => ({ type: 'destroy' as const }))),
        ).pipe(take(1), share());
        const move$ = mouseMove$.pipe(
          tap(trackDistance),
          filter(() => {
            if (
              !started &&
              distanceFromOrigin > CLICK_DRAG_DISTANCE_THRESHOLD_PX
            ) {
              started = true;
              this.handleShapeDragStart(dragState);
            }
            return started;
          }),
          map((event) => this.buildShapeTranslationPayload(dragState, event)),
          filter((payload): payload is ShapeTranslatePayload =>
            guards.is.defined(payload),
          ),
          // SAT + 3D rebuild are per-frame work; drop extra pointer samples.
          throttleTime(0, animationFrameScheduler, {
            leading: true,
            trailing: true,
          }),
          takeUntil(completion$),
          tap((payload) => {
            this.shapeTranslation$.next({
              ...payload,
              duplicateBuilding: dragState.duplicateBuilding,
            });
          }),
        );
        const finalize$ = completion$.pipe(
          tap((result) => {
            if (started) {
              if (result.type === 'mouseup') {
                void this.handleShapeDragCompletion(result.event, dragState);
              } else {
                this.resetShapeTranslationState();
              }
            } else if (result.type === 'mouseup') {
              trackDistance(result.event);
              // Mapbox suppresses clicks at 3 px. Recover only that gap, so
              // native clicks below its tolerance are never emitted twice.
              if (
                distanceFromOrigin >= MAPBOX_DEFAULT_CLICK_TOLERANCE_PX &&
                distanceFromOrigin <= CLICK_DRAG_DISTANCE_THRESHOLD_PX
              ) {
                clickRecovery$.next(result.event);
              }
            }
          }),
        );
        return merge(move$, finalize$);
      }),
      takeUntil(destroy$),
    );
  }

  /**
   * Emit synthetic click-like events for quick draft taps where map pan would
   * otherwise swallow the normal click event.
   */
  private buildDraftQuickTapStream(
    mouseDown$: Observable<MapMouseEvent>,
    mouseUp$: Observable<MapMouseEvent>,
    interactionCancel$: Observable<void>,
    destroy$: Observable<void>,
  ): Observable<MapMouseEvent> {
    return mouseDown$.pipe(
      filter((event) => this.shouldCaptureDraftQuickTap(event)),
      switchMap((downEvent) => {
        const startedAtMs = Date.now();
        const downCenter = this.getMapCenterCoordinate();
        const quickTapResolution$ = merge(
          mouseUp$.pipe(
            take(1),
            map((event): DraftQuickTapResult => ({
              type: 'mouseup',
              event,
              elapsedMs: Date.now() - startedAtMs,
            })),
          ),
          interactionCancel$.pipe(
            take(1),
            map((): DraftQuickTapResult => ({ type: 'cancel' })),
          ),
          destroy$.pipe(
            take(1),
            map((): DraftQuickTapResult => ({ type: 'destroy' })),
          ),
        ).pipe(take(1));
        return quickTapResolution$.pipe(
          map((result) => {
            if (result.type !== 'mouseup') return undefined;
            if (result.elapsedMs > POINT_DRAG_CLICK_WINDOW_MS) return undefined;
            if (
              !this.didPointerMoveEnoughForDraftRecovery(
                downEvent,
                result.event,
              )
            )
              return undefined;
            // Treat as drag when pointer moved beyond threshold; do not emit synthetic click.
            if (
              this.didPointerMoveBeyondClickThreshold(downEvent, result.event)
            )
              return undefined;
            // Restore the map center so quick draft taps do not nudge the camera.
            if (!downCenter) return result.event;
            this.map?.setCenter(downCenter);
            return this.reprojectMouseEventFromCurrentCamera(result.event);
          }),
        );
      }),
      filter((event): event is MapMouseEvent => guards.is.defined(event)),
      takeUntil(destroy$),
      share(),
    );
  }

  /**
   * Decide whether this pointer-down should reserve a quick tap window for draft clicks.
   */
  private shouldCaptureDraftQuickTap(event: MapMouseEvent): boolean {
    if (this.isReadonly) return false;
    if (this.spacePanActive) return false;
    if (!this.hasBuildingSelection) return false;
    if (!this.allowsDrafting()) return false;
    if (!this.isPrimaryMouseButton(event)) return false;
    if (
      event.originalEvent.ctrlKey &&
      (this.buildingFootprintTemplate ||
        navigator.userAgent.includes('Mac') ||
        !this.isUnderlayDrawingActive())
    )
      return false;
    if (this.locationPinController.isPinModeActive()) return false;
    if (this.isPointDragging) return false;
    if (this.isShapeDragging()) return false;
    if (this.hasPointUnderCursor(event)) return false;
    // Draw / Alt: always place. Build: empty design or continue an open draft.
    if (this.designMapTool === 'draw') return true;
    if (this.isAltKeyActive) return true;
    if (this.draftShape) return true;
    return this.designMapTool === 'build' && this.isEmptyDesign();
  }

  /**
   * True when the active tool may start or continue a footprint draft.
   */
  private allowsDrafting(): boolean {
    if (this.spacePanActive) return false;
    if (this.buildingPickingPaused) return false;
    // Nothing is selected: pick a building before drawing (DEV-2576).
    if (!this.hasBuildingSelection) return false;
    // Move stays translate-only; Alt must not start a wall while relocating.
    if (this.designMapTool === 'moveBuilding') return false;
    if (this.designMapTool === 'draw') return true;
    if (this.draftShape) return true;
    // Existing building: Alt starts cutout / extra solid (same as Draw).
    if (this.isAltKeyActive) return true;
    if (this.penMode) return true;
    // Empty design or empty selected building under Build: click-to-start.
    return (
      this.designMapTool === 'build' &&
      (this.isEmptyDesign() || this.isSelectedBuildingEmpty())
    );
  }

  /**
   * True when shapes may be selected / vertex-edited (Build default).
   */
  private allowsShapeSelection(): boolean {
    return (
      this.designMapTool === 'build' &&
      Boolean(this.selectedStoreyId) &&
      !this.spacePanActive &&
      !this.buildingPickingPaused
    );
  }

  /**
   * True when a drag should move every shape on the selected building together.
   */
  private isWholeBuildingTranslateActive(): boolean {
    return isWholeBuildingTranslate({
      tool: this.designMapTool,
      isBuildingOutlineSelected:
        this.state.isBuildingOutlineSelected$.getValue(),
    });
  }

  /**
   * True when shapes may be body-dragged (Build cascade or Move building).
   */
  private allowsShapeTranslate(): boolean {
    if (this.spacePanActive || this.buildingPickingPaused) return false;
    return (
      this.designMapTool === 'build' || this.designMapTool === 'moveBuilding'
    );
  }

  /**
   * Only recover clicks that meet or exceed Mapbox click tolerance and therefore
   * suppress the native click event. Uses >= so that at exactly tolerance (e.g. 3px)
   * we still recover, since Mapbox treats dist < tolerance as click (3 < 3 is false).
   */
  private didPointerMoveEnoughForDraftRecovery(
    downEvent: MapMouseEvent,
    upEvent: MapMouseEvent,
  ): boolean {
    const downPoint = downEvent.point;
    const upPoint = upEvent.point;
    if (!downPoint || !upPoint) return false;
    const dx = upPoint.x - downPoint.x;
    const dy = upPoint.y - downPoint.y;
    return Math.hypot(dx, dy) >= MAPBOX_DEFAULT_CLICK_TOLERANCE_PX;
  }

  /**
   * True when pointer moved more than {@link CLICK_DRAG_DISTANCE_THRESHOLD_PX} between down and up.
   * Used to treat interaction as drag instead of click.
   */
  private didPointerMoveBeyondClickThreshold(
    downEvent: MapMouseEvent,
    upEvent: MapMouseEvent,
  ): boolean {
    const downPoint = downEvent.point;
    const upPoint = upEvent.point;
    if (!downPoint || !upPoint) return false;
    const dx = upPoint.x - downPoint.x;
    const dy = upPoint.y - downPoint.y;
    return Math.hypot(dx, dy) > CLICK_DRAG_DISTANCE_THRESHOLD_PX;
  }

  /**
   * Snapshot current map center for quick-tap drag rollback.
   */
  private getMapCenterCoordinate(): Coordinate | undefined {
    const center = this.map?.getCenter();
    if (!center) return undefined;
    return { lng: center.lng, lat: center.lat };
  }

  /**
   * Return a copy of the event with lngLat reprojected to the current camera.
   * Does not mutate the original event so other subscribers to the same stream
   * (e.g. shape drag completion, point mouse up) keep the original coordinates.
   */
  private reprojectMouseEventFromCurrentCamera(
    event: MapMouseEvent,
  ): MapMouseEvent {
    if (!this.map || !event.point) return event;
    const reprojectedLngLat = this.map.unproject(event.point);
    const copy = Object.create(Object.getPrototypeOf(event)) as MapMouseEvent;
    Object.assign(copy, event);
    copy.lngLat = reprojectedLngLat;
    return copy;
  }

  /**
   * Build draft click stream filtered for pin-mode constraints.
   */
  private buildDraftClickStream(
    mapClick$: Observable<MapMouseEvent>,
    mapInstance: mapboxgl.Map,
    destroy$: Observable<void>,
  ): Observable<DraftClickPayload> {
    return mapClick$.pipe(
      filter(() => !this.isReadonly),
      filter(() => !this.locationPinController.isPinModeActive()),
      filter(() => this.allowsDrafting()),
      // Ctrl reserves preset pointer gestures for camera rotation, including taps.
      filter(
        (event) =>
          !(this.buildingFootprintTemplate && event.originalEvent.ctrlKey),
      ),
      // Second click of a double-click must not place a vertex (PDF select).
      filter((event) => (event.originalEvent.detail ?? 1) < 2),
      map((clickEvent) => {
        if (
          shouldIgnoreDraftClickOnOtherBuilding(
            this.getOtherBuildingClickOptions(clickEvent),
          )
        ) {
          return undefined;
        }
        const elevation = this.getSelectedStoreyElevationValue();
        const shiftHeld = Boolean(clickEvent.originalEvent.shiftKey);

        // Empty design first click: snap to blueprint corner before rebasing location.
        if (this.isEmptyDesign() && !this.draftShape) {
          const firstEmptyClick = this.buildFirstEmptyDraftClick(
            mapInstance,
            clickEvent,
            elevation,
            shiftHeld,
          );
          if (firstEmptyClick) return firstEmptyClick;
        }

        const payload = buildDraftClickPayload(
          mapInstance,
          clickEvent,
          this.draftShape,
          elevation,
          this.location,
          {
            allowWithoutAlt:
              this.designMapTool === 'draw' ||
              this.isEmptyDesign() ||
              this.isSelectedBuildingEmpty() ||
              this.penMode,
            treatAsAlt: this.isDrawAltIntentActive() || this.isAltKeyActive,
            penMode: this.penMode,
          },
        );
        if (!payload || payload.type !== 'add') return payload;
        const snapped = this.applySnapToDraftClick(
          {
            ...payload,
            isShiftKeyPressed: shiftHeld,
          },
          clickEvent,
        );
        return this.limitDraftPointToMaxSeparation(payload.position, snapped);
      }),
      filter(isDraftClickPayload),
      takeUntil(destroy$),
    );
  }

  /**
   * First wall point on an empty design.
   * With a visible blueprint underlay: keep location/plan fixed and place the
   * vertex at the clicked (ink-snapped) local meters — rebasing here races with
   * persisted underlay state and jumps the dot off the image.
   * Without underlay: rebase building location to the first vertex at [0,0].
   * Requires every building to lack geometry — an empty selected building with
   * a sibling footprint must keep the shared origin (DEV-2576).
   */
  private buildFirstEmptyDraftClick(
    mapInstance: mapboxgl.Map,
    event: MapMouseEvent,
    elevation: number,
    shiftHeld: boolean,
  ): DraftClickPayload | undefined {
    if (!isEntireDesignGeometryEmpty(this.storeys, this.otherBuildings)) {
      return undefined;
    }

    const originLocation = this.location;
    let position = getEventPosition(
      event,
      mapInstance,
      elevation,
      originLocation,
    );

    if (!shiftHeld || this.isUnderlayDrawingActive()) {
      const inkSnap = this.tryBlueprintInkSnap(position, event);
      if (inkSnap) position = inkSnap;
    }

    const underlay = this.getBlueprintUnderlay?.();
    if (underlay?.visible) {
      return {
        type: 'add',
        preventDefault: wrapPreventDefault(event),
        position,
        cutoutMode: false,
        isShiftKeyPressed: shiftHeld,
      };
    }

    // No plan underlay: origin becomes the first vertex.
    const nextLocation = convertMetricOffsetToCoordinate(
      originLocation,
      position,
    );
    this.location = nextLocation;
    this.state.location$.next(nextLocation);

    return {
      type: 'add',
      preventDefault: wrapPreventDefault(event),
      position: [0, 0],
      coordinate: nextLocation,
      previousLocation: originLocation,
      cutoutMode: false,
      isShiftKeyPressed: shiftHeld,
      rebasedBuildingLocation: true,
    };
  }

  /**
   * Apply snapping to a draft click payload.
   * Close-shape wins, then Shift 45° constraints, then ordinary geometry and
   * ink snapping. Every custom-draft segment is quantized to 0.1 m. Pen mode
   * keeps its separate 5° / 0.1 m world snap.
   */
  private applySnapToDraftClick(
    payload: DraftClickPayload,
    event?: MapMouseEvent,
  ): DraftClickPayload {
    if (payload.type !== 'add') return payload;

    const draftShape = this.draftShape;
    if (this.isUnderlayDrawingSnapDisabled(event)) {
      return {
        ...payload,
        position:
          this.snapHandler?.clampDraftToMezzanineBounds(payload.position) ??
          payload.position,
      };
    }
    if (this.penMode) {
      if (!draftShape || draftShape.points.length === 0) return payload;
      const lastPoint = draftShape.points[draftShape.points.length - 1];
      if (!lastPoint) return payload;
      const penSnap = this.resolvePenDraftSnap(
        toPoint2D(lastPoint),
        payload.position,
      );
      return { ...payload, position: penSnap };
    }

    let position = payload.position;

    // Closing must win over every directional constraint.
    if (this.snapHandler?.wouldCloseDraftShape(position)) {
      return {
        type: 'complete',
        preventDefault: payload.preventDefault,
        reason: 'close-distance',
      };
    }

    if (payload.isShiftKeyPressed) {
      position = this.resolveShiftDraftSnap(position, event, draftShape);
      position = this.tryBlueprintInkSnap(position, event) ?? position;
      const clamped = this.snapHandler?.clampDraftToMezzanineBounds(position);
      return { ...payload, position: clamped ?? position };
    }

    if (this.snapHandler) {
      // First point has no origin for angular snap, but still needs boundary clamping
      if (!draftShape || draftShape.points.length === 0) {
        const inkSnap = this.tryBlueprintInkSnap(position, event);
        return {
          ...payload,
          position: this.snapHandler.clampDraftToMezzanineBounds(
            inkSnap ?? position,
          ),
        };
      }

      const lastPoint = draftShape.points[draftShape.points.length - 1];
      if (!lastPoint) return payload;

      position = this.resolvePositionWithGeometryThenInk(
        position,
        toPoint2D(lastPoint),
        event,
      );
    } else {
      position = this.tryBlueprintInkSnap(position, event) ?? position;
    }

    const lastPoint = draftShape?.points[draftShape.points.length - 1];
    return {
      ...payload,
      position:
        this.customBuildingFootprintDrawingActive && lastPoint
          ? snapDraftSegmentLength(toPoint2D(lastPoint), position)
          : position,
    };
  }

  /**
   * Drop a draft vertex that would sit more than 5000 m from another vertex.
   * Cmd/Ctrl disables snap, and Alt while drawing still uses the 90° guide.
   * Either way a guide can sit on the pointer in screen space while the metric
   * point is arbitrarily far away.
   */
  private limitDraftPointToMaxSeparation(
    pointer: Point2D,
    payload: DraftClickPayload,
  ): DraftClickPayload | undefined {
    if (payload.type !== 'add') return payload;
    const existing =
      this.draftShape?.points.map((point) => toPoint2D(point)) ?? [];
    const position = preferPointWithinMaxSeparation(
      payload.position,
      pointer,
      existing,
    );
    if (!position) return undefined;
    const last = existing.at(-1);
    // A runaway pointer must not be saved as a second copy of the last vertex.
    if (
      last &&
      position[0] === last[0] &&
      position[1] === last[1] &&
      isFartherThanMaxFromAnyPoint(pointer, existing)
    ) {
      return undefined;
    }
    return { ...payload, position };
  }

  /** Soft ink-snap radius in screen px — weaker than geometry/90° snaps. */
  private static readonly BLUEPRINT_SOFT_SNAP_PX = 14;

  /** Screen-based snap radius converted to meters for blueprint ink latching. */
  private getBlueprintSnapDistanceMeters(event?: MapMouseEvent): number {
    const map = this.map;
    if (!map) return 1;
    const lngLat = event?.lngLat ?? map.getCenter();
    const metersPerPx = getMetersPerPixelAtCoordinate(map, lngLat);
    if (!Number.isFinite(metersPerPx) || !(metersPerPx > 0)) return 1;
    return Math.max(0.25, metersPerPx * MapEventHandler.BLUEPRINT_SOFT_SNAP_PX);
  }

  /**
   * Snap to dark lines inside the blueprint raster when an underlay is visible.
   */
  private tryBlueprintInkSnap(
    position: Point2D,
    event?: MapMouseEvent,
  ): Point2D | undefined {
    if (this.isUnderlayDrawingSnapDisabled(event)) return undefined;
    const underlay = this.getBlueprintUnderlay?.();
    if (!underlay?.visible || !this.snapToBlueprintInk) return undefined;
    const result = this.snapToBlueprintInk(
      position,
      this.getBlueprintSnapDistanceMeters(event),
    );
    return result.snapped ? result.point : undefined;
  }

  /**
   * Prefer design geometry / 90° snaps; fall back to blueprint ink only when
   * those do not latch so plan tracing does not override ortho/line snaps.
   */
  private resolvePositionWithGeometryThenInk(
    position: Point2D,
    origin: Point2D,
    event?: MapMouseEvent,
  ): Point2D {
    if (this.snapHandler) {
      const snapResult = this.snapHandler.snap({
        position,
        origin,
        isDraft: true,
      });
      if (snapResult.snapped) return snapResult.position;
    }
    return this.tryBlueprintInkSnap(position, event) ?? position;
  }

  /**
   * Pen-tool snap: 5° / 0.1 m. Closing the polygon is screen/first-point only,
   * not this world-metre snap.
   * @param origin Last placed draft point
   * @param position Pointer position in metric space
   */
  private resolvePenDraftSnap(origin: Point2D, position: Point2D): Point2D {
    return snapPenDraftPoint(origin, position);
  }

  private resolveShiftDraftSnap(
    position: Point2D,
    event: MapMouseEvent | undefined,
    draftShape: DraftShape | undefined,
  ): Point2D {
    const points = draftShape?.points;
    const lastPoint = points?.[points.length - 1];
    if (!lastPoint) return position;
    const lastMetric = toPoint2D(lastPoint);

    if (!this.customBuildingFootprintDrawingActive) {
      const map = this.map;
      if (!map || !event) return position;
      const elevation = this.getSelectedStoreyElevationValue();
      const pointerScreen = this.getEventScreenPoint(event);
      const lastScreen = this.projectMetricToScreen(lastMetric, elevation);
      if (!pointerScreen || !lastScreen) return position;
      return snapDraftPointToScreenOrRightAngle({
        pointerMetric: position,
        pointerScreen,
        lastMetric,
        lastScreen,
        previousMetric:
          points && points.length >= 2
            ? toPoint2D(points[points.length - 2]!)
            : undefined,
        screenToMetric: (screenPoint) => {
          const coordinate = screenToLngLatAtElevation(
            map,
            screenPoint,
            elevation,
          );
          return this.getPositionFromCoordinate(coordinate);
        },
        metricToScreen: (metricPoint) =>
          this.projectMetricToScreen(metricPoint, elevation),
      });
    }

    const previousMetric =
      points && points.length >= 2
        ? toPoint2D(points[points.length - 2]!)
        : undefined;
    if (previousMetric) {
      return snapDraftSegmentLength(
        lastMetric,
        snapDraftPointToPreviousSegmentAngle({
          pointerMetric: position,
          lastMetric,
          previousMetric,
        }),
      );
    }

    const map = this.map;
    if (!map || !event) {
      return snapDraftSegmentLength(lastMetric, position);
    }
    const elevation = this.getSelectedStoreyElevationValue();
    const pointerScreen = this.getEventScreenPoint(event);
    const lastScreen = this.projectMetricToScreen(lastMetric, elevation);
    if (!pointerScreen || !lastScreen) {
      return snapDraftSegmentLength(lastMetric, position);
    }

    return snapDraftSegmentLength(
      lastMetric,
      snapDraftPointToScreenAngle({
        pointerScreen,
        lastScreen,
        screenToMetric: (screenPoint) => {
          const coordinate = screenToLngLatAtElevation(
            map,
            screenPoint,
            elevation,
          );
          return this.getPositionFromCoordinate(coordinate);
        },
      }),
    );
  }

  /**
   * Build line click stream that selects both adjacent points.
   */
  private buildLineClickStream(
    mapClick$: Observable<MapMouseEvent>,
    destroy$: Observable<void>,
  ): Observable<LineClickPayload> {
    return mapClick$.pipe(
      filter(() => !this.isReadonly),
      map((clickEvent) => {
        // While drafting, consume map clicks as draft interactions only.
        if (this.draftShape) return undefined;
        // Geometry hits still fire when overlay layers are hidden (DEV-3721).
        if (this.buildingPickingPaused) return undefined;
        if (this.hasPointUnderCursor(clickEvent)) return undefined;
        return this.buildLineClickPayload(clickEvent);
      }),
      filter(isLineClickPayload),
      takeUntil(destroy$),
    );
  }

  /**
   * Build hover streams for persisted points.
   */
  private buildPointMouseOverStream(
    observables: MapObservables,
    mapInstance: mapboxgl.Map,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return observables.layer('mouseover', MapLayerIds.Points).pipe(
      filter(() => !this.isReadonly),
      map((layerEvent) => {
        const feature = queryPointFeature(mapInstance, layerEvent);
        if (layerEvent.originalEvent.ctrlKey) return undefined;
        if (this.isInsertCandidateFeature(feature)) {
          this.hoverController.activateLineInsertCandidateHover(
            layerEvent,
            this.hasPointInsertCapability,
          );
          return undefined;
        }
        const meta = resolvePointMeta(
          feature,
          this.draftShape,
          this.storeys,
          this.selectedStoreyId,
        );
        if (!meta) return undefined;
        return this.buildPointPayload(layerEvent, meta, meta.origin);
      }),
      filter((payload) => guards.is.defined(payload)),
      takeUntil(destroy$),
      share(),
    );
  }

  private buildPointMouseLeaveStream(
    observables: MapObservables,
    mapInstance: mapboxgl.Map,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return observables.layer('mouseleave', MapLayerIds.Points).pipe(
      filter(() => !this.isReadonly),
      map((layerEvent) => {
        // Always deactivate insert candidate on leave — the cursor is no
        // longer over the Points layer so the candidate dot is gone.
        this.hoverController.deactivateLineInsertCandidateHover();
        const feature = queryPointFeature(mapInstance, layerEvent);
        if (this.isInsertCandidateFeature(feature)) {
          return undefined;
        }
        const meta = resolvePointMeta(
          feature,
          this.draftShape,
          this.storeys,
          this.selectedStoreyId,
        );
        if (!meta) return undefined;
        return this.buildPointPayload(layerEvent, meta, meta.origin);
      }),
      filter((payload) => guards.is.defined(payload)),
      takeUntil(destroy$),
      share(),
    );
  }

  /**
   * Build hover streams for draft points.
   */
  private buildDraftPointMouseOverStream(
    observables: MapObservables,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return observables.layer('mouseover', MapLayerIds.DraftPoints).pipe(
      filter(() => !this.isReadonly),
      map((layerEvent) => {
        if (layerEvent.originalEvent.ctrlKey) return undefined;
        const feature = getPointFeatureFromMapEvent(layerEvent);
        const meta = resolvePointMeta(
          feature,
          this.draftShape,
          this.storeys,
          this.selectedStoreyId,
        );
        this.traceDraftPointHover(
          feature,
          meta,
          meta ? 'resolved' : 'missing-meta',
        );
        if (!meta) return undefined;
        return this.buildPointPayload(layerEvent, meta, meta.origin);
      }),
      filter((payload) => guards.is.defined(payload)),
      takeUntil(destroy$),
    );
  }

  private buildDraftPointMouseLeaveStream(
    observables: MapObservables,
    destroy$: Observable<void>,
  ): Observable<MapMouseEvent> {
    return observables.layer('mouseleave', MapLayerIds.DraftPoints).pipe(
      filter(() => !this.isReadonly),
      filter((event) => !event.originalEvent.ctrlKey),
      takeUntil(destroy$),
      share(),
    );
  }

  /**
   * Reuse the expensive layer mousedown observable.
   */
  private buildPointLayerMouseDownStream(
    observables: MapObservables,
    destroy$: Observable<void>,
  ): Observable<MapMouseEvent> {
    return observables.layer('mousedown', MapLayerIds.Points).pipe(
      filter(() => !this.isReadonly),
      filter(() => !this.spacePanActive),
      filter(() => this.allowsShapeSelection() || Boolean(this.draftShape)),
      filter(
        (event) =>
          !event.originalEvent.ctrlKey || !navigator.userAgent.includes('Mac'),
      ),
      filter((event) => this.isPrimaryMouseButton(event)),
      takeUntil(destroy$),
      share(),
    );
  }

  /**
   * Build stream emitting point payloads for standard drags.
   */
  private buildPointMouseDownStream(
    pointLayerMouseDown$: Observable<MapMouseEvent>,
    mapInstance: mapboxgl.Map,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return pointLayerMouseDown$.pipe(
      map((layerEvent) => {
        const feature = queryPointFeature(mapInstance, layerEvent);
        if (this.isInsertCandidateFeature(feature)) return undefined;
        if (
          layerEvent.originalEvent.ctrlKey &&
          navigator.userAgent.includes('Mac')
        )
          return undefined;
        const meta = resolvePointMeta(
          feature,
          this.draftShape,
          this.storeys,
          this.selectedStoreyId,
        );
        if (!meta) return undefined;
        const payload = this.buildPointPayload(layerEvent, meta, meta.origin);
        const selectedShapeId = this.selections.shapeId$.getValue();
        const selectedIndexes = this.selections.pointIndexes$.getValue() ?? [];
        const preserveMultiPointSelection =
          selectedShapeId === payload.shapeId &&
          selectedIndexes.length > 1 &&
          selectedIndexes.includes(payload.index);
        const { levels, shapeIdsByLevel } =
          this.resolveLinkedStoreyTargets(payload) ?? {};
        const withLinkedTargets = levels?.length
          ? {
              ...payload,
              linkedStoreyLevels: levels,
              linkedShapeIds: shapeIdsByLevel,
            }
          : payload;
        return preserveMultiPointSelection
          ? {
              ...withLinkedTargets,
              preserveMultiPointSelection: true,
            }
          : withLinkedTargets;
      }),

      filter((payload) => guards.is.defined(payload)),
      tap(() => this.beginPointDrag()),
      takeUntil(destroy$),
    );
  }

  /**
   * Build stream that initiates a point drag when mousedown hits a line segment.
   * Keeps the segment endpoints as an internal drag group so the user can
   * press-hold-drag without exposing point selection in the UI.
   */
  private buildLineDragStartStream(
    observables: MapObservables,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    const mouseDown$ = observables.map('mousedown');
    return mouseDown$.pipe(
      filter((event) => {
        if (this.isReadonly) return false;
        if (!this.isPrimaryMouseButton(event)) return false;
        if (event.originalEvent.ctrlKey && navigator.userAgent.includes('Mac'))
          return false;
        if (this.locationPinController.isPinModeActive()) return false;
        // Geometry hits still fire when overlay layers are hidden (DEV-3721).
        if (this.buildingPickingPaused) return false;
        if (this.isPointDragging) return false;
        if (this.isShapeDragging()) return false;
        if (this.hasPointUnderCursor(event)) return false;
        // Draft clicks, including Shift-constrained drawing, must not start segment drag.
        if (this.draftShape) return false;
        if (this.draftCursorState$.getValue().active) return false;
        return true;
      }),
      map((event): PointPayload | undefined => {
        const hit = this.resolveVirtualLineHit(event);
        if (!hit) return undefined;

        const storey = this.selectedStorey;
        if (!storey) return undefined;

        const shape = hit.shape;
        // Segment endpoints are internal drag state, not visible point selection.
        if (this.selections.pointIndexes$.getValue()?.length) {
          this.selections.pointIndexes$.next(undefined);
          this.updateTooltip();
        }

        const pointerPosition = this.getMetricPointerPosition(
          event,
          hit.elevation,
        );
        if (!pointerPosition) return undefined;

        // Use the point on the segment (projected from pointer) as drag origin so offset
        // is correct regardless of where along the segment the user grabbed.
        const startPoint = shape.points?.[hit.startIndex];
        const endPoint = shape.points?.[hit.endIndex];
        const segmentOrigin =
          startPoint && endPoint
            ? projectPointOntoSegmentClamped(
                pointerPosition,
                toPoint2D(startPoint),
                toPoint2D(endPoint),
              )
            : pointerPosition;

        const anchorIndex = hit.startIndex;
        const meta: PointMeta = {
          shape,
          shapeId: hit.shapeId,
          storeyId: this.selectedStoreyId,
          isDraft: false,
          index: anchorIndex,
          origin: segmentOrigin,
          elevation: hit.elevation,
        };

        const lineSegmentPointIndexes = [hit.startIndex, hit.endIndex] as const;
        const payload = this.buildPointPayload(event, meta, segmentOrigin, {
          isLineSegmentDrag: true,
          lineSegmentPointIndexes,
        });
        // Carry both endpoints as internal drag state without selecting them.
        return {
          ...payload,
          isLineSegmentDrag: true,
          lineSegmentPointIndexes,
        };
      }),
      filter((payload): payload is PointPayload => Boolean(payload)),
      tap((payload) => {
        // Disable map panning immediately so Mapbox doesn't consume the drag.
        this.map?.dragPan?.disable?.();
        payload.preventDefault();
        this.beginPointDrag();
      }),
      takeUntil(destroy$),
    );
  }

  /**
   * Build stream for insert drag start interactions.
   */
  private buildInsertPointDragStartStream(
    pointLayerMouseDown$: Observable<MapMouseEvent>,
    mapInstance: mapboxgl.Map,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return pointLayerMouseDown$.pipe(
      map((layerEvent) => {
        const feature = queryPointFeature(mapInstance, layerEvent);
        if (!this.isInsertCandidateFeature(feature)) {
          return undefined;
        }
        return layerEvent;
      }),
      filter((layerEvent): layerEvent is MapMouseEvent =>
        guards.is.defined(layerEvent),
      ),
      filter(() => this.hasPointInsertCapability),
      filter(() => !this.locationPinController.isPinModeActive()),
      exhaustMap((event) =>
        from(this.buildInsertPointPayload(event)).pipe(
          tap((payload) => {
            if (!payload) {
              this.endPointDrag();
            }
          }),
        ),
      ),
      filter((payload): payload is PointPayload => guards.is.defined(payload)),
      tap(() => this.beginPointDrag()),
      takeUntil(destroy$),
    );
  }

  /**
   * Translate mouse move events into continuous drag payloads.
   * Snapping is applied in {@link buildPointPayload}.
   */
  private buildPointMouseMoveStream(
    pointDragStart$: Observable<PointPayload>,
    mouseMove$: Observable<PointDragPointerMove>,
    mouseUp$: Observable<MapMouseEvent>,
    interactionCancel$: Observable<void>,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return pointDragStart$.pipe(
      switchMap((startPayload) =>
        mouseMove$.pipe(
          map(({ event, modifiers }) => {
            const movePayload = this.buildPointPayload(
              event,
              {
                shape: startPayload.shape,
                shapeId: startPayload.shapeId,
                isDraft: startPayload.isDraft,
                index: startPayload.index,
                origin: startPayload.origin,
                elevation: startPayload.elevation,
              },
              startPayload.origin,
              {
                isLineSegmentDrag: startPayload.isLineSegmentDrag,
                lineSegmentPointIndexes: startPayload.lineSegmentPointIndexes,
                dragStoreyId: startPayload.storeyId,
                modifiers,
              },
            );
            return {
              ...movePayload,
              isInsertDrag: startPayload.isInsertDrag,
              insertPlaceholderId: startPayload.insertPlaceholderId,
              preserveMultiPointSelection:
                startPayload.preserveMultiPointSelection,
              isLineSegmentDrag: startPayload.isLineSegmentDrag,
              lineSegmentPointIndexes: startPayload.lineSegmentPointIndexes,
              linkedStoreyLevels: startPayload.linkedStoreyLevels,
              linkedShapeIds: startPayload.linkedShapeIds,
              linkedInsertIndexes: startPayload.linkedInsertIndexes,
              isShiftKeyPressed: startPayload.isShiftKeyPressed,
              dragStartedAtMs: startPayload.dragStartedAtMs,
              dragStartScreenPoint: startPayload.dragStartScreenPoint,
            };
          }),
          takeUntil(merge(mouseUp$, interactionCancel$, destroy$)),
        ),
      ),
      filter((payload) => guards.is.defined(payload)),
      takeUntil(destroy$),
    );
  }

  /**
   * Emit final payload when drag completes.
   */
  private buildPointMouseUpStream(
    pointDragStart$: Observable<PointPayload>,
    mouseUp$: Observable<MapMouseEvent>,
    interactionCancel$: Observable<void>,
    destroy$: Observable<void>,
  ): Observable<PointPayload> {
    return pointDragStart$.pipe(
      switchMap((payload) =>
        mouseUp$.pipe(
          map((event) => {
            const upPayload = this.buildPointPayload(
              event,
              {
                shape: payload.shape,
                shapeId: payload.shapeId,
                isDraft: payload.isDraft,
                index: payload.index,
                origin: payload.origin,
                elevation: payload.elevation,
              },
              payload.origin,
              {
                isLineSegmentDrag: payload.isLineSegmentDrag,
                lineSegmentPointIndexes: payload.lineSegmentPointIndexes,
                dragStoreyId: payload.storeyId,
              },
            );
            return {
              ...upPayload,
              isInsertDrag: payload.isInsertDrag,
              insertPlaceholderId: payload.insertPlaceholderId,
              preserveMultiPointSelection: payload.preserveMultiPointSelection,
              isLineSegmentDrag: payload.isLineSegmentDrag,
              lineSegmentPointIndexes: payload.lineSegmentPointIndexes,
              linkedStoreyLevels: payload.linkedStoreyLevels,
              linkedShapeIds: payload.linkedShapeIds,
              linkedInsertIndexes: payload.linkedInsertIndexes,
              isShiftKeyPressed: payload.isShiftKeyPressed,
              dragStartedAtMs: payload.dragStartedAtMs,
              dragStartScreenPoint: payload.dragStartScreenPoint,
            };
          }),
          takeUntil(merge(interactionCancel$, destroy$)),
          take(1),
        ),
      ),
      filter((payload) => guards.is.defined(payload)),
      tap(() => {
        this.endPointDrag();
      }),
      takeUntil(destroy$),
    );
  }

  /**
   * Mark that a point drag is active and clear transient insert states.
   */
  private beginPointDrag(): void {
    if (this.isPointDragging) return;
    this.isPointDragging = true;
    this.hoverController.deactivateLineInsertCandidateHover();
    this.hoverController.clearHoverState(MapHoverStateType.Line);
    this.hoverController.clearHoverState(MapHoverStateType.DraftLine);
  }

  /**
   * Mark that the active point drag has completed.
   */
  private endPointDrag(): void {
    if (!this.isPointDragging) return;
    this.isPointDragging = false;
    this.snapHandler?.resetStickyDirectionSnap();
  }

  /**
   * Build a {@link PointPayload} for a newly inserted point.
   * @param event {@link MapMouseEvent} describing the pointer interaction
   * @returns {@link PointPayload} or undefined when insertion failed
   */
  private async buildInsertPointPayload(
    event: MapMouseEvent,
    atPointer = false,
  ): Promise<PointPayload | undefined> {
    const linePayload = this.buildLineClickPayload(event, atPointer);
    if (!linePayload) return undefined;
    const context = this.getPointActionContext();
    const insertResult = buildLineInsertResult(context, linePayload);

    if (!insertResult) return undefined;
    // Ensure the temporary insert candidate disappears once the drag begins.
    this.hoverController.deactivateLineInsertCandidateHover();
    this.hoverController.clearHoverState(MapHoverStateType.Line);
    const placeholderId = this.allocateInsertPlaceholderId(
      insertResult.shapeId,
    );
    return buildInsertedPointPayload({
      event,
      linePayload,
      insertResult,
      map: this.map,
      selectedStoreyId: this.selectedStoreyId,
      getStoreyAdjustedScreenPoint: (event) =>
        this.getStoreyAdjustedScreenPoint(event),
      placeholderId,
    });
  }

  private allocateInsertPlaceholderId(shapeId: string): string {
    this.insertPlaceholderSeed += 1;
    return `insert-placeholder:${shapeId}:${this.insertPlaceholderSeed}`;
  }

  // Henrik: insert drags are handled via Handlers.point.insert.{start,update,end}.

  /**
   * Construct a {@link PointPayload} from a map layer event.
   * Applies snapping and boundary enforcement when available.
   * @param event {@link MapMouseEvent} emitted by Mapbox
   * @param meta {@link PointMeta} describing the point
   * @param originPoint Original position in metric space
   * @param options Drag flags. Omit for a new point gesture so the live selection is used.
   * @returns {@link PointPayload}
   */
  private buildPointPayload(
    event: MapMouseEvent,
    meta: PointMeta,
    originPoint: Point2D,
    options?: PointDragPayloadOptions,
  ): PointPayload {
    const isLineSegmentDrag = options?.isLineSegmentDrag ?? false;
    const lineSegmentPointIndexes = options?.lineSegmentPointIndexes;
    const dragStoreyId = options?.dragStoreyId;
    const modifiers = options?.modifiers;
    const modifierKeys = modifiers ?? event.originalEvent;
    const payload = {
      ...buildPointPayload({
        event,
        meta,
        originPoint,
        map: this.map,
        location: this.location,
        // Continuations pass the storey captured at mousedown. A later mouseup
        // can run after deselect or a camera URL update cleared the live selection.
        selectedStoreyId: resolvePointDragStoreyId({
          dragStoreyId,
          selectedStoreyId: this.selectedStoreyId,
        }),
        modifiers,
      }),
      isLineSegmentDrag,
      uniformPolygonOffset: isLineSegmentDrag && Boolean(modifierKeys.altKey),
      suppressUpwardPropagation: Boolean(
        isLineSegmentDrag &&
        modifierKeys.shiftKey &&
        (modifierKeys.metaKey || modifierKeys.ctrlKey),
      ),
      segmentTranslationSnapMeters: isLineSegmentDrag
        ? modifierKeys.shiftKey
          ? SHIFT_SEGMENT_TRANSLATION_SNAP_METERS
          : DEFAULT_SEGMENT_TRANSLATION_SNAP_METERS
        : undefined,
    };

    // Uniform polygon offset has its own whole-ring clearance and cutout
    // clamping. Point/edge snap only sees the grabbed endpoints and would
    // therefore distort the pointer offset for the remaining edges.
    if (payload.uniformPolygonOffset) return payload;

    // Cmd/Ctrl bypasses external snap targets for both corners and segments.
    if (payload.isMetaOrCtrlKeyPressed) {
      this.snapHandler?.resetStickyDirectionSnap();
      return this.applyCutoutPointBoundaryClamp(meta, originPoint, payload);
    }
    if (modifierKeys.shiftKey) {
      this.snapHandler?.resetStickyDirectionSnap();
      const pointDragSnapGuides = isLineSegmentDrag
        ? undefined
        : getPointDragSnapGuides(
            meta.shape.points,
            meta.index,
            meta.isDraft,
            originPoint,
          );
      const constrained = pointDragSnapGuides
        ? this.toSnappedPointPayload(
            payload,
            meta,
            originPoint,
            snapPointToDragGuides(payload.position, pointDragSnapGuides),
          )
        : payload;
      return {
        ...this.applyCutoutPointBoundaryClamp(meta, originPoint, constrained),
        pointDragSnapGuides,
      };
    }

    if (!this.snapHandler) return payload;

    // Build exclusion set from co-selected points so snap treats them as
    // part of the drag group instead of snap targets.
    const selectedIndexes = this.selections.pointIndexes$.getValue();
    const excludedPointIndexes = resolveDragGroupPointIndexes({
      isLineSegmentDrag,
      lineSegmentPointIndexes,
      selectedPointIndexes: selectedIndexes,
    });

    // Geometry / 90° first; blueprint ink only when those do not latch.
    const snapResult = this.snapHandler.snap({
      position: payload.position,
      origin: originPoint,
      shapeId: meta.shapeId,
      pointIndex: meta.index,
      isDraft: meta.isDraft,
      excludedPointIndexes,
      isLineSegmentDrag,
    });
    if (snapResult.snapped) {
      return this.toSnappedPointPayload(
        payload,
        meta,
        originPoint,
        snapResult.position,
        snapResult.snapAngle,
      );
    }

    const inkSnap = this.tryBlueprintInkSnap(payload.position, event);
    if (inkSnap) {
      return this.toSnappedPointPayload(payload, meta, originPoint, inkSnap);
    }

    return this.applyCutoutPointBoundaryClamp(meta, originPoint, payload);
  }

  /**
   * Build a point payload from a snapped metric position (shared by blueprint + geometry snaps).
   */
  private toSnappedPointPayload(
    payload: PointPayload,
    meta: PointMeta,
    originPoint: Point2D,
    snappedPosition: Point2D,
    snapAngle?: number,
  ): PointPayload {
    const snappedCoordinate = convertMetricOffsetToCoordinate(
      this.location,
      snappedPosition,
    );
    const snappedScreenPoint =
      this.map && meta.elevation !== undefined && !Number.isNaN(meta.elevation)
        ? toPoint2D(
            projectLngLatAtElevation(
              this.map,
              snappedCoordinate,
              meta.elevation,
            ),
          )
        : payload.screenPoint;

    return this.applyCutoutPointBoundaryClamp(meta, originPoint, {
      ...payload,
      position: snappedPosition,
      offset: getOffset(originPoint, snappedPosition),
      distance: getDistance(originPoint, snappedPosition),
      snapAngle,
      coordinate: snappedCoordinate,
      screenPoint: snappedScreenPoint,
    });
  }

  /**
   * Clamp cutout point drags to keep a valid 1m margin in real time.
   * Uses the same “follow pointer + slide along enclosing wall” idea as mezzanine snapping
   * so vertices keep moving when the cursor leaves the feasible region.
   */
  private applyCutoutPointBoundaryClamp(
    meta: PointMeta,
    originPoint: Point2D,
    payload: PointPayload,
  ): PointPayload {
    if (meta.isDraft) return payload;
    if (!meta.storeyId || !meta.shapeId) return payload;

    const storey = getStoreyById(this.storeys, meta.storeyId);
    if (!storey?.shapes?.length) return payload;
    const shape = storey.shapes.find(
      (candidate) => candidate.id === meta.shapeId,
    );
    if (!shape?.points?.length) return payload;

    if (shape.cutout !== true) {
      return this.applyCourtyardSolidPointBoundaryClamp(
        meta,
        originPoint,
        storey,
        shape,
        payload,
      );
    }

    const currentPoint = shape.points[meta.index];
    if (!currentPoint) return payload;

    const clampedPosition = resolveClampedCutoutVertexPosition({
      storey,
      cutout: shape,
      vertexIndex: meta.index,
      currentVertex: currentPoint,
      targetVertex: payload.position,
      minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
    });
    const clampedCoordinate = convertMetricOffsetToCoordinate(
      this.location,
      clampedPosition,
    );
    const clampedScreenPoint =
      this.map && meta.elevation !== undefined && !Number.isNaN(meta.elevation)
        ? toPoint2D(
            projectLngLatAtElevation(
              this.map,
              clampedCoordinate,
              meta.elevation,
            ),
          )
        : payload.screenPoint;

    return {
      ...payload,
      position: clampedPosition,
      offset: getOffset(originPoint, clampedPosition),
      distance: getDistance(originPoint, clampedPosition),
      coordinate: clampedCoordinate,
      screenPoint: clampedScreenPoint,
    };
  }

  /**
   * Clamp a courtyard-house vertex so the footprint stays inside its cutout.
   * Solids that are not inside a hole are unchanged.
   */
  private applyCourtyardSolidPointBoundaryClamp(
    meta: PointMeta,
    originPoint: Point2D,
    storey: Storey,
    shape: Shape,
    payload: PointPayload,
  ): PointPayload {
    const currentPoint = shape.points[meta.index];
    if (!currentPoint) return payload;

    const clampedPosition = resolveClampedSolidVertexInsideCutout({
      storey,
      solid: shape,
      vertexIndex: meta.index,
      currentVertex: currentPoint,
      targetVertex: payload.position,
      minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
    });
    if (
      clampedPosition[0] === payload.position[0] &&
      clampedPosition[1] === payload.position[1]
    ) {
      return payload;
    }

    const clampedCoordinate = convertMetricOffsetToCoordinate(
      this.location,
      clampedPosition,
    );
    const clampedScreenPoint =
      this.map && meta.elevation !== undefined && !Number.isNaN(meta.elevation)
        ? toPoint2D(
            projectLngLatAtElevation(
              this.map,
              clampedCoordinate,
              meta.elevation,
            ),
          )
        : payload.screenPoint;

    return {
      ...payload,
      position: clampedPosition,
      offset: getOffset(originPoint, clampedPosition),
      distance: getDistance(originPoint, clampedPosition),
      coordinate: clampedCoordinate,
      screenPoint: clampedScreenPoint,
    };
  }

  /**
   * Resolve linked storey levels for live drag rendering using geometry, not ids.
   * @param payload {@link PointPayload} describing the active point
   * @returns linked storey level indexes
   */
  private resolveLinkedStoreyTargets(
    payload: PointPayload,
  ): { levels: number[]; shapeIdsByLevel: Record<number, string> } | undefined {
    const targets = getLinkedStoreyTargets(
      this.getPointActionContext(),
      payload,
    );
    if (!targets.length) return undefined;

    const levels: number[] = [];
    const shapeIdsByLevel: Record<number, string> = {};
    targets.forEach(({ storeyId, shapeId }) => {
      const level = this.storeys.findIndex((storey) => storey.id === storeyId);
      if (level >= 0) {
        levels.push(level);
        shapeIdsByLevel[level] = shapeId;
      }
    });

    return levels.length ? { levels, shapeIdsByLevel } : undefined;
  }

  /**
   * Convert a geographic coordinate into the local metric coordinate space.
   * @param coordinate {@link Coordinate} to convert
   * @returns {@link Point2D} in metric units
   */
  private getPositionFromCoordinate(coordinate: Coordinate): Point2D {
    const [mx, my] = convertCoordinateToMetricOffset(coordinate, this.location);
    return [mx, -my];
  }

  /**
   * Build the draft preview payload describing pointer distance.
   * Applies snapping to compute the final position indicator.
   * Also shows a cursor-following crosshair before the first vertex when
   * draft mode is active (empty design / Alt draw).
   * @param event {@link MapMouseEvent} from the map
   * @returns {@link DraftPreviewPayload} or undefined when invalid
   */
  private buildDraftPreviewPayload(
    event: MapMouseEvent,
  ): DraftPreviewPayload | undefined {
    if (
      event.originalEvent.ctrlKey &&
      (this.buildingFootprintTemplate ||
        navigator.userAgent.includes('Mac') ||
        !this.isUnderlayDrawingActive())
    )
      return undefined;

    const draftCursor = this.draftCursorState$.getValue();
    if (
      !draftCursor.active ||
      (draftCursor.suppressed && !this.buildingFootprintTemplate)
    )
      return undefined;

    const draftShape = this.draftShape;
    const screenPoint = this.getStoreyAdjustedScreenPoint(event);
    if (!screenPoint) return undefined;
    const pointerPosition = this.getMetricPointerPosition(
      event,
      this.getSelectedStoreyElevationValue(),
    );
    if (!pointerPosition) return undefined;

    this.lastDraftPointerEvent = event;

    const lastPoint = draftShape?.points[draftShape.points.length - 1];
    const lastPointMetric = lastPoint ? toPoint2D(lastPoint) : undefined;
    let snappedPosition = pointerPosition;
    if (this.isUnderlayDrawingSnapDisabled(event)) {
      snappedPosition =
        this.snapHandler?.clampDraftToMezzanineBounds(pointerPosition) ??
        pointerPosition;
    } else if (this.penMode) {
      snappedPosition = lastPointMetric
        ? this.resolvePenDraftSnap(lastPointMetric, pointerPosition)
        : pointerPosition;
    } else {
      const shiftHeld = Boolean(event.originalEvent.shiftKey);
      // Prefer close-to-first-point so the preview matches finalize behavior.
      if (
        draftShape?.points[0] &&
        this.snapHandler?.wouldCloseDraftShape(pointerPosition)
      ) {
        snappedPosition = toPoint2D(draftShape.points[0]);
      } else if (lastPointMetric) {
        if (shiftHeld) {
          snappedPosition = this.resolveShiftDraftSnap(
            pointerPosition,
            event,
            draftShape,
          );
          snappedPosition =
            this.tryBlueprintInkSnap(snappedPosition, event) ?? snappedPosition;
        } else {
          const geometrySnapped = this.resolvePositionWithGeometryThenInk(
            pointerPosition,
            lastPointMetric,
            event,
          );
          snappedPosition = this.customBuildingFootprintDrawingActive
            ? snapDraftSegmentLength(lastPointMetric, geometrySnapped)
            : geometrySnapped;
        }
      } else if (!shiftHeld || this.isUnderlayDrawingActive()) {
        // First draft tip: no angular origin yet — ink soft-snap only.
        snappedPosition =
          this.tryBlueprintInkSnap(pointerPosition, event) ?? pointerPosition;
      }
    }

    if (lastPointMetric) {
      const existing =
        draftShape?.points.map((point) => toPoint2D(point)) ?? [];
      // Keep the rubber band on the pointer when a 90° snap would run away.
      snappedPosition =
        preferPointWithinMaxSeparation(
          snappedPosition,
          pointerPosition,
          existing,
        ) ?? lastPointMetric;
    }

    const snappedCoordinate = convertMetricOffsetToCoordinate(
      this.location,
      snappedPosition,
    );

    const distance = lastPointMetric
      ? getDistance(lastPointMetric, snappedPosition)
      : 0;
    if (!Number.isFinite(distance)) return undefined;

    const draftPoints = draftShape?.points.map((point) => toPoint2D(point));
    const templateShapes =
      !draftShape && this.buildingFootprintTemplate
        ? createBuildingFootprintTemplateDrafts(
            this.buildingFootprintTemplate,
            snappedPosition,
            this.map?.getBearing() ?? 0,
            0,
          )
        : undefined;
    const templatePlacementInvalid = templateShapes
      ? applyCompletedDraftsToStoreys(this.storeys, templateShapes, {
          selectedStoreyId: this.selectedStoreyId,
          otherBuildings: this.otherBuildings,
        }).status !== 'applied'
      : undefined;
    return {
      screenPoint,
      distance,
      snappedPosition,
      snappedCoordinate,
      draftPoints,
      templateShapes,
      templatePlacementInvalid,
      templateCenterScreenPoint: templateShapes
        ? this.projectMetricToScreen(
            snappedPosition,
            this.getSelectedStoreyElevationValue(),
          )
        : undefined,
      lastCommittedSegment: this.buildLastCommittedSegmentLabel(draftPoints),
    };
  }

  /**
   * Midpoint length label for the last already-placed draft edge.
   */
  private buildLastCommittedSegmentLabel(
    draftPoints: Point2D[] | undefined,
  ): DraftPreviewPayload['lastCommittedSegment'] {
    if (!draftPoints || draftPoints.length < 2) return undefined;
    const start = draftPoints[draftPoints.length - 2];
    const end = draftPoints[draftPoints.length - 1];
    if (!start || !end) return undefined;
    const distanceMeters = getDistance(start, end);
    if (!Number.isFinite(distanceMeters) || !(distanceMeters > 0)) {
      return undefined;
    }
    const midpoint: Point2D = [
      (start[0] + end[0]) / 2,
      (start[1] + end[1]) / 2,
    ];
    const screenPoint = this.projectMetricToScreen(midpoint);
    if (!screenPoint) return undefined;
    return { screenPoint, distanceMeters };
  }

  /**
   * Project a local-meter point to current map screen pixels, optionally in a
   * storey's elevation plane.
   */
  private projectMetricToScreen(
    point: Point2D,
    elevation?: number,
  ): Point2D | undefined {
    if (!this.map) return undefined;
    const coordinate = convertMetricOffsetToCoordinate(this.location, point);
    return elevation === undefined
      ? toPoint2D(this.map.project([coordinate.lng, coordinate.lat]))
      : projectLngLatAtElevation(this.map, coordinate, elevation);
  }

  private setBuildingRotationCursorState(
    state: BuildingRotationCursorState,
  ): void {
    const current = this.buildingRotationCursor$.getValue();
    if (current.hover === state.hover && current.dragging === state.dragging) {
      return;
    }
    this.buildingRotationCursor$.next(state);
  }

  /** Resolve the invisible rotation band outside the selected building volume. */
  private resolveBuildingRotationHandle(event: MapMouseEvent):
    | {
        center: Point2D;
        elevation: number;
        shapeId: Shape['id'];
        storeyId: Storey['id'];
      }
    | undefined {
    if (event.originalEvent.ctrlKey && navigator.userAgent.includes('Mac'))
      return undefined;
    if (this.facadeModeActive || this.designMapTool !== 'build')
      return undefined;
    if (!this.state.isBuildingOutlineSelected$.getValue()) return undefined;
    if (this.spacePanActive || this.buildingPickingPaused || this.isReadonly) {
      return undefined;
    }
    if (this.locationPinController.isPinModeActive()) return undefined;
    if (this.isPointDragging || this.activeShapeDrag) return undefined;

    const center = getBuildingRotationCenter(this.storeys);
    if (!center) return undefined;
    const topStorey = this.storeys.reduce<Storey | undefined>(
      (current, storey) => {
        if (!getSolidShapes(storey).some((shape) => shape.points.length)) {
          return current;
        }
        if (!current) return storey;
        const currentTop =
          getStoreyElevation(this.storeys, current) + current.height;
        const candidateTop =
          getStoreyElevation(this.storeys, storey) + storey.height;
        return candidateTop >= currentTop ? storey : current;
      },
      undefined,
    );
    const shape = getSolidShapes(topStorey)[0];
    if (!topStorey || !shape) return undefined;
    const elevation =
      getStoreyElevation(this.storeys, topStorey) + topStorey.height;
    const pointerScreen = toPoint2D(
      event.point ?? this.map?.project(event.lngLat),
    );
    if (!pointerScreen) return undefined;

    const faces: Point2D[][] = [];
    const project = (point: Point2D, height: number): Point2D | undefined =>
      this.buildingContourProjector?.(
        convertMetricOffsetToCoordinate(this.location, point),
        height,
      ) ?? this.projectMetricToScreen(point, height);
    for (const storey of this.storeys) {
      const { bottom, top } = getStoreyElevationRange(this.storeys, storey);
      for (const solid of getSolidShapes(storey)) {
        const floor = solid.points.map((point) => project(point, bottom));
        const roof = solid.points.map((point) => project(point, top));
        if (floor.some((point) => !point) || roof.some((point) => !point))
          continue;
        const floorPoints = floor.filter((point) => point !== undefined);
        const roofPoints = roof.filter((point) => point !== undefined);
        faces.push(floorPoints, roofPoints);
        for (let index = 0; index < floorPoints.length; index++) {
          const next = (index + 1) % floorPoints.length;
          const wall = [
            floorPoints[index],
            floorPoints[next],
            roofPoints[next],
            roofPoints[index],
          ];
          faces.push(wall.filter((point) => point !== undefined));
        }
      }
    }
    const hasHandle = isPointerOutsideBuildingContour({
      pointer: pointerScreen,
      faces,
    });
    return hasHandle
      ? { center, elevation, shapeId: shape.id, storeyId: topStorey.id }
      : undefined;
  }

  private buildBuildingRotationDragState(
    event: MapMouseEvent,
  ): BuildingRotationDragState | undefined {
    if (this.activeBuildingRotation || this.activeShapeDrag) return undefined;
    const handle = this.resolveBuildingRotationHandle(event);
    if (!handle) return undefined;
    const pointerOrigin = this.getMetricPointerPosition(
      event,
      handle.elevation,
    );
    if (!pointerOrigin) return undefined;
    event.preventDefault();
    return {
      ...handle,
      pointerOrigin,
      referenceEdge: getBuildingRotationReferenceEdge(this.storeys),
      hasMoved: false,
      targets: getBuildingRotationTargets(this.storeys),
    };
  }

  private handleBuildingRotationStart(state: BuildingRotationDragState): void {
    this.activeBuildingRotation = state;
    this.setBuildingRotationCursorState({ hover: true, dragging: true });
    this.shapeTranslation$.next({
      shapeId: state.shapeId,
      baseStoreyId: state.storeyId,
      targets: state.targets,
      delta: [0, 0],
      rotation: { center: state.center, radians: 0 },
      groundLevelOriginOutline: true,
    });
    this.updateTooltip();
  }

  private buildBuildingRotationPayload(
    state: BuildingRotationDragState,
    event: MapMouseEvent,
  ): ShapeTranslatePayload | undefined {
    const pointer = this.getMetricPointerPosition(event, state.elevation);
    if (!pointer) return undefined;
    state.hasMoved ||= getDistance(state.pointerOrigin, pointer) > 1e-3;
    return {
      shapeId: state.shapeId,
      baseStoreyId: state.storeyId,
      targets: state.targets,
      delta: [0, 0],
      rotation: {
        center: state.center,
        radians: snapBuildingRotationRadians({
          radians: getRotationDeltaRadians(
            state.center,
            state.pointerOrigin,
            pointer,
          ),
          disabled: isBuildingRotationSnapDisabled(
            event.originalEvent,
            navigator.userAgent.includes('Mac'),
          ),
          shiftKey: event.originalEvent.shiftKey,
          center: state.center,
          referenceEdge: state.referenceEdge,
          project: (point) =>
            this.buildingContourProjector?.(
              convertMetricOffsetToCoordinate(this.location, point),
              state.elevation,
            ) ?? this.projectMetricToScreen(point, state.elevation),
        }),
      },
      groundLevelOriginOutline: true,
      screenPoint: toPoint2D(event.point ?? this.map?.project(event.lngLat)),
    };
  }

  private handleBuildingRotationCompletion(
    event: MapMouseEvent,
    state: BuildingRotationDragState,
  ): void {
    const payload = this.buildBuildingRotationPayload(state, event);
    const radians = payload?.rotation?.radians ?? 0;
    this.activeBuildingRotation = undefined;
    if (state.hasMoved && payload && Math.abs(radians) > 1e-6) {
      this.shapeTranslateEnd$.next({
        shapeId: state.shapeId,
        storeyId: state.storeyId,
        targets: state.targets,
        delta: [0, 0],
        rotation: payload.rotation,
      });
    }
    this.shapeTranslation$.next(undefined);
    this.setBuildingRotationCursorState({ hover: false, dragging: false });
    this.updateTooltip();
    this.interactionResetHandler?.();
  }

  /** Use the same eligible target for hover affordances and polygon drag-start. */
  private buildShapeHoverFromPointer(
    event: MapMouseEvent,
  ): MapHoverState | undefined {
    const target = this.resolveShapeTranslationTarget(event);
    return target
      ? this.hoverController.buildShapeHoverState(
          target.shape.id,
          this.getEventScreenPoint(event),
        )
      : undefined;
  }

  /**
   * Resolve a draggable polygon without calculating linked translation targets.
   * @param event {@link MapMouseEvent} describing the pointer interaction
   */
  private resolveShapeTranslationTarget(event: MapMouseEvent):
    | {
        shape: Shape;
        shapeId: string;
        storeyId: string;
        storeyIndex: number;
        elevation: number;
        pointerOrigin: Point2D;
      }
    | undefined {
    if (
      !this.allowsShapeTranslate() ||
      event.originalEvent.ctrlKey ||
      this.spacePanActive ||
      this.locationPinController.isPinModeActive() ||
      this.isPointDragging ||
      this.activeShapeDrag ||
      this.isWholeBuildingTranslateActive()
    )
      return undefined;
    if (this.hasPointUnderCursor(event) || this.resolveVirtualLineHit(event))
      return undefined;
    const shapeId =
      this.resolveSelectedStoreyShapeIdFromPointerEvent(event) ??
      getPolygonFeatureFromMapEvent(event)?.properties?.id;
    if (!shapeId) return undefined;

    // Build: only drag the already-selected shape.
    if (this.designMapTool === 'build') {
      const selectedShapeId = this.selections.shapeId$.getValue();
      if (!selectedShapeId || selectedShapeId !== shapeId) return undefined;
    }

    const storeyId = this.selectedShapeStoreyId ?? this.selectedStoreyId;
    if (!storeyId) return undefined;
    const storeyIndex = this.storeys.findIndex(
      (candidate) => candidate.id === storeyId,
    );
    if (storeyIndex < 0) return undefined;
    const storey = this.storeys[storeyIndex];
    const shape = storey?.shapes?.find((candidate) => candidate.id === shapeId);
    if (!shape?.points?.length) return undefined;
    if (
      this.designMapTool === 'build' &&
      !isEntirePolygonSelected(shape, this.getSelections())
    )
      return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointerOrigin = this.getMetricPointerPosition(event, elevation);
    if (!pointerOrigin) return undefined;

    return { shape, shapeId, storeyId, storeyIndex, elevation, pointerOrigin };
  }

  private buildShapeDragState(
    event: MapMouseEvent,
  ): ShapeDragState | undefined {
    if (!this.allowsShapeTranslate()) return undefined;
    if (event.originalEvent.ctrlKey) return undefined; // Don't drag when ctrl is pressed since that should be used for camera rotation
    if (this.locationPinController.isPinModeActive()) return undefined;
    if (this.isPointDragging) return undefined; // Insert-drags should not translate shapes.
    if (this.activeShapeDrag) return undefined;

    if (this.isWholeBuildingTranslateActive()) {
      return this.buildWholeBuildingDragState(event);
    }

    const target = this.resolveShapeTranslationTarget(event);
    if (!target) return undefined;
    const { shape, shapeId, storeyId, storeyIndex, elevation, pointerOrigin } =
      target;

    const originPoints = shape.points.map((point) => toPoint2D(point));
    const targets = resolveShapeTranslationTargets({
      storeys: this.storeys,
      shapeId,
      baseShape: shape,
      originPoints,
      baseStoreyIndex: storeyIndex,
    });
    return {
      shapeId,
      storeyId,
      chainTargets: targets.chain.length
        ? targets.chain
        : [{ storeyId, storeyLevel: storeyIndex }],
      elevation,
      pointerOrigin,
      screenPoint: toPoint2D(event.point ?? this.map?.project(event.lngLat)),
      intersectionTargets: targets.intersections,
    };
  }

  /**
   * Drag state that translates every shape on the selected building together.
   * Anchor is the volume (or 2D fill) under the pointer. The location pin is
   * rebased to the combined footprint midpoint as soon as the drag ends.
   */
  private buildWholeBuildingDragState(
    event: MapMouseEvent,
  ): ShapeDragState | undefined {
    const anchor = this.resolveWholeBuildingDragAnchor(event);
    if (!anchor) return undefined;
    const pointerOrigin = this.getMetricPointerPosition(
      event,
      anchor.elevation,
    );
    if (!pointerOrigin) return undefined;
    const targets = buildWholeBuildingTranslationTargets(this.storeys);
    return {
      shapeId: anchor.shapeId,
      storeyId: anchor.storeyId,
      chainTargets: targets.chain,
      elevation: anchor.elevation,
      pointerOrigin,
      screenPoint: toPoint2D(event.point ?? this.map?.project(event.lngLat)),
      intersectionTargets: targets.intersections,
      wholeBuilding: true,
      duplicateBuilding: event.originalEvent.altKey,
    };
  }

  /**
   * Shape and storey under the pointer used as the drag handle for a
   * whole-building translate. Volume picking covers 3D walls; fill-layer
   * features cover a top-down footprint.
   */
  private resolveWholeBuildingDragAnchor(
    event: MapMouseEvent,
  ): WholeBuildingDragAnchor | undefined {
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) {
      const hit = getStoreyVolumeRayHit(facadeRay, this.storeys);
      return hit
        ? {
            shapeId: hit.shape.id,
            storeyId: hit.storey.id,
            elevation: hit.point[2],
          }
        : undefined;
    }

    const ray = this.getBuildingPickRay(event);
    const storeyFromRay = ray ? pickStoreyByRay(ray, this.storeys) : undefined;
    if (storeyFromRay && ray) {
      const elevation =
        getStoreyRayHitElevation(ray, this.storeys, storeyFromRay, {
          mode: 'solid-footprint',
        }) ?? getStoreyElevation(this.storeys, storeyFromRay);
      const point = getRayPointAtElevation(ray, elevation);
      const shape = pickSolidShapeOnStorey(storeyFromRay, point);
      if (shape) {
        return {
          shapeId: shape.id,
          storeyId: storeyFromRay.id,
          elevation,
        };
      }
    }

    const features = this.queryRenderedFeaturesAroundPoint(
      event,
      [MapLayerIds.BuildingFill2d],
      0,
    );
    const polygon = features.find((feature) =>
      mapGuards.is.feature.polygon(feature),
    );
    const fillShapeId = polygon?.properties?.id;
    if (typeof fillShapeId !== 'string' || !fillShapeId) return undefined;
    const fillLevel = Number(polygon?.properties?.[MapStoreyProperty.Level]);
    const storey = Number.isFinite(fillLevel)
      ? this.storeys[fillLevel]
      : this.storeys.find((candidate) =>
          candidate.shapes.some((shape) => shape.id === fillShapeId),
        );
    if (!storey?.shapes.some((shape) => shape.id === fillShapeId)) {
      return undefined;
    }
    return {
      shapeId: fillShapeId,
      storeyId: storey.id,
      elevation: getStoreyElevation(this.storeys, storey),
    };
  }

  /**
   * Normalize translation targets per storey to avoid shared shape id collisions.
   * Cached for the duration of the active drag so mousemove does not rebuild them.
   * @param state Current drag state
   */
  private buildShapeTranslationTargets(
    state: ShapeDragState,
  ): ShapeTranslatePayload['targets'] {
    if (this.cachedShapeTranslationTargets) {
      return this.cachedShapeTranslationTargets;
    }
    const targetsByStorey = new Map<
      string,
      { level: number | undefined; shapeIds: Set<string> }
    >();

    const register = (
      storeyId: string,
      storeyLevel: number | undefined,
      shapeId: string,
    ): void => {
      const existing = targetsByStorey.get(storeyId);
      if (existing) {
        if (storeyLevel !== undefined) existing.level = storeyLevel;
        existing.shapeIds.add(shapeId);
        return;
      }
      targetsByStorey.set(storeyId, {
        level: storeyLevel,
        shapeIds: new Set([shapeId]),
      });
    };

    state.chainTargets.forEach(({ storeyId, storeyLevel }) => {
      register(storeyId, storeyLevel, state.shapeId);
    });

    Object.entries(state.intersectionTargets ?? {}).forEach(
      ([storeyId, shapeIds]) => {
        shapeIds.forEach((shapeId) => register(storeyId, undefined, shapeId));
      },
    );

    const targets = Array.from(targetsByStorey.entries()).map(
      ([storeyId, { level, shapeIds }]) => ({
        storeyId,
        storeyLevel:
          level ??
          Math.max(
            0,
            this.storeys.findIndex((candidate) => candidate.id === storeyId),
          ),
        shapeIds: Array.from(shapeIds),
      }),
    );
    this.cachedShapeTranslationTargets = targets;
    return targets;
  }

  private handleShapeDragStart(state: ShapeDragState): void {
    this.activeShapeDrag = state;
    this.state.hoveredStoreyId$.next(undefined);
    this.clearShapeTranslationCaches();
    // Ensure add-point candidate visuals are hidden while translating a shape.
    this.hoverController.deactivateLineInsertCandidateHover();
    this.hoverController.clearHoverState(MapHoverStateType.Line);
    this.hoverController.clearHoverState(MapHoverStateType.DraftLine);
    const targets = this.buildShapeTranslationTargets(state);
    this.cachedMovingShapes = this.getMovingShapesForTranslation(
      state.storeyId,
      state.shapeId,
    );
    this.cachedOverlapMoving = this.getOverlapMovingForTranslation(
      state.storeyId,
      state.shapeId,
    );
    this.shapeTranslation$.next({
      shapeId: state.shapeId,
      baseStoreyId: state.storeyId,
      targets,
      delta: [0, 0],
      screenPoint: state.screenPoint,
      groundLevelOriginOutline: state.wholeBuilding,
      duplicateBuilding: state.duplicateBuilding,
    });
    this.updateTooltip();
  }

  private buildShapeTranslationPayload(
    state: ShapeDragState,
    event: MapMouseEvent,
  ): ShapeTranslatePayload | undefined {
    state.duplicateBuilding = Boolean(
      state.wholeBuilding && event.originalEvent.altKey,
    );
    const pointer = this.getMetricPointerPosition(event, state.elevation);
    if (!pointer) return undefined;
    const delta: Point2D = [
      pointer[0] - state.pointerOrigin[0],
      pointer[1] - state.pointerOrigin[1],
    ];
    const clampedDelta = this.clampShapeTranslationDelta(
      state.storeyId,
      state.shapeId,
      delta,
    );
    // Shift disables translation snapping (storey-below / cutout center).
    if (event.originalEvent.shiftKey) {
      return {
        shapeId: state.shapeId,
        baseStoreyId: state.storeyId,
        targets: this.buildShapeTranslationTargets(state),
        delta: clampedDelta,
        screenPoint: toPoint2D(event.point ?? this.map?.project(event.lngLat)),
        groundLevelOriginOutline: state.wholeBuilding,
        duplicateBuilding: state.duplicateBuilding,
      };
    }
    const cutoutCenterDelta = this.snapHandler
      ? this.snapHandler.snapCutoutTranslationToEnclosingCenter({
          storeyId: state.storeyId,
          shapeId: state.shapeId,
          delta: clampedDelta,
        })
      : undefined;
    const afterCutoutCenter = cutoutCenterDelta ?? clampedDelta;
    const snappedDelta = this.snapHandler
      ? this.snapHandler.snapShapeTranslationToStoreyBelow({
          storeyId: state.storeyId,
          shapeId: state.shapeId,
          delta: afterCutoutCenter,
        })
      : afterCutoutCenter;
    // Snap that did not move the delta would re-run the same SAT; skip it.
    const constrainedDelta =
      snappedDelta[0] === clampedDelta[0] && snappedDelta[1] === clampedDelta[1]
        ? clampedDelta
        : this.clampShapeTranslationDelta(
            state.storeyId,
            state.shapeId,
            snappedDelta,
          );
    return {
      shapeId: state.shapeId,
      baseStoreyId: state.storeyId,
      targets: this.buildShapeTranslationTargets(state),
      delta: constrainedDelta,
      screenPoint: toPoint2D(event.point ?? this.map?.project(event.lngLat)),
      groundLevelOriginOutline: state.wholeBuilding,
      duplicateBuilding: state.duplicateBuilding,
    };
  }

  /**
   * Apply all shape translation constraints in sequence:
   * 1. Mezzanine shapes must stay inside parent storey (skipped for whole-building)
   * 2. Cutouts keep their 1 m margin inside the enclosing solid
   * 3. Courtyard houses stay inside their cutout with the same margin
   * 4. Non-ground shapes must keep overlapping at least one shape below
   *    (skipped for whole-building: relative overlap is preserved)
   * 5. Moved shapes must not overlap same-storey siblings or other buildings
   */
  private clampShapeTranslationDelta(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
    delta: Point2D,
  ): Point2D {
    const isWholeBuildingTranslate = this.isWholeBuildingTranslateActive();
    let clamped = isWholeBuildingTranslate
      ? delta
      : this.clampMezzanineTranslationDelta(storeyId, shapeId, delta);
    clamped = this.clampCutoutTranslationDelta(storeyId, shapeId, clamped);
    if (!isWholeBuildingTranslate) {
      clamped = this.clampCourtyardSolidTranslationDelta(
        storeyId,
        shapeId,
        clamped,
      );
    }
    clamped = clampStoreyOverlapDelta({
      storeys: this.storeys,
      storeyId,
      shapeId,
      delta: clamped,
      isWholeBuildingTranslate,
    });
    clamped = this.clampSiblingShapeOverlapDelta(storeyId, shapeId, clamped);
    return clamped;
  }

  /**
   * Clamp translation so moved shapes do not overlap same-storey siblings
   * (same kind: solid vs solid, cutout vs cutout) or sibling building solids.
   * An overlapping desired pose is pushed to the side nearest the mouse,
   * including while the pointer is still moving.
   */
  private clampSiblingShapeOverlapDelta(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
    delta: Point2D,
  ): Point2D {
    const [dx, dy] = delta;
    if (!dx && !dy) return delta;
    const moving = this.getOverlapMovingForTranslation(storeyId, shapeId);
    if (!moving.length) return delta;
    return clampTranslationDeltaOutsideObstacles({ delta, moving });
  }

  /**
   * Obstacle polygons for the in-flight drag. Cached for the gesture because
   * storeys and sibling buildings do not change mid-drag.
   */
  private getOverlapMovingForTranslation(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
  ): MovingPolygonForOverlapClamp[] {
    if (this.cachedOverlapMoving) return this.cachedOverlapMoving;
    const movingShapes = this.getMovingShapesForTranslation(storeyId, shapeId);
    if (!movingShapes.length) {
      this.cachedOverlapMoving = [];
      return this.cachedOverlapMoving;
    }
    const movingIdsByStorey = new Map<string, Set<string>>();
    for (const { shape, storey } of movingShapes) {
      const ids = movingIdsByStorey.get(storey.id) ?? new Set<string>();
      ids.add(shape.id);
      movingIdsByStorey.set(storey.id, ids);
    }
    this.cachedOverlapMoving = movingShapes.map(({ shape, storey }) => {
      const isCutout = shape.cutout === true;
      const movingIds = movingIdsByStorey.get(storey.id) ?? new Set<string>();
      // Same kind only; shared edges stay allowed via interior-overlap checks.
      const sameStoreyObstacles = storey.shapes
        .filter((candidate) => {
          if (movingIds.has(candidate.id)) return false;
          if (!candidate.points?.length) return false;
          if ((candidate.cutout === true) !== isCutout) return false;
          // A courtyard house may overlap its outer plate; the hole is the bound.
          if (
            !isCutout &&
            isSolidPairAllowedByCutoutHole(shape, candidate, storey)
          ) {
            return false;
          }
          return true;
        })
        .map((candidate) => ({ points: candidate.points }));
      const otherBuildingObstacles =
        isCutout || !this.otherBuildings.length
          ? []
          : getBuildingSolidShapesOverlappingElevation(
              this.otherBuildings,
              getStoreyElevationRange(this.storeys, storey),
            ).map((candidate) => ({ points: candidate.points }));
      return {
        points: shape.points,
        obstacles: [...sameStoreyObstacles, ...otherBuildingObstacles],
      };
    });
    return this.cachedOverlapMoving;
  }

  /** Drop in-flight translation caches so the next drag rebuilds them. */
  private clearShapeTranslationCaches(): void {
    this.cachedShapeTranslationTargets = undefined;
    this.cachedMovingShapes = undefined;
    this.cachedOverlapMoving = undefined;
  }

  /**
   * Shapes that move with a shape drag: chain + intersecting storeys, else the
   * grabbed shape. Includes cutouts that travel with a solid. Keyed per storey
   * because chained floors can share the same shape id.
   */
  private getMovingShapesForTranslation(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
  ): MovingShapeForTranslation[] {
    if (this.cachedMovingShapes) return this.cachedMovingShapes;
    const moving: MovingShapeForTranslation[] = [];
    const seen = new Set<string>();
    const addShape = (storey: Storey, shape: Shape | undefined): void => {
      if (!shape?.points?.length) return;
      const key = `${storey.id}:${shape.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      moving.push({ shape, storey });
    };

    const drag = this.activeShapeDrag;
    if (drag) {
      for (const target of this.buildShapeTranslationTargets(drag)) {
        const storey = getStoreyById(this.storeys, target.storeyId);
        if (!storey) continue;
        for (const id of target.shapeIds) {
          addShape(storey, getItemById(storey.shapes, id, false));
        }
      }
    }

    if (!moving.length) {
      const storey = getStoreyById(this.storeys, storeyId);
      if (!storey) return moving;
      addShape(storey, getItemById(storey.shapes, shapeId, false));
    }
    return moving;
  }

  /**
   * Clamp cutout translation so the moved cutout keeps a valid 1m boundary
   * to the enclosing solid shape during drag.
   */
  private clampCutoutTranslationDelta(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
    delta: Point2D,
  ): Point2D {
    const storey = getStoreyById(this.storeys, storeyId);
    if (!storey?.shapes?.length) return delta;
    const shape = storey.shapes.find((candidate) => candidate.id === shapeId);
    if (!shape?.points?.length || shape.cutout !== true) return delta;
    const [dx, dy] = delta;
    if (!dx && !dy) return delta;

    if (this.isCutoutTranslationValid(storey, shape, dx, dy)) return delta;

    let low = 0;
    let high = 1;
    for (let step = 0; step < 20; step += 1) {
      const mid = (low + high) / 2;
      if (this.isCutoutTranslationValid(storey, shape, dx * mid, dy * mid)) {
        low = mid;
      } else {
        high = mid;
      }
    }
    return [dx * low, dy * low];
  }

  /**
   * Clamp a courtyard-house translation so it stays inside its cutout.
   * Solids outside every hole are unchanged.
   */
  private clampCourtyardSolidTranslationDelta(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
    delta: Point2D,
  ): Point2D {
    const storey = getStoreyById(this.storeys, storeyId);
    if (!storey?.shapes?.length) return delta;
    const shape = storey.shapes.find((candidate) => candidate.id === shapeId);
    if (!shape?.points?.length || shape.cutout === true) return delta;
    return clampSolidTranslationInsideCutout(storey, shape, delta);
  }

  /**
   * Check whether translating a cutout by delta keeps boundary constraints valid.
   */
  private isCutoutTranslationValid(
    storey: Storey,
    shape: Shape,
    dx: number,
    dy: number,
  ): boolean {
    const translatedShape: Shape = {
      ...shape,
      points: shape.points.map(
        ([x, y]) => [x + dx, y + dy] as Point2D,
      ) as Polygon2D,
    };
    const translatedStorey: Storey = {
      ...storey,
      shapes: storey.shapes.map((candidate) =>
        candidate.id === shape.id ? translatedShape : candidate,
      ),
    };
    return isCutoutInsideStoreyWithMargin({
      cutout: translatedShape,
      storey: translatedStorey,
      minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
    });
  }

  /**
   * Clamp shape translation delta so a mezzanine shape stays inside its
   * parent storey. When direct movement is blocked, slides along the
   * nearest parent edge. Returns the original delta for non-mezzanine storeys.
   */
  private clampMezzanineTranslationDelta(
    storeyId: Storey['id'],
    shapeId: Shape['id'],
    delta: Point2D,
  ): Point2D {
    const storey = getStoreyById(this.storeys, storeyId);
    if (!storey?.mezzanine) return delta;

    const parentStorey = getStoreyBelow(this.storeys, storey);
    if (!parentStorey?.shapes?.length) return delta;

    const shape = storey.shapes.find((s) => s.id === shapeId);
    if (!shape?.points?.length) return delta;

    const [dx, dy] = delta;
    if (this.areAllPointsInsideParent(shape.points, dx, dy, parentStorey))
      return delta;

    const directDelta = this.binarySearchTranslationDelta(
      shape.points,
      dx,
      dy,
      parentStorey,
    );

    // If direct search made meaningful progress, use it
    const directDist =
      directDelta[0] * directDelta[0] + directDelta[1] * directDelta[1];
    if (directDist > 0.0004) return directDelta;

    // Slide along parent boundary edges
    return (
      this.slideMezzanineTranslation(shape.points, dx, dy, parentStorey) ??
      directDelta
    );
  }

  /** Binary search for the largest ratio t ∈ [0,1] where delta * t is valid. */
  private binarySearchTranslationDelta(
    points: readonly Point2D[],
    dx: number,
    dy: number,
    parentStorey: Storey,
  ): Point2D {
    let low = 0;
    let high = 1;
    for (let i = 0; i < 20; i += 1) {
      const mid = (low + high) / 2;
      if (
        this.areAllPointsInsideParent(points, dx * mid, dy * mid, parentStorey)
      ) {
        low = mid;
      } else {
        high = mid;
      }
    }
    return [dx * low, dy * low];
  }

  /**
   * Project drag vector onto parent boundary edges and find the direction
   * that allows the most movement.
   */
  private slideMezzanineTranslation(
    points: readonly Point2D[],
    dx: number,
    dy: number,
    parentStorey: Storey,
  ): Point2D | undefined {
    const dragLen = Math.sqrt(dx * dx + dy * dy);
    if (dragLen < 0.001) return undefined;

    let bestDelta: Point2D | undefined;
    let bestDist = 0;

    for (const parentShape of parentStorey.shapes) {
      const pts = parentShape.points ?? [];
      if (pts.length < 2) continue;
      const total = pts.length;

      for (let i = 0; i < total; i += 1) {
        const start = pts[i];
        const end = pts[(i + 1) % total];
        if (!start || !end) continue;

        const edgeDx = end[0] - start[0];
        const edgeDy = end[1] - start[1];
        const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
        if (edgeLen < 0.001) continue;

        // Scalar projection of drag onto edge direction
        const dot = (dx * edgeDx + dy * edgeDy) / edgeLen;
        if (Math.abs(dot) < 0.001) continue;

        const slideDx = (edgeDx / edgeLen) * dot;
        const slideDy = (edgeDy / edgeLen) * dot;

        const candidate = this.binarySearchTranslationDelta(
          points,
          slideDx,
          slideDy,
          parentStorey,
        );

        const dist = candidate[0] * candidate[0] + candidate[1] * candidate[1];
        if (dist > bestDist) {
          bestDist = dist;
          bestDelta = candidate;
        }
      }
    }

    if (bestDelta && bestDist > 0.0004) return bestDelta;
    return undefined;
  }

  /**
   * Check that the translated shape stays fully inside parent storey shapes.
   * Validates vertices and edge midpoints to catch concave-boundary crossings.
   */
  private areAllPointsInsideParent(
    points: readonly Point2D[],
    dx: number,
    dy: number,
    parentStorey: Storey,
  ): boolean {
    const total = points.length;
    for (let i = 0; i < total; i += 1) {
      const curr = points[i];
      const next = points[(i + 1) % total];
      if (!curr || !next) return false;

      const movedCurr: Point2D = [curr[0] + dx, curr[1] + dy];
      if (!isPointInsideStoreyShapes(movedCurr, parentStorey)) return false;

      // Midpoint check catches edges that bulge outside a concave parent
      const mid: Point2D = [
        (movedCurr[0] + next[0] + dx) / 2,
        (movedCurr[1] + next[1] + dy) / 2,
      ];
      if (!isPointInsideStoreyShapes(mid, parentStorey)) return false;
    }
    return true;
  }

  private async handleShapeDragCompletion(
    event: MapMouseEvent | undefined,
    state: ShapeDragState,
  ): Promise<void> {
    const latestPayload = this.shapeTranslation$.getValue();
    const payload = event
      ? this.buildShapeTranslationPayload(state, event)
      : latestPayload;
    if (!payload) {
      this.resetShapeTranslationState();
      return;
    }
    const [dx, dy] = payload.delta;
    if (!dx && !dy) {
      this.resetShapeTranslationState();
      return;
    }

    if (this.activeShapeDrag) {
      this.activeShapeDrag = undefined;
    }

    this.shapeTranslateEnd$.next({
      shapeId: state.shapeId,
      storeyId: state.storeyId,
      targets: this.buildShapeTranslationTargets(state),
      delta: payload.delta,
      duplicateBuilding: state.duplicateBuilding,
    });
    this.shapeTranslation$.next(undefined);
    this.clearShapeTranslationCaches();
    this.updateTooltip();
    this.interactionResetHandler?.();
  }

  private resetShapeTranslationState(): void {
    if (this.activeShapeDrag) {
      this.activeShapeDrag = undefined;
    }
    this.activeBuildingRotation = undefined;
    this.setBuildingRotationCursorState({ hover: false, dragging: false });
    this.clearShapeTranslationCaches();
    if (this.shapeTranslation$.getValue()) {
      this.shapeTranslation$.next(undefined);
    }
    this.updateTooltip();
  }

  /**
   * Check if a shape translation drag is currently active.
   * @returns true when a shape is being dragged
   */
  private isShapeDragging(): boolean {
    return Boolean(
      this.activeShapeDrag ??
      this.activeBuildingRotation ??
      this.shapeTranslation$.getValue(),
    );
  }

  /**
   * Recompute and push the latest tooltip payload.
   */
  private updateTooltip(): void {
    this.tooltip$.next(this.buildTooltipPayload());
  }

  private buildTooltipPayload(): MapTooltipPayload[] {
    if (this.isReadonly || this.facadeModeActive) return [];
    const pointContext = this.getPointActionContext();
    const pointMove = this.pointMove$.getValue();
    const hoverState = this.hoverState$.getValue();
    // Use hoverState.screenPoint if it exists and is defined, otherwise fallback to currentMouseScreenPoint
    const hoverScreenPoint =
      hoverState?.screenPoint ?? this.currentMouseScreenPoint;
    const selectedShapeId = this.selections.shapeId$.getValue();
    const canDragSelectedShapeUnderCursor = Boolean(
      !this.locationPinController.isPinModeActive() &&
      !this.activeShapeDrag &&
      isEntirePolygonSelected(
        this.selectedStorey?.shapes.find(
          (shape) => shape.id === selectedShapeId,
        ),
        this.getSelections(),
      ) &&
      hoverState?.type === MapHoverStateType.Shape &&
      hoverState.shapeId === selectedShapeId,
    );
    const selectedPointCount =
      this.selections.pointIndexes$.getValue()?.length ?? 0;
    const interactionTooltips = computeTooltipPayload({
      pointMove,
      canDeletePoint: Boolean(
        pointMove && shouldShowDeleteTooltip(pointContext, pointMove),
      ),
      hoverState,
      draftPreview: this.draftPreview$.getValue(),
      hasPointInsertCapability: this.hasPointInsertCapability,
      selectedShapeId,
      shapeTranslationActive: Boolean(this.shapeTranslation$.getValue()),
      shapeTranslation: this.shapeTranslation$.getValue(),
      canDragSelectedShapeUnderCursor,
      locationPin: {
        hoverPoint: this.locationPinController.getHoverPoint(),
        isPinModeActive: this.locationPinController.isPinModeActive(),
        canTranslateBuilding: this.locationPinController.canTranslateBuilding(),
        location: this.location,
      },
      isBottomFloorRestricted:
        this.isBottomFloorWithShapes() && !this.isCutoutIntentActive(),
      altKeyActive: this.isAltKeyActive || this.isDrawAltIntentActive(),
      hoverScreenPoint,
    });
    const measurementFocus = resolveGeometryMeasurementFocus({
      focusedMeasurement: this.focusedGeometryMeasurement,
      selectedPointCount,
      selectedShapeId,
      hoveredPointShapeId:
        hoverState?.type === MapHoverStateType.Point
          ? hoverState.shapeId
          : undefined,
    });
    const geometryTooltips =
      measurementFocus?.kind === 'corner-angle'
        ? this.buildCornerAngleTooltips(measurementFocus.shapeId)
        : this.buildSegmentLengthTooltips();
    return [...geometryTooltips, ...interactionTooltips];
  }

  /** Build editable angle labels at every corner of one active polygon. */
  private buildCornerAngleTooltips(shapeId: Shape['id']): MapTooltipPayload[] {
    if (
      !this.map ||
      !this.selectedStorey ||
      !this.hasBuildingSelection ||
      this.locationPinController.isPinModeActive() ||
      this.isShapeDragging()
    ) {
      return [];
    }
    const shape = this.selectedStorey.shapes.find(
      (candidate) => candidate.id === shapeId,
    );
    if (!shape) return [];
    const metricPoints = shape.points.map(toPoint2D);
    const elevation = this.getSelectedStoreyElevationValue();
    const screenPoints = metricPoints.flatMap((point) => {
      const screenPoint = this.projectMetricToScreen(point, elevation);
      return screenPoint ? [screenPoint] : [];
    });
    if (screenPoints.length !== metricPoints.length) return [];
    const signedAreaTwice = metricPoints.reduce((sum, point, index) => {
      const next = metricPoints[(index + 1) % metricPoints.length];
      return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
    }, 0);
    return buildCornerAngleLabels({ metricPoints, screenPoints }).flatMap(
      (label): MapTooltipPayload[] => {
        const angleText = formatCornerAngleDegrees(label.angleDegrees);
        if (!angleText) return [];
        return [
          {
            type: MapTooltipType.Distance,
            screenPoint: label.screenPoint,
            text: `${angleText}°`,
            tone: 'segment-length',
            anchor: 'center',
            cornerAngle: {
              shapeId: shape.id,
              pointIndex: label.pointIndex,
              angleDegrees: label.angleDegrees,
              pointsAreClockwise: signedAreaTwice > 0,
            },
          },
        ];
      },
    );
  }

  /**
   * Build one always-visible length label outside every segment on the selected
   * storey. Labels are projected on every map move so they remain attached to
   * the geometry while panning, zooming, pitching, or rotating.
   */
  private buildSegmentLengthTooltips(): MapTooltipPayload[] {
    if (
      !this.map ||
      !this.selectedStorey ||
      !this.hasBuildingSelection ||
      this.locationPinController.isPinModeActive() ||
      this.isShapeDragging()
    ) {
      return [];
    }

    const elevation = this.getSelectedStoreyElevationValue();

    return this.selectedStorey.shapes.flatMap((shape) => {
      const metricPoints = shape.points.map(toPoint2D);
      const signedAreaTwice = metricPoints.reduce((sum, point, index) => {
        const next = metricPoints[(index + 1) % metricPoints.length];
        return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
      }, 0);
      const screenPoints = metricPoints.flatMap((point) => {
        const screenPoint = this.projectMetricToScreen(point, elevation);
        return screenPoint ? [screenPoint] : [];
      });
      if (screenPoints.length !== metricPoints.length) return [];

      return buildSegmentLengthLabels({
        metricPoints,
        screenPoints,
        preferInside: shape.cutout === true,
      }).flatMap((label): MapTooltipPayload[] => {
        const lengthText = formatDisplayedSegmentLengthMeters(
          label.lengthMeters,
        );
        if (!lengthText) return [];
        return [
          {
            type: MapTooltipType.Distance,
            screenPoint: label.screenPoint,
            text: `${lengthText} m`,
            tone: 'segment-length',
            anchor: 'center',
            segment: {
              shapeId: shape.id,
              startIndex: label.startIndex,
              endIndex: label.endIndex,
              lengthMeters: label.lengthMeters,
              screenStart: screenPoints[label.startIndex]!,
              screenEnd: screenPoints[label.endIndex]!,
              // Metric Y points south, so positive signed area is clockwise
              // when viewed from above on the map.
              pointsAreClockwise: signedAreaTwice > 0,
            },
          },
        ];
      });
    });
  }

  private isInsertCandidateFeature(
    feature?: MapStoreyPointFeature | mapboxgl.MapboxGeoJSONFeature,
  ): feature is MapStoreyPointFeature {
    return Boolean(feature?.properties?.insertCandidate);
  }

  /**
   * Resolve the closest line segment on the selected storey to the pointer.
   * @param event {@link MapMouseEvent} to inspect
   * @returns {@link VirtualLineHit} or undefined when no segment matches
   */
  private resolveVirtualLineHit(
    event: MapMouseEvent,
  ): VirtualLineHit | undefined {
    const storey = this.selectedStorey;
    if (!storey?.shapes?.length) return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointerCoordinate = this.getCoordinateAtElevation(event, elevation);
    const pointerPosition = this.getMetricPointerPosition(event, elevation);
    const cursorScreenPoint =
      this.map && pointerCoordinate
        ? toPoint2D(
            projectLngLatAtElevation(this.map, pointerCoordinate, elevation),
          )
        : this.getStoreyAdjustedScreenPoint(event);
    if (!pointerCoordinate || !pointerPosition || !cursorScreenPoint)
      return undefined;
    const tolerance = this.getLineHitToleranceMeters(pointerCoordinate);
    if (!Number.isFinite(tolerance)) return undefined;

    let best: VirtualLineHit | undefined;
    for (const shape of storey.shapes) {
      if (!shape?.points?.length || shape.points.length < 2) continue;
      const segment = this.findNearestSegment(shape, pointerPosition);
      if (!segment) continue;
      const points = shape.points;
      const startPoint = points[segment.startIndex];
      const endPoint = points[segment.endIndex % points.length];
      if (!startPoint || !endPoint) continue;
      const distance = this.distancePointToSegment(
        pointerPosition,
        startPoint,
        endPoint,
      );
      if (distance > tolerance) continue;
      const lengthMeters = Math.hypot(
        endPoint[0] - startPoint[0],
        endPoint[1] - startPoint[1],
      );
      const midpoint: Point2D = [
        (startPoint[0] + endPoint[0]) / 2,
        (startPoint[1] + endPoint[1]) / 2,
      ];
      const midpointCoordinate = convertMetricOffsetToCoordinate(
        this.location,
        midpoint,
      );
      if (!best || distance < best.distance) {
        best = {
          shape,
          shapeId: shape.id,
          startIndex: segment.startIndex,
          endIndex: segment.endIndex,
          midpoint,
          midpointCoordinate,
          lengthMeters,
          screenPoint: cursorScreenPoint,
          distance,
          elevation,
        };
      }
    }
    return best;
  }

  private toLineHoverHit(
    hit: VirtualLineHit | undefined,
  ): LineHoverHit | undefined {
    if (!hit) return undefined;
    return {
      shapeId: hit.shapeId,
      startIndex: hit.startIndex,
      endIndex: hit.endIndex,
      screenPoint: hit.screenPoint,
      lengthMeters: hit.lengthMeters,
      midpoint: hit.midpoint,
      midpointCoordinate: hit.midpointCoordinate,
    };
  }

  /**
   * Resolve the closest draft line segment to the pointer.
   * @param event {@link MapMouseEvent} to inspect
   * @returns Draft segment indices or undefined when none match
   */
  private resolveVirtualDraftLineHit(
    event: MapMouseEvent,
  ): DraftLineHoverHit | undefined {
    const draft = this.draftShape;
    if (!draft) return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointerCoordinate = this.getCoordinateAtElevation(event, elevation);
    const pointerPosition = this.getMetricPointerPosition(event, elevation);
    const pointerScreenPoint =
      this.map && pointerCoordinate
        ? toPoint2D(
            projectLngLatAtElevation(this.map, pointerCoordinate, elevation),
          )
        : this.getStoreyAdjustedScreenPoint(event);
    if (!pointerCoordinate || !pointerPosition || !pointerScreenPoint)
      return undefined;
    const tolerance = this.getLineHitToleranceMeters(pointerCoordinate);
    if (!Number.isFinite(tolerance)) return undefined;
    const segment = this.findNearestSegment(draft, pointerPosition);
    if (!segment) return undefined;
    const points = draft.points ?? [];
    const startPoint = points[segment.startIndex];
    const endPoint = points[segment.endIndex % points.length];
    if (!startPoint || !endPoint) return undefined;
    const distance = this.distancePointToSegment(
      pointerPosition,
      startPoint,
      endPoint,
    );
    if (distance > tolerance) return undefined;
    return {
      startIndex: segment.startIndex,
      endIndex: segment.endIndex,
      screenPoint: pointerScreenPoint,
    };
  }

  /**
   * Convert the configured line hit padding (in px) to meters at the pointer location.
   * @param coordinate {@link Coordinate} representing the pointer projection
   */
  private getLineHitToleranceMeters(
    coordinate: Coordinate | undefined,
  ): number {
    const mapInstance = this.map;
    if (!mapInstance || !coordinate) return Number.POSITIVE_INFINITY;
    const metersPerPixel = getMetersPerPixelAtCoordinate(
      mapInstance,
      coordinate,
    );
    if (!Number.isFinite(metersPerPixel)) return Number.POSITIVE_INFINITY;
    return metersPerPixel * LINE_HIT_PADDING_PX * 3;
  }

  /**
   * Determine if any map point currently sits under the cursor.
   * @param event {@link MapMouseEvent} to inspect
   * @returns true when at least one point is within the padding radius
   */
  private hasPointUnderCursor(event: MapMouseEvent): boolean {
    const selectedLevel = this.selectedStoreyId
      ? getStoreyIndex(this.storeys, this.selectedStoreyId)
      : undefined;
    const features = this.queryRenderedFeaturesAroundPoint(
      event,
      [MapLayerIds.Points, MapLayerIds.DraftPoints],
      POINT_HIT_PADDING_PX,
    );
    const hasPoint = features.some((feature) => {
      if (!mapGuards.is.feature.point(feature)) return false;
      if (this.isInsertCandidateFeature(feature as MapStoreyPointFeature))
        return false;

      // Ignore points from non-selected storeys so mezzanine shape clicks are not blocked.
      if (selectedLevel === undefined || selectedLevel < 0) return true;
      const pointLevel = Number(feature.properties?.[MapStoreyProperty.Level]);
      if (!Number.isFinite(pointLevel) || pointLevel !== selectedLevel)
        return false;
      return true;
    });

    return hasPoint;
  }

  // Henrik: canvas focus is handled by subscribers (DesignMap), not the event source.

  /**
   * Check whether the pointer currently intersects any building shape surfaces.
   * @param event {@link MapMouseEvent} to inspect
   * @returns true when a persisted polygon sits under the cursor
   */
  private hasShapeUnderCursor(event: MapMouseEvent): boolean {
    const features = this.queryRenderedFeaturesAroundPoint(
      event,
      [MapLayerIds.BuildingFill2d],
      0,
    );
    return features.some((feature) => mapGuards.is.feature.polygon(feature));
  }

  /**
   * Camera ray through the pointer, in local metres. Its horizontal position
   * is affine in elevation, so two ground samples describe the whole ray.
   * @returns The ray, or undefined when the pointer cannot be projected
   */
  private getBuildingPickRay(event: MapMouseEvent) {
    const atGround = this.getMetricPointerPosition(event, 0);
    const camera = this.getCameraGroundPosition();
    if (!atGround || !camera || camera.heightMeters <= 0) return undefined;
    // The camera itself is the ray's position at its own altitude, which makes
    // the ray exact instead of relying on an iterative screen reprojection.
    return createBuildingPickRay(
      atGround,
      camera.position,
      camera.heightMeters,
    );
  }

  /** Ray matching the custom projection that is actually visible in facade mode. */
  private getFacadeBuildingPickRay(
    event: MapMouseEvent,
  ): BuildingVolumeRay | undefined {
    if (!this.facadeModeActive || !this.facadePickRayResolver) return undefined;
    const referencePoint = event.point ?? this.map?.project(event.lngLat);
    const point = this.toScreenPoint(referencePoint);
    return point ? this.facadePickRayResolver([point.x, point.y]) : undefined;
  }

  /** Geometry handles have priority over floor selection at every pitch. */
  private shouldKeepStoreyOnDoubleClick(event: MapMouseEvent): boolean {
    return (
      this.hasPointUnderCursor(event) ||
      Boolean(this.buildLineClickPayload(event, true))
    );
  }

  /** Double-clicking a facade floor opens its editable height measurement. */
  private focusFacadeStoreyHeight(event: MapMouseEvent): boolean {
    if (!this.facadeModeActive || this.isReadonly) return false;
    if (this.selectedStoreyId && this.buildLineClickPayload(event, true))
      return false;
    const storeyId = this.getClickedStoreyId(event, 'lowest');
    if (!storeyId) return false;
    event.preventDefault();
    this.storeyVolumeClick$.next({ event, storeyId });
    this.state.hoveredStoreyId$.next(storeyId);
    this.state.storeyHeightFocus$.next(storeyId);
    return true;
  }

  getTopmostSelectedStoreyIdUnderPointer(
    event: MapMouseEvent,
  ): Storey['id'] | undefined {
    return this.getClickedStoreyId(event, 'highest');
  }

  private getClickedStoreyId(
    event: MapMouseEvent,
    order: 'highest' | 'lowest',
    storeys = this.storeys,
  ): Storey['id'] | undefined {
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) return pickStoreyByVolumeRay(facadeRay, storeys, order)?.id;
    const ray = this.getBuildingPickRay(event);
    return ray
      ? pickStoreyByVolumeClick(ray, storeys, { order })?.id
      : undefined;
  }

  /**
   * Camera position projected onto the ground, in local metres, plus its
   * height above ground.
   */
  private getCameraGroundPosition():
    { position: Point2D; heightMeters: number } | undefined {
    const cameraPosition = this.map?.getFreeCameraOptions?.().position;
    if (!cameraPosition) return undefined;
    const heightMeters = cameraPosition.toAltitude();
    if (!Number.isFinite(heightMeters)) return undefined;
    const { lng, lat } = cameraPosition.toLngLat();
    const [east, north] = convertCoordinateToMetricOffset(
      { lng, lat },
      this.location,
    );
    // Shape space has y pointing south; the offset helper returns it north.
    return { position: [east, -north], heightMeters };
  }

  /**
   * Non-selected building whose volume sits under the pointer, picking the one
   * nearest the camera when several overlap (DEV-2576). Tests the rendered
   * volume rather than the flat footprint, so a wall or roof counts as a hit.
   */
  private getOtherBuildingIdUnderPointer(
    event: MapMouseEvent,
  ): string | undefined {
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) {
      return pickBuildingByVolumeRay(
        facadeRay,
        this.otherBuildings.map((building) => ({
          buildingId: building.id,
          storeys: building.storeys,
        })),
      );
    }
    const ray = this.getBuildingPickRay(event);
    const fromVolumes = ray
      ? pickBuildingByRay(
          ray,
          this.otherBuildings.map((building) => ({
            buildingId: building.id,
            storeys: building.storeys,
          })),
        )
      : undefined;
    if (fromVolumes) return fromVolumes;
    // Buildings without a footprint exist only as a flat click target in the
    // source, so they still need the rendered-feature query. Buildings that do
    // have geometry must not fall back to it: at a pitched angle the ground
    // point under the cursor can sit inside a completely different footprint.
    const fromHitTarget = this.getTopmostOtherBuildingHit(event)?.buildingId;
    return this.isEmptyOtherBuilding(fromHitTarget) ? fromHitTarget : undefined;
  }

  /** Visible building contour under the pointer, nearest to the active camera. */
  private getHoveredBuildingIdUnderPointer(
    event: MapMouseEvent,
  ): string | undefined {
    if (this.buildingFootprintTemplate) return undefined;
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) {
      const candidates = this.otherBuildings.map((building) => ({
        buildingId: building.id,
        storeys: building.storeys,
      }));
      if (this.hasBuildingSelection && this.currentBuildingId) {
        candidates.push({
          buildingId: this.currentBuildingId,
          storeys: this.storeys,
        });
      }
      return pickBuildingByVolumeRay(facadeRay, candidates);
    }

    const otherBuildingId = this.getOtherBuildingIdUnderPointer(event);
    const selectedBuildingId = this.hasSelectedBuildingVolumeHit(event)
      ? this.currentBuildingId
      : undefined;
    if (!otherBuildingId || !selectedBuildingId) {
      return otherBuildingId ?? selectedBuildingId;
    }
    const depths = this.getBuildingPickDepths(event, otherBuildingId);
    return depths.other !== undefined &&
      depths.selected !== undefined &&
      depths.other > depths.selected
      ? otherBuildingId
      : selectedBuildingId;
  }

  /**
   * Hit elevations under the pointer for the selected building and one
   * sibling. A higher elevation means the surface is nearer the camera, so it
   * decides which building a click lands on when volumes overlap (DEV-2576).
   */
  private getBuildingPickDepths(
    event: MapMouseEvent,
    otherBuildingId: string | undefined,
  ): { selected: number | undefined; other: number | undefined } {
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) {
      const other = otherBuildingId
        ? getItemById(this.otherBuildings, otherBuildingId, false)
        : undefined;
      const selectedDistance = this.hasBuildingSelection
        ? getBuildingVolumeRayHitDistance(facadeRay, this.storeys)
        : undefined;
      const otherDistance = getBuildingVolumeRayHitDistance(
        facadeRay,
        other?.storeys,
      );
      return {
        selected:
          selectedDistance === undefined ? undefined : -selectedDistance,
        other: otherDistance === undefined ? undefined : -otherDistance,
      };
    }
    const ray = this.getBuildingPickRay(event);
    if (!ray) return { selected: undefined, other: undefined };
    const other = otherBuildingId
      ? getItemById(this.otherBuildings, otherBuildingId, false)
      : undefined;
    return {
      selected: this.hasBuildingSelection
        ? getBuildingRayHitElevation(ray, this.storeys)
        : undefined,
      other: getBuildingRayHitElevation(ray, other?.storeys),
    };
  }

  /**
   * True when the pointer is over the selected building's volume.
   */
  private hasSelectedBuildingVolumeHit(event: MapMouseEvent): boolean {
    if (!this.hasBuildingSelection) return false;
    const facadeRay = this.getFacadeBuildingPickRay(event);
    if (facadeRay) {
      return (
        getBuildingVolumeRayHitDistance(facadeRay, this.storeys) !== undefined
      );
    }
    const ray = this.getBuildingPickRay(event);
    if (!ray) return false;
    return getBuildingRayHitElevation(ray, this.storeys) !== undefined;
  }

  /**
   * Highest other-building fill under the pointer.
   * When several footprints overlap, roof height wins over list order.
   */
  private getTopmostOtherBuildingHit(
    event: MapMouseEvent,
  ): BuildingFillHit | undefined {
    const features = this.queryRenderedFeaturesAroundPoint(
      event,
      [MapLayerIds.OtherBuildingsFill],
      0,
    );
    return pickTopmostBuildingHit(
      toBuildingFillHits(features),
      this.otherBuildings.map((building) => building.id),
    );
  }

  /**
   * Building id of a static other-building footprint under the pointer.
   * Prefers the highest overlapping sibling rather than source order.
   * @param event {@link MapMouseEvent} to inspect
   */
  private getOtherBuildingIdUnderCursor(
    event: MapMouseEvent,
  ): string | undefined {
    return this.getOtherBuildingIdUnderPointer(event);
  }

  /**
   * Click options for sibling-footprint vs empty-hit-target handling.
   */
  private getOtherBuildingClickOptions(event: MapMouseEvent): {
    penMode: boolean;
    otherBuildingId: string | undefined;
    isEmptyOtherBuilding: boolean;
  } {
    const otherBuildingId = this.getOtherBuildingIdUnderCursor(event);
    return {
      penMode: this.penMode,
      otherBuildingId,
      isEmptyOtherBuilding: this.isEmptyOtherBuilding(otherBuildingId),
    };
  }

  /**
   * True when {@link buildingId} is a non-selected building with no footprint.
   */
  private isEmptyOtherBuilding(buildingId: string | undefined): boolean {
    if (!buildingId) return false;
    const building = getItemById(this.otherBuildings, buildingId, false);
    if (!building) return false;
    return isEmptyBuildingGeometry(building);
  }

  /** Clear selected polygon points before clearing the storey/building on an empty click. */
  private handleDeselectMapClick(event: MapMouseEvent): void {
    const selectedPoints = this.selections.pointIndexes$.getValue();
    if (selectedPoints?.length && !this.selections.draftShape$.getValue()) {
      if (!event.originalEvent.shiftKey) this.deselectShapesAndPoints();
      // Clear geometry first; the active storey stays selected. A subsequent
      // background click without selected points keeps the ordinary behavior.
      return;
    }
    if (this.blueprintHitTest?.(event)) {
      this.deselectShapesAndPoints();
      this.requestBuildingDeselection();
      return;
    }
    this.onBlueprintDeselectRequest?.();
    this.deselectClick$.next(event);
    this.requestBuildingDeselection();
  }

  /**
   * Determine whether a map click should clear current selections.
   * @param event {@link MapMouseEvent} emitted by the map
   * @returns true when no interactive geometry was hit
   */
  private shouldDeselectOnMapClick(event: MapMouseEvent): boolean {
    if (this.locationPinController.isPinModeActive()) return false;
    if (this.buildingPickingPaused) return false;
    if (this.hasSelectedBuildingHit(event)) return false;
    if (this.hasSelectedBuildingVolumeHit(event)) return false;
    if (this.resolveVirtualDraftLineHit(event)) return false;
    if (this.getOtherBuildingIdUnderCursor(event)) return false;
    if (this.resolveBuildingRotationHandle(event)) return false;
    return true;
  }

  /**
   * Clear the building selection after a background click (DEV-2576).
   * Skipped while drafting, in pen mode, or when the selected building has no
   * footprint yet — those states use background clicks to draw.
   */
  private requestBuildingDeselection(): void {
    if (
      !shouldClearBuildingSelection({
        hasBuildingSelection: this.hasBuildingSelection,
        hasDraftShape: Boolean(this.draftShape),
        penMode: this.penMode,
        selectedBuildingIsEmpty: this.isSelectedBuildingEmpty(),
      })
    ) {
      return;
    }
    this.onDeselectBuilding?.();
  }

  /**
   * Geometry drags should only start with primary mouse button (LMB).
   */
  private isPrimaryMouseButton(event: MapMouseEvent): boolean {
    return event.originalEvent.button === 0;
  }

  /**
   * Decide whether a shape click should be converted into a shape selection.
   * Points and walls of the selected building take priority over fill clicks.
   * Sibling footprints are handled separately and must not steal this click.
   * @param event {@link MapMouseEvent} representing the click
   * @returns true when the selected building's fill should be selected
   */
  private shouldHandleShapeClick(event: MapMouseEvent): boolean {
    const selectedStoreyShapeId =
      this.resolveSelectedStoreyShapeIdFromPointerEvent(event);
    const insideSelectedStorey = this.selectedStoreyId
      ? Boolean(selectedStoreyShapeId)
      : true;
    if (this.selectedStoreyId && !insideSelectedStorey) return false;
    if (this.hasPointUnderCursor(event)) return false;
    if (this.resolveVirtualLineHit(event)) return false;
    return true;
  }

  /**
   * True when the pointer hits the selected building (vertex, wall, or fill).
   * Shared walls stay on the current building; stacked fills use height instead.
   */
  private hasSelectedBuildingHit(event: MapMouseEvent): boolean {
    if (this.hasSelectedBuildingVertexOrWallHit(event)) return true;
    return Boolean(this.resolveSelectedStoreyShapeIdFromPointerEvent(event));
  }

  /**
   * True when a selected-building vertex or wall is under the pointer.
   */
  private hasSelectedBuildingVertexOrWallHit(event: MapMouseEvent): boolean {
    if (this.hasPointUnderCursor(event)) return true;
    return Boolean(this.resolveVirtualLineHit(event));
  }

  /**
   * Query rendered features in a padded rectangle around the pointer.
   * @param event {@link MapMouseEvent} emitted by Mapbox
   * @param layers Array of layer ids to query
   * @param paddingPx Padding radius in pixels
   * @param pointOverride Optional {@link PointLike} override
   * @returns Feature array limited to existing layers
   */
  private queryRenderedFeaturesAroundPoint(
    event: MapMouseEvent,
    layers: string[],
    paddingPx: number,
    pointOverride?: PointLike,
  ): mapboxgl.MapboxGeoJSONFeature[] {
    const mapInstance = this.map;
    if (!mapInstance) return [];

    // Filter out layers that don't exist in the map's style
    const existingLayers = layers.filter((layerId) =>
      mapInstance.getLayer(layerId),
    );
    if (existingLayers.length === 0) return [];

    const referencePoint =
      pointOverride ?? event.point ?? mapInstance.project(event.lngLat);
    const screenPoint = this.toScreenPoint(referencePoint);
    if (!screenPoint) return [];
    if (paddingPx <= 0) {
      return mapInstance.queryRenderedFeatures(referencePoint, {
        layers: existingLayers,
      });
    }
    const minPoint: PointLike = [
      screenPoint.x - paddingPx,
      screenPoint.y - paddingPx,
    ];
    const maxPoint: PointLike = [
      screenPoint.x + paddingPx,
      screenPoint.y + paddingPx,
    ];
    return mapInstance.queryRenderedFeatures([minPoint, maxPoint], {
      layers: existingLayers,
    });
  }

  /**
   * Normalise a {@link PointLike} value into a screen point object.
   * @param pointLike {@link PointLike} value
   * @returns Object with x and y coordinates or undefined when unsupported
   */
  private toScreenPoint(
    pointLike: PointLike,
  ): { x: number; y: number } | undefined {
    if (!pointLike) return undefined;
    if (Array.isArray(pointLike)) {
      const [x, y] = pointLike;
      return { x, y };
    }
    const candidate = pointLike as { x?: number; y?: number };
    if (typeof candidate.x === 'number' && typeof candidate.y === 'number') {
      return { x: candidate.x, y: candidate.y };
    }
    return undefined;
  }

  /** Pick the actual facade segment, whose projection differs from the hidden basemap. */
  private resolveFacadeDoubleClickLineHit(
    event: MapMouseEvent,
  ): VirtualLineHit | undefined {
    const storey = this.selectedStorey;
    const projector = this.buildingContourProjector;
    if (!storey || !projector || !event.point) return undefined;
    const elevation = this.getSelectedStoreyElevationValue();
    const pointer: Point2D = [event.point.x, event.point.y];
    let best: VirtualLineHit | undefined;
    for (const shape of storey.shapes) {
      for (let index = 0; index < shape.points.length; index++) {
        const start = shape.points[index];
        const end = shape.points[(index + 1) % shape.points.length];
        if (!start || !end) continue;
        const a = projector(
          convertMetricOffsetToCoordinate(this.location, start),
          elevation,
        );
        const b = projector(
          convertMetricOffsetToCoordinate(this.location, end),
          elevation,
        );
        if (!a || !b) continue;
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const squaredLength = dx * dx + dy * dy;
        if (squaredLength < 1e-6) continue;
        const along = Math.max(
          0,
          Math.min(
            1,
            ((pointer[0] - a[0]) * dx + (pointer[1] - a[1]) * dy) /
              squaredLength,
          ),
        );
        const distance = Math.hypot(
          pointer[0] - a[0] - along * dx,
          pointer[1] - a[1] - along * dy,
        );
        if (
          distance > LINE_HIT_PADDING_PX ||
          (best && best.distance <= distance)
        )
          continue;
        // A corner double-click keeps its corner behavior.
        if (
          Math.min(
            Math.hypot(pointer[0] - a[0], pointer[1] - a[1]),
            Math.hypot(pointer[0] - b[0], pointer[1] - b[1]),
          ) <= POINT_HIT_PADDING_PX
        )
          continue;
        const midpoint: Point2D = [
          (start[0] + end[0]) / 2,
          (start[1] + end[1]) / 2,
        ];
        best = {
          shape,
          shapeId: shape.id,
          startIndex: index,
          endIndex: (index + 1) % shape.points.length,
          midpoint,
          midpointCoordinate: convertMetricOffsetToCoordinate(
            this.location,
            midpoint,
          ),
          lengthMeters: Math.hypot(end[0] - start[0], end[1] - start[1]),
          screenPoint: pointer,
          distance,
          elevation,
          pointerPosition: [
            start[0] + along * (end[0] - start[0]),
            start[1] + along * (end[1] - start[1]),
          ],
        };
      }
    }
    return best;
  }

  /**
   * Build a line click payload for the segment under the pointer.
   * Used for line-click-to-select (both adjacent points) and, when point-insert
   * is enabled, for insert-drag. Callers that need insert behavior must gate on
   * {@link hasPointInsertCapability}.
   * @param event {@link MapMouseEvent} describing the click interaction
   * @returns {@link LineClickPayload} or undefined when no segment was hit
   */
  private buildLineClickPayload(
    event: MapMouseEvent,
    atPointer = false,
  ): LineClickPayload | undefined {
    const hit =
      atPointer && this.facadeModeActive
        ? this.resolveFacadeDoubleClickLineHit(event)
        : this.resolveVirtualLineHit(event);
    if (!hit) return undefined;
    let coordinate = hit.midpointCoordinate;
    const points = hit.shape.points ?? [];
    const startPoint = points[hit.startIndex];
    const endPoint = points[hit.endIndex % points.length];
    if (atPointer) {
      const pointer =
        hit.pointerPosition ??
        this.getMetricPointerPosition(event, hit.elevation);
      if (!pointer || !startPoint || !endPoint) return undefined;
      coordinate = convertMetricOffsetToCoordinate(
        this.location,
        projectPointOntoSegmentClamped(pointer, startPoint, endPoint),
      );
    }
    return {
      preventDefault: wrapPreventDefault(event),
      shape: hit.shape,
      shapeId: hit.shapeId,
      elevation: hit.elevation,
      lng: coordinate.lng,
      lat: coordinate.lat,
      startIndex: hit.startIndex,
      endIndex: hit.endIndex,
      startPoint,
      endPoint,
    };
  }

  /**
   * Determine whether the pointer currently resides inside forbidden shapes.
   * @param event {@link MapMouseEvent} representing pointer state
   * @returns true when the pointer is blocked
   */
  private isPointerInsideForbiddenShape(event: MapMouseEvent): boolean {
    if (this.isCutoutIntentActive()) return false;
    const pointerPosition = this.getMetricPointerPosition(
      event,
      this.getSelectedStoreyElevationValue(),
    );
    if (!pointerPosition) return false;
    return isPointInsideStoreyShapes(pointerPosition, this.selectedStorey);
  }

  /**
   * Check if the selected storey is the bottom floor and already has shapes.
   */
  private isBottomFloorWithShapes(): boolean {
    const selectedStorey = this.selectedStorey;
    if (!selectedStorey) return false;
    const storeyIndex = getStoreyIndex(this.storeys, selectedStorey);
    // Bottom floor is index 0
    if (storeyIndex !== 0) return false;
    // Restriction applies only to solid shapes; cutouts are allowed.
    return getSolidShapes(selectedStorey).length > 0;
  }

  /**
   * True when every building on the map has no drawable footprint.
   * Used for origin rebase and the location pin — an empty selected building
   * with a drawn sibling must keep the shared origin (DEV-2576).
   */
  private isEmptyDesign(): boolean {
    return isEntireDesignGeometryEmpty(this.storeys, this.otherBuildings);
  }

  /**
   * Drop an unfinished footprint draft (e.g. after PDF dblclick select).
   */
  private clearInProgressDraft(): void {
    if (!this.draftShape && !this.state.draftShape$.getValue()) return;
    this.draftShape = undefined;
    this.state.draftShape$.next(undefined);
    this.selections.draftShape$.next(false);
  }

  /**
   * True when the selected building has no drawable footprint.
   * Drawing then matches the empty-map flow (click without Alt, normal point
   * size) even if a sibling already has geometry.
   */
  private isSelectedBuildingEmpty(): boolean {
    // No selection means there is nothing to draw into, not an empty building.
    if (!this.hasBuildingSelection) return false;
    return isEmptyStoreysGeometry(this.storeys);
  }

  /**
   * Push the latest draft cursor state to observers.
   */
  private updateDraftCursorState(): void {
    if (this.isReadonly) {
      this.draftCursorState$.next({ active: false, suppressed: false });
      return;
    }
    const hasDraft = Boolean(this.draftShape);
    const pinSuppressed = this.locationPinController.isPinModeActive();
    const selectedStorey = this.selectedStorey;
    const altIntent = this.isAltKeyActive || this.isDrawAltIntentActive();
    const mezzanineCutoutBlocked =
      altIntent &&
      Boolean(
        selectedStorey && designGuards.is.mezzanineStorey(selectedStorey),
      );
    const bottomFloorRestricted =
      altIntent &&
      this.isBottomFloorWithShapes() &&
      !this.isCutoutIntentActive();
    // Alt on a whole selected building is duplication, not drawing.
    const altDrawingActive =
      this.isAltKeyActive && !this.isWholeBuildingTranslateActive();
    // Draw, Alt drawing, open draft, empty selected building, or pen → crosshair.
    const emptyBuild =
      this.designMapTool === 'build' && this.isSelectedBuildingEmpty();
    const drawToolActive = this.designMapTool === 'draw';
    this.draftCursorState$.next({
      active:
        (drawToolActive ||
          altDrawingActive ||
          hasDraft ||
          emptyBuild ||
          this.penMode) &&
        !pinSuppressed &&
        !this.spacePanActive &&
        this.designMapTool !== 'moveBuilding',
      suppressed:
        this.isPointerForbidden ||
        pinSuppressed ||
        bottomFloorRestricted ||
        mezzanineCutoutBlocked ||
        this.spacePanActive,
    });
  }

  /**
   * Draw tool with an existing building mirrors holding Alt (cutout / new solid).
   */
  private isDrawAltIntentActive(): boolean {
    return this.designMapTool === 'draw' && !this.isEmptyDesign();
  }

  /**
   * Resolve whether the current draft intent targets cutout drawing.
   */
  private isCutoutIntentActive(): boolean {
    const selectedStorey = this.selectedStorey;
    if (selectedStorey && designGuards.is.mezzanineStorey(selectedStorey)) {
      return this.draftShape?.cutout === true;
    }
    return (
      this.draftShape?.cutout === true ||
      this.isAltKeyActive ||
      this.isDrawAltIntentActive()
    );
  }

  /**
   * Find the line segment that is closest to the supplied position.
   * @param shape {@link Shape} or {@link DraftShape} to evaluate
   * @param position {@link Point2D} pointer location in metric space
   * @returns Indices encapsulating the closest segment
   */
  private findNearestSegment(
    shape: Shape | DraftShape,
    position: Point2D,
  ): { startIndex: number; endIndex: number } | undefined {
    const points = shape.points ?? [];
    if (points.length < 2) return undefined;
    const total = points.length;
    const isClosed = !this.isDraftShape(shape) && total >= 3;
    const limit = isClosed ? total : total - 1;
    let best:
      { startIndex: number; endIndex: number; distance: number } | undefined;

    for (let index = 0; index < limit; index += 1) {
      const start = points[index];
      const end = points[(index + 1) % total];
      if (!start || !end) continue;
      const distance = this.distancePointToSegment(position, start, end);
      if (!best || distance < best.distance) {
        best = {
          startIndex: index,
          endIndex: (index + 1) % total,
          distance,
        };
      }
    }

    if (!best) return undefined;
    return {
      startIndex: best.startIndex,
      endIndex: best.endIndex,
    };
  }

  /**
   * Compute the distance between a point and a segment in metric space.
   * @param point {@link Point2D} to measure from
   * @param start Segment start {@link Point2D}
   * @param end Segment end {@link Point2D}
   * @returns Euclidean distance
   */
  private distancePointToSegment(
    point: Point2D,
    start: Point2D,
    end: Point2D,
  ): number {
    const [px, py] = point;
    const [ax, ay] = start;
    const [bx, by] = end;
    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;
    const abLengthSquared = abx * abx + aby * aby;
    if (abLengthSquared === 0) return Math.hypot(px - ax, py - ay);
    const t = Math.max(
      0,
      Math.min(1, (apx * abx + apy * aby) / abLengthSquared),
    );
    const cx = ax + t * abx;
    const cy = ay + t * aby;
    return Math.hypot(px - cx, py - cy);
  }

  /**
   * Type guard returning true when the supplied shape is a draft.
   * @param shape {@link Shape} or {@link DraftShape}
   * @returns shape is {@link DraftShape}
   */
  private isDraftShape(shape: Shape | DraftShape): shape is DraftShape {
    return (shape as DraftShape)?.draft === true;
  }

  /**
   * Resolve the current storey elevation used for 3D interactions.
   * @returns Elevation in metres
   */
  private getSelectedStoreyElevationValue(): number {
    const storey = this.selectedStorey;
    if (!storey) return 0;
    return getStoreyElevation(this.storeys, storey);
  }

  /**
   * Project a screen event into geographic space at the provided elevation.
   * @param event {@link MapMouseEvent} carrying pointer data
   * @param elevation Elevation in metres
   * @returns Geographic coordinate or undefined
   */
  private getCoordinateAtElevation(
    event: MapMouseEvent,
    elevation: number,
  ): { lng: number; lat: number } | undefined {
    const mapInstance = this.map;
    if (!mapInstance) return undefined;
    const screenPoint = event.point ?? mapInstance.project(event.lngLat);
    if (!screenPoint) return undefined;
    return screenToLngLatAtElevation(mapInstance, screenPoint, elevation);
  }

  /**
   * Convert a pointer event into metric coordinates at the requested elevation.
   * @param event {@link MapMouseEvent} emitted by Mapbox
   * @param elevation Elevation in metres
   * @returns {@link Point2D} or undefined
   */
  private getMetricPointerPosition(
    event: MapMouseEvent,
    elevation: number,
  ): Point2D | undefined {
    const coordinate = this.getCoordinateAtElevation(event, elevation);
    if (!coordinate) return undefined;
    const [mx, my] = convertCoordinateToMetricOffset(
      { lat: coordinate.lat, lng: coordinate.lng },
      this.location,
    );
    return [mx, -my];
  }

  /**
   * Derive a query point aligned with the currently selected storey elevation.
   * @param event {@link MapMouseEvent} to project
   * @returns {@link PointLike} or undefined
   */
  private getStoreyAdjustedQueryPoint(
    event: MapMouseEvent,
  ): PointLike | undefined {
    const elevation = this.getSelectedStoreyElevationValue();
    const mapInstance = this.map;
    if (!mapInstance) return undefined;
    if (!this.selectedStorey) return undefined;
    const coordinate = this.getCoordinateAtElevation(event, elevation);
    if (!coordinate) return undefined;
    const projected = mapInstance.project([coordinate.lng, coordinate.lat]);
    return [projected.x, projected.y];
  }

  private getStoreyAdjustedScreenPoint(
    event: MapMouseEvent,
  ): Point2D | undefined {
    const projected = this.getStoreyAdjustedQueryPoint(event);
    if (projected) {
      return toPoint2D(projected);
    }
    return toPoint2D(event.point ?? this.map?.project(event.lngLat));
  }

  private getEventScreenPoint(event: MapMouseEvent): Point2D | undefined {
    return toPoint2D(event.point ?? this.map?.project(event.lngLat));
  }

  private traceDraftPointHover(
    feature: MapStoreyPointFeature | undefined,
    meta: PointMeta | undefined,
    reason: 'resolved' | 'missing-meta',
  ): void {
    if (!TRACE_DRAFT_INTERACTIONS || !feature) return;
    const properties = feature.properties;
    console.info('[map][draft-hover]', {
      reason,
      selectedStoreyId: this.selectedStoreyId,
      draftShapeId: this.draftShape?.id,
      featureShapeId: properties?.shapeId,
      featureIndex: properties?.index,
      featureElevation: properties?.[MapStoreyProperty.Elevation],
      featureLevel: properties?.[MapStoreyProperty.Level],
      metaShapeId: meta?.shapeId,
      metaIndex: meta?.index,
    });
  }
}
